import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import { getEstimateExtras } from './estimateExtrasApi';

const API_BASE_URL = 'https://trpliveapi1.tripbox.co.kr';

// Function to handle API errors
const handleApiError = (error) => {
  try {
    const statusFromMessage = (() => {
      const msg = String(error?.message ?? '');
      const m1 = msg.match(/\bHTTP\s+(\d{3})\b/i);
      if (m1?.[1]) return Number(m1[1]);
      const m2 = msg.match(/\bAPI\s*error\s*:\s*(\d{3})\b/i);
      if (m2?.[1]) return Number(m2[1]);
      return null;
    })();

    const status = Number(error?.status ?? statusFromMessage ?? 0);
    const message = String(error?.message ?? '');
    const isNetworkFailure =
      error?.name === 'TypeError' ||
      /Failed to fetch/i.test(message) ||
      /NetworkError/i.test(message) ||
      /fetch\s+failed/i.test(message);

    const isMaintenance = isNetworkFailure || [502, 503, 504].includes(status);

    if (isMaintenance && typeof window !== 'undefined' && window?.dispatchEvent) {
      window.dispatchEvent(
        new CustomEvent('app:maintenance', {
          detail: {
            status: status || null,
            message: message || null,
            ts: Date.now(),
          },
        })
      );
    }
  } catch (_) {}
  console.error('API Error:', error);
  try {
    if (error && (error.status || error.apiMessage || error.message)) {
      console.error('API Error Detail:', {
        status: error.status,
        apiMessage: error.apiMessage,
        message: error.message,
      });
    }
  } catch (_) {}
  throw error;
};

// Delete consult (견적 삭제)
// SP: DBO.TRP_T_%EC%83%81%EB%8B%B4%EC%A0%95%EB%B3%B4%EC%82%AD%EC%A0%9C
// payload Table1: @상담번호, @처리자, @업체번호
export const deleteConsult = async ({ consultNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@상담번호', 값: Number(consultNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%81%EB%8B%B4%EC%A0%95%EB%B3%B4%EC%82%AD%EC%A0%9C`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      let serverMsg = '';
      try {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const errJson = await res.json();
          serverMsg = errJson?.메시지 || errJson?.message || errJson?.MESSAGE || errJson?.오류 || errJson?.error || '';
          if (!serverMsg && errJson && typeof errJson === 'object') {
            const first = errJson?.table?.[0] || errJson?.table1?.[0] || errJson?.Table?.[0] || errJson?.Table1?.[0] || errJson?.result?.[0] || null;
            if (first && typeof first === 'object') {
              serverMsg = first?.메시지 || first?.message || first?.MESSAGE || first?.오류 || first?.error || '';
            }
            if (!serverMsg) {
              serverMsg = JSON.stringify(errJson);
            }
          }
        } else {
          serverMsg = await res.text();
        }
      } catch (_) {}
      const err = new Error(serverMsg || `HTTP ${res.status}`);
      err.status = res.status;
      err.apiMessage = serverMsg;
      throw err;
    }
    const data = await res.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get series date info (좌석 상세 조회)
// SP: DBO.TRP_S_%EC%8B%9C%EB%A6%AC%EC%A6%88%EB%82%A0%EC%A7%9C%EC%A0%95%EB%B3%B4
// Params: { sharedSeriesItemNo }
export const getSeriesDateInfo = async ({ sharedSeriesItemNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '1') },
        { 컬럼명: '@상품공유일련번호', 값: Number(sharedSeriesItemNo ?? 0) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%8B%9C%EB%A6%AC%EC%A6%88%EB%82%A0%EC%A7%9C%EC%A0%95%EB%B3%B4`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const table = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const extra1 = Array.isArray(data?.empt1) ? data.empt1 : Array.isArray(data?.Empt1) ? data.Empt1 : [];
    const extra2 = Array.isArray(data?.empt2) ? data.empt2 : Array.isArray(data?.Empt2) ? data.Empt2 : [];
    return { table, extra1, extra2 };
  } catch (error) {
    return handleApiError(error);
  }
};

// Update series date info (좌석 상세 저장)
// SP: DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EB%82%A0%EC%A7%9C%ED%8E%B8%EC%A7%91
// Params: detail object with edited seatCount and other fields
export const updateSeriesDateInfo = async ({
  companyNo,
  userNo,
  sharedSeriesItemNo,
  seatCount,
  seatStatus,
  nameTl,
  ticketTl,
  flightCurrencyAdult = 0,
  flightCurrencyChild = 0,
  flightCurrencyInfant = 0,
  fuelCurrencyAdult = 0,
  fuelCurrencyChild = 0,
  fuelCurrencyInfant = 0,
  reserveClose = false,
  ticketClose = false,
  flightJson = '[]',
  cutoffJson = '[]',
  addPnrJson = '[]',
  pnrType = '',
  pnrInfo = '',
  flightClass = null,
  pnrNo = '',
  memo = '',
  pnrNameCount = 0,
  ticketOsNo = '',
}) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '1') },
        { 컬럼명: '@유저번호', 값: String(userNo ?? '') },
        { 컬럼명: '@상품공유일련번호', 값: Number(sharedSeriesItemNo ?? 0) },
        { 컬럼명: '@좌석수', 값: String(seatCount ?? '0') },
        { 컬럼명: '@좌석상태', 값: String(seatStatus ?? '') },
        { 컬럼명: '@네임티엘', 값: String(nameTl ?? '') },
        { 컬럼명: '@발권티엘', 값: String(ticketTl ?? '') },
        { 컬럼명: '@항공료_성인', 값: Number(flightCurrencyAdult ?? 0) },
        { 컬럼명: '@항공료_어린이', 값: Number(flightCurrencyChild ?? 0) },
        { 컬럼명: '@항공료_영유아', 값: Number(flightCurrencyInfant ?? 0) },
        { 컬럼명: '@유류할증료_성인', 값: Number(fuelCurrencyAdult ?? 0) },
        { 컬럼명: '@유류할증료_어린이', 값: Number(fuelCurrencyChild ?? 0) },
        { 컬럼명: '@유류할증료_영유아', 값: Number(fuelCurrencyInfant ?? 0) },
        { 컬럼명: '@예약마감', 값: !!reserveClose },
        { 컬럼명: '@발권마감', 값: !!ticketClose },
        { 컬럼명: '@항공여정_JSON', 값: typeof flightJson === 'string' ? flightJson : JSON.stringify(flightJson ?? []) },
        { 컬럼명: '@컷오프_JSON', 값: typeof cutoffJson === 'string' ? cutoffJson : JSON.stringify(cutoffJson ?? []) },
        { 컬럼명: '@추가PNR_JSON', 값: typeof addPnrJson === 'string' ? addPnrJson : JSON.stringify(addPnrJson ?? []) },
        { 컬럼명: '@PNR구분', 값: String(pnrType ?? '') },
        { 컬럼명: '@PNR정보', 값: String(pnrInfo ?? '') },
        { 컬럼명: '@클래스', 값: flightClass == null ? null : String(flightClass) },
        { 컬럼명: '@PNR번호', 값: String(pnrNo ?? '') },
        { 컬럼명: '@비고', 값: String(memo ?? '') },
        { 컬럼명: '@PNR네임갯수', 값: Number(pnrNameCount ?? 0) },
        { 컬럼명: '@발권오스번호', 값: String(ticketOsNo ?? '') },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EB%82%A0%EC%A7%9C%ED%8E%B8%EC%A7%91`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save a simple user event (일정관리 캘린더 신규/수정 일정 저장)
// SP: DBO.RCT_T_%EC%9C%A0%EC%A0%80%EC%9D%B4%EB%B2%A4%ED%8A%B8
// payload Table1:
//   @이벤트번호 = 'NULL' for new, or existing 이벤트번호 for update
//   @이벤트 = JSON string with 시작일/종료일 = selected date, 일정명 = provided title
//   @업체번호, @유저번호 from current login
export const saveUserEvent = async ({ date, title, eventNo = null }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    if (!companyNo || !userNo) {
      throw new Error('로그인 정보를 찾을 수 없습니다.');
    }

    const safeDate = String(date || '').trim();
    if (!safeDate) {
      throw new Error('저장할 날짜가 없습니다.');
    }

    const safeTitle = String(title || '').trim();
    if (!safeTitle) {
      throw new Error('일정명을 입력해 주세요.');
    }

    const eventPayload = {
      시작일: safeDate,
      시작시간: '00:00',
      종료일: safeDate,
      종료시간: '00:00',
      구분: '363002',
      분류: '',
      일정명: safeTitle,
      내용: '내용',
      ALLDAY: true,
      구분명: '',
      분류명: '',
      일정관리번호: null,
      유저번호: null,
    };

    const payload = {
      Table1: [
        { 컬럼명: '@이벤트번호', 값: eventNo != null ? String(eventNo) : 'NULL', 리스트값: null },
        { 컬럼명: '@이벤트', 값: JSON.stringify(eventPayload), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%9C%A0%EC%A0%80%EC%9D%B4%EB%B2%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get customer list by sale number using RCT_S_판매번호별고객리스트
// This is mainly used to verify passport copy URLs (여권사본경로)
// Params: { saleNo }
export const getCustomerListBySaleNoFromRCT = async ({ saleNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };

    const procedureName = encodeURIComponent('DBO.RCT_S_판매번호별고객리스트');
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${procedureName}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete a simple user event (일정관리 캘린더 일정 삭제)
// SP: DBO.RCT_T_%EC%9C%A0%EC%A0%80%EC%9D%B4%EB%B2%A4%ED%8A%B8%EC%82%AD%EC%A0%9C
// payload Table1:
//   @이벤트번호, @업체번호, @유저번호
export const deleteUserEvent = async ({ eventNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    if (!companyNo || !userNo) {
      throw new Error('로그인 정보를 찾을 수 없습니다.');
    }

    if (eventNo == null || eventNo === '' ) {
      throw new Error('삭제할 일정 번호가 없습니다.');
    }

    const payload = {
      Table1: [
        { 컬럼명: '@이벤트번호', 값: String(eventNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%9C%A0%EC%A0%80%EC%9D%B4%EB%B2%A4%ED%8A%B8%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create new receipt record for bank management (수납 등록 - 통장관리 화면용)
// SP: DBO.CW_T_%EC%88%98%EB%82%A9%EA%B4%80%EB%A6%AC
// payload 동일: @공급업체번호, @유저번호, @구분, @거래일련번호, @결제수단, @거래일시, @환율, @통화코드,
//               @수납액, @거래금액, @계좌번호, @은행, @기타은행, @카드, @비고1, @비고2, @승인번호, @할부개월수
export const createBankReceiptRecord = async ({
  transactionDate,
  amount,
  paymentMethodCode,
  accountNo,
  bankCode,
  senderName,
  note,
  mode = 'I',
  transactionSeq = 'NULL',
  hideFlag = false,
}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const numericAmount = Number(amount) || 0;
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo) },
        { 컬럼명: '@유저번호', 값: String(userNo) },
        { 컬럼명: '@구분', 값: String(mode || 'I') },
        { 컬럼명: '@거래일련번호', 값: transactionSeq ?? 'NULL' },
        { 컬럼명: '@결제수단', 값: String(paymentMethodCode) },
        { 컬럼명: '@거래일시', 값: String(transactionDate) },
        { 컬럼명: '@환율', 값: 1 },
        { 컬럼명: '@통화코드', 값: '058001' },
        { 컬럼명: '@수납액', 값: String(numericAmount) },
        { 컬럼명: '@거래금액', 값: numericAmount },
        { 컬럼명: '@계좌번호', 값: String(accountNo || '') },
        { 컬럼명: '@은행', 값: String(bankCode || '') },
        { 컬럼명: '@기타은행', 값: '' },
        { 컬럼명: '@카드', 값: '' },
        { 컬럼명: '@비고1', 값: String(senderName || '') },
        { 컬럼명: '@비고2', 값: String(note || '') },
        { 컬럼명: '@숨김여부', 값: Boolean(hideFlag) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%EC%88%98%EB%82%A9%EA%B4%80%EB%A6%AC`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create or update disbursement record (출납 등록/수정)
// SP: DBO.CW_T_%EC%B6%9C%EB%82%A9%EA%B4%80%EB%A6%AC
// payload Table1: @공급업체번호, @유저번호, @구분, @거래일련번호, @결제수단, @거래일시, @환율, @통화코드,
//                  @수납액, @거래금액, @계좌번호, @은행, @기타은행, @카드, @비고1, @비고2, @승인번호, @할부개월수
// mode: 'I' (insert), 'U' (update)
// transactionSeq: 수정 시 대상 거래의 @거래일련번호
export const createDisbursementRecord = async ({
  transactionDate,
  amount,
  paymentMethodCode,
  accountNo,
  bankCode,
  senderName,
  note,
  mode = 'I',
  transactionSeq = 'NULL',
}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const numericAmount = Number(amount) || 0;
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo) },
        { 컬럼명: '@유저번호', 값: String(userNo) },
        { 컬럼명: '@구분', 값: String(mode || 'I') },
        { 컬럼명: '@거래일련번호', 값: transactionSeq ?? 'NULL' },
        { 컬럼명: '@결제수단', 값: String(paymentMethodCode) },
        { 컬럼명: '@거래일시', 값: String(transactionDate) },
        { 컬럼명: '@환율', 값: 1 },
        { 컬럼명: '@통화코드', 값: '058001' },
        { 컬럼명: '@수납액', 값: String(numericAmount) },
        { 컬럼명: '@거래금액', 값: numericAmount },
        { 컬럼명: '@계좌번호', 값: String(accountNo || '') },
        { 컬럼명: '@은행', 값: String(bankCode || '') },
        { 컬럼명: '@기타은행', 값: '' },
        { 컬럼명: '@카드', 값: '' },
        { 컬럼명: '@비고1', 값: String(senderName || '') },
        { 컬럼명: '@비고2', 값: String(note || '') },
        { 컬럼명: '@승인번호', 값: '' },
        { 컬럼명: '@할부개월수', 값: 'NULL' },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%EC%B6%9C%EB%82%A9%EA%B4%80%EB%A6%AC`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getProductCodeRuleInfo = async () => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo ?? ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%83%81%ED%92%88%EC%BD%94%EB%93%9C%EB%A3%8C%EC%A0%95%EB%B3%B4`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const v = rows?.[0]?.상품코드룰사용;
    return typeof v === 'boolean' ? v : !!v;
  } catch (error) {
    return null;
  }
};

export const saveProductCodeRules = async ({ productList, regionList, ruleUse = true }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo ?? ''), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo ?? ''), 리스트값: null },
        { 컬럼명: '@상품타입리스트', 값: JSON.stringify(productList || []), 리스트값: null },
        { 컬럼명: '@여행지대분류리스트', 값: JSON.stringify(regionList || []), 리스트값: null },
        { 컬럼명: '@상품코드룰사용', 값: !!ruleUse, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%BD%94%EB%93%9C%EB%A3%B0`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return data || {};
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete tour rate plan (요금제 삭제)
// SP: DBO.RCT_T_%ED%88%AC%EC%96%B4%EC%9A%94%EA%B8%88%EC%A0%9C_%EC%82%AD%EC%A0%9C
// payload Table1: @업체번호, @상품번호, @가격옵션번호, @처리자
export const deleteTourRatePlan = async ({ companyNo, productNo, priceOptionNo, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@가격옵션번호', 값: Number(priceOptionNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%ED%88%AC%EC%96%B4%EC%9A%94%EA%B8%88%EC%A0%9C_%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getPreDepositWithdrawalList = async ({ documentNo, preDepositNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@결재문서번호', 값: Number(documentNo), 리스트값: null },
        { 컬럼명: '@전도금번호', 값: Number(preDepositNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%B6%9C%EA%B8%88%EB%A6%AC%EC%8A%A4%ED%8A%B8_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create a default tour rate plan under a resource (pattern)
// SP: DBO.RCT_T_%ED%88%AC%EC%96%B4%EC%9A%94%EA%B8%88%EC%A0%9C_%EB%94%94%ED%8F%B4%ED%8A%B8%EC%83%9D%EC%84%B1_01
// payload Table1: @업체번호, @상품번호, @요금제명, @요금제구분(083001/083002/083003), @처리자, @자원번호
export const createTourRatePlanDefault01 = async ({ companyNo, productNo, planName, planPolicyCode, userNo, resourceNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@요금제명', 값: String(planName), 리스트값: null },
        { 컬럼명: '@요금제구분', 값: String(planPolicyCode), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@자원번호', 값: Number(resourceNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%ED%88%AC%EC%96%B4%EC%9A%94%EA%B8%88%EC%A0%9C_%EB%94%94%ED%8F%B4%ED%8A%B8%EC%83%9D%EC%84%B1_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update tour rate plan order (요금제 순서 변경)
// SP: DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%9A%94%EA%B8%88%EC%A0%9C%EC%88%9C%EC%84%9C%EB%B3%80%EA%B2%BD
// payload Table1: @상품번호, @공급업체번호, @처리자, @상품리스트(JSON string), @자원번호
// orderList: Array<{ 가격옵션번호: number, 순번: number }>
export const updateTourRatePlanOrder = async ({ companyNo, productNo, userNo, resourceNo, orderList }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@상품리스트', 값: JSON.stringify(orderList || []), 리스트값: null },
        { 컬럼명: '@자원번호', 값: Number(resourceNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%9A%94%EA%B8%88%EC%A0%9C%EC%88%9C%EC%84%9C%EB%B3%80%EA%B2%BD`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create tour product resource (항공패턴 추가)
// SP: DBO.RCT_T_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EC%83%9D%EC%84%B1_02
// payload Table1: @자원명, @상품번호, @업체번호, @유저번호, @자원번호='', @대표항공코드='NULL', @재고기준=true
export const createTourProductResource02 = async ({ resourceName, productNo, companyNo, userNo, 대표항공코드 = 'NULL', 재고기준 = true }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@자원명', 값: String(resourceName), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@자원번호', 값: '', 리스트값: null },
        { 컬럼명: '@대표항공코드', 값: String(대표항공코드), 리스트값: null },
        { 컬럼명: '@재고기준', 값: !!재고기준 },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EC%83%9D%EC%84%B1_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete tour product resource (항공패턴 삭제)
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90_%EC%82%AD%EC%A0%9C
// payload Table1: @업체번호, @상품번호, @자원번호, @처리자
export const deleteTourProductResource = async ({ companyNo, productNo, resourceNo, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@자원번호', 값: Number(resourceNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90_%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update tour product resource (항공패턴) name
// SP: DBO.RCT_T_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EC%88%98%EC%A0%95_02
// payload Table1: @자원명, @상품번호, @업체번호, @유저번호, @자원번호, @대표항공코드, @재고기준
export const updateTourProductResource02 = async ({ resourceName, productNo, companyNo, userNo, resourceNo, 대표항공코드 = 'NULL', 재고기준 = false }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@자원명', 값: String(resourceName), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@자원번호', 값: Number(resourceNo), 리스트값: null },
        { 컬럼명: '@대표항공코드', 값: String(대표항공코드), 리스트값: null },
        { 컬럼명: '@재고기준', 값: !!재고기준 },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EC%88%98%EC%A0%95_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete shared series (시리즈 삭제)
// SP: DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%82%AD%EC%A0%9C
// Params: { sharedSeriesNo, companyNo?, userNo? }
export const deleteSharedSeries = async ({ sharedSeriesNo, companyNo: companyNoOverride, userNo: userNoOverride } = {}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNoOverride ?? companyNo ?? '') },
        { 컬럼명: '@처리자', 값: String(userNoOverride ?? userNo ?? '') },
        { 컬럼명: '@상품공유번호', 값: Number(sharedSeriesNo ?? 0) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%82%AD%EC%A0%9C`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      let serverMsg = '';
      try {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const errJson = await res.json();
          serverMsg = errJson?.메시지 || errJson?.message || errJson?.MESSAGE || errJson?.오류 || errJson?.error || '';
          if (!serverMsg && errJson && typeof errJson === 'object') {
            // Try common nested shapes
            const first = errJson?.table?.[0] || errJson?.table1?.[0] || errJson?.Table?.[0] || errJson?.Table1?.[0] || errJson?.result?.[0] || null;
            if (first && typeof first === 'object') {
              serverMsg = first?.메시지 || first?.message || first?.MESSAGE || first?.오류 || first?.error || '';
            }
            if (!serverMsg) {
              // Fallback to stringifying minimal content
              serverMsg = JSON.stringify(errJson);
            }
          }
        } else {
          serverMsg = await res.text();
        }
      } catch (_) {
        // ignore parse errors
      }
      const err = new Error(serverMsg || `HTTP ${res.status}`);
      err.status = res.status;
      err.apiMessage = serverMsg;
      throw err;
    }
    const data = await res.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create new shared series (시리즈 추가)
// SP: DBO.TRP_T_%EC%83%88%EB%A1%9C%EC%9A%B4%EC%8B%9C%EB%A6%AC%EC%9A%B4%EC%8B%9C%EB%A6%AC%EC%A6%88%EB%93%B1%EB%A1%9D
// Params: similar to saveSharedInventory but without @상품공유번호
export const createSharedSeries = async (params = {}) => {
  try {
    const { companyNo: tokenCompanyNo, userNo: tokenUserNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(params.companyNo ?? tokenCompanyNo ?? '') },
        { 컬럼명: '@유저번호', 값: String(params.userNo ?? tokenUserNo ?? '') },
        { 컬럼명: '@공급거래처번호', 값: params.supplierPartnerNo != null ? String(params.supplierPartnerNo) : '' },
        { 컬럼명: '@시리즈명', 값: String(params.seriesName ?? '') },
        { 컬럼명: '@재고종류', 값: String(params.stockType ?? '') },
        { 컬럼명: '@계약종류', 값: String(params.contractType ?? '') },
        { 컬럼명: '@출발공항', 값: String(params.departAirportCode ?? '') },
        { 컬럼명: '@목적공항', 값: String(params.arriveAirportCode ?? '') },
        { 컬럼명: '@패널티규정', 값: String(params.penaltyRule ?? '') },
        { 컬럼명: '@색상', 값: String(params.color ?? '') },
        { 컬럼명: '@출발편명', 값: String(params.departFlightNo ?? '') },
        { 컬럼명: '@귀국편명', 값: String(params.returnFlightNo ?? '') },
        { 컬럼명: '@박수', 값: params.nights != null ? String(params.nights) : '' },
        { 컬럼명: '@일수', 값: params.days != null ? String(params.days) : '' },
        { 컬럼명: '@편도구분', 값: String(params.oneWayType ?? '') },
        { 컬럼명: '@항공부가서비스', 값: String(params.airlineExtras ?? '') },
        { 컬럼명: '@항공부가서비스명', 값: String(params.airlineExtrasName ?? '') },
        { 컬럼명: '@호텔명', 값: String(params.hotelName ?? '') },
        { 컬럼명: '@객실타입', 값: String(params.roomType ?? '') },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%88%EB%A1%9C%EC%9A%B4%EC%8B%9C%EB%A6%AC%EC%A6%88%EB%93%B1%EB%A1%9D`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save shared inventory (공유재고 저장)
// SP: DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%88%98%EC%A0%95
// Params object expects: {
//   companyNo, userNo, sharedSeriesNo, supplierPartnerNo, seriesName, stockType,
//   contractType, departAirportCode, arriveAirportCode, penaltyRule, color,
//   departFlightNo, returnFlightNo, nights, days, oneWayType, airlineExtras,
//   hotelName, roomType
// }
export const saveSharedInventory = async (params = {}) => {
  try {
    const { companyNo: tokenCompanyNo, userNo: tokenUserNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(params.companyNo ?? tokenCompanyNo ?? '') },
        { 컬럼명: '@유저번호', 값: String(params.userNo ?? tokenUserNo ?? '') },
        { 컬럼명: '@상품공유번호', 값: Number(params.sharedSeriesNo ?? 0) },
        { 컬럼명: '@공급거래처번호', 값: params.supplierPartnerNo != null ? Number(params.supplierPartnerNo) : null },
        { 컬럼명: '@시리즈명', 값: String(params.seriesName ?? '') },
        { 컬럼명: '@재고종류', 값: String(params.stockType ?? '') },
        { 컬럼명: '@계약종류', 값: String(params.contractType ?? '') },
        { 컬럼명: '@출발공항', 값: String(params.departAirportCode ?? '') },
        { 컬럼명: '@목적공항', 값: String(params.arriveAirportCode ?? '') },
        { 컬럼명: '@패널티규정', 값: String(params.penaltyRule ?? '') },
        { 컬럼명: '@색상', 값: String(params.color ?? '') },
        { 컬럼명: '@출발편명', 값: String(params.departFlightNo ?? '') },
        { 컬럼명: '@귀국편명', 값: String(params.returnFlightNo ?? '') },
        { 컬럼명: '@박수', 값: params.nights != null ? Number(params.nights) : null },
        { 컬럼명: '@일수', 값: params.days != null ? Number(params.days) : null },
        { 컬럼명: '@편도구분', 값: String(params.oneWayType ?? '') },
        { 컬럼명: '@항공부가서비스', 값: String(params.airlineExtras ?? '') },
        { 컬럼명: '@호텔명', 값: params.hotelName ?? null },
        { 컬럼명: '@객실타입', 값: params.roomType ?? null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%88%98%EC%A0%95`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get shared series seat status by month range (공유재고 좌석현황)
// Register series inventory (시리즈 재고 등록)
// SP: DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%9E%AC%EA%B3%A0_01
// Params: { sharedSeriesNo, weekdays, startDate, endDate, seatCount }
// weekdays: comma-separated string like "1,2,3,4,5,6,7" (1=월요일)
export const registerSeriesInventory = async ({ sharedSeriesNo, weekdays, startDate, endDate, seatCount }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '1') },
        { 컬럼명: '@상품공유번호', 값: Number(sharedSeriesNo ?? 0) },
        { 컬럼명: '@유저번호', 값: String(userNo ?? '') },
        { 컬럼명: '@요일', 값: String(weekdays ?? '1,2,3,4,5,6,7') },
        { 컬럼명: '@시작일', 값: String(startDate ?? '') },
        { 컬럼명: '@종료일', 값: String(endDate ?? '') },
        { 컬럼명: '@좌석수', 값: String(seatCount ?? '0') },
        { 컬럼명: '@좌석상태', 값: '352001' },
        { 컬럼명: '@좌석구분', 값: '353001' },
        { 컬럼명: '@발권티엘', 값: 0 },
        { 컬럼명: '@네임티엘', 값: 0 },
        { 컬럼명: '@발권티엘_날짜', 값: 'NULL' },
        { 컬럼명: '@네임티엘_날짜', 값: 'NULL' },
        { 컬럼명: '@항공료_성인', 값: 0 },
        { 컬럼명: '@항공료_어린이', 값: 0 },
        { 컬럼명: '@항공료_영유아', 값: 0 },
        { 컬럼명: '@유류할증료_성인', 값: 0 },
        { 컬럼명: '@유류할증료_어린이', 값: 0 },
        { 컬럼명: '@유류할증료_영유아', 값: 0 },
        { 컬럼명: '@담당자', 값: String(userNo ?? '') },
        { 컬럼명: '@PNR네임갯수', 값: 0 },
        { 컬럼명: '@발권오스번호', 값: '' },
        { 컬럼명: '@컷오프정보', 값: '[]' },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%9E%AC%EA%B3%A0_01`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get series management data (시리즈 관리 조회)
// SP: DBO.TRP_S_%EC%8B%9C%EB%A6%AC%EC%A6%88%EA%B4%80%EB%A6%AC
// Params: { companyNo, startDate, endDate, searchCriteria='출발일', userNo, seriesNo }
// Returns: array in data.table with fields like 시리즈명, 재고종류, 계약종류, 출발공항, 목적공항, 출발편명, 귀국편명, 출발일, 요일, 블럭수, 판매인원, etc.
export const getSeriesManagement = async ({ startDate, endDate, searchCriteria = '출발일', seriesNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '1') },
        { 컬럼명: '@시작일', 값: String(startDate ?? '') },
        { 컬럼명: '@종료일', 값: String(endDate ?? '') },
        { 컬럼명: '@검색기준', 값: String(searchCriteria) },
        { 컬럼명: '@유저번호', 값: String(userNo ?? '') },
        { 컬럼명: '@시리즈번호', 값: Number(seriesNo ?? 0) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%8B%9C%EB%A6%AC%EC%A6%88%EA%B4%80%EB%A6%AC`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// SP: DBO.TRP_S_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%9E%AC%EA%B3%A0_%EA%B8%B0%EA%B0%84_%EC%8B%9C%EB%A6%AC%EC%A6%88%EB%B2%88%ED%98%B8
// payload Table1: @업체번호, @시리즈번호, @시작년월(YYYYMM), @종료년월(YYYYMM)
// Returns: parsed array from first row's 재고 JSON string or []
export const getSharedSeriesSeatStatus = async ({ seriesNo, startYm, endYm }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@시리즈번호', 값: Number(seriesNo) },
        { 컬럼명: '@시작년월', 값: String(startYm) },
        { 컬럼명: '@종료년월', 값: String(endYm) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%9E%AC%EA%B3%A0_%EA%B8%B0%EA%B0%84_%EC%8B%9C%EB%A6%AC%EC%A6%88%EB%B2%88%ED%98%B8`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const row = Array.isArray(data?.table) && data.table.length ? data.table[0] : Array.isArray(data?.Table) && data.Table.length ? data.Table[0] : null;
    const raw = row?.재고;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  } catch (error) {
    return handleApiError(error);
  }
};

// Unlink shared series from selected events (시리즈 연동 해제)
// SP: DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%97%B0%EB%8F%99%ED%95%B4%EC%A0%9C
// Params: { productNo, companyNo, userNo, events: Array<{ 날짜, 시간, 자원번호 }>, calendarUse=true }
export const unlinkSharedSeriesFromSelectedEvents = async ({ productNo, companyNo, userNo, events, calendarUse = true }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@선택행사', 값: JSON.stringify(Array.isArray(events) ? events : []) },
        { 컬럼명: '@캘린더용', 값: !!calendarUse },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%97%B0%EB%8F%99%ED%95%B4%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Link selected dates to a shared series (선택DATA 시리즈 연동)
// SP: DBO.TRP_T_%EC%84%A0%ED%83%9DDATA%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%97%B0%EB%8F%99_01
// Params: { productNo, companyNo, userNo, events: Array<{ 날짜, 시간, 자원번호 }>, sharedSeriesNo, calendarUse=true }
export const linkSharedSeriesToSelectedEvents = async ({ productNo, companyNo, userNo, events, sharedSeriesNo, calendarUse = true }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@선택행사', 값: JSON.stringify(Array.isArray(events) ? events : []) },
        { 컬럼명: '@상품공유번호', 값: Number(sharedSeriesNo) },
        { 컬럼명: '@캘린더용', 값: !!calendarUse },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%84%A0%ED%83%9DDATA%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%97%B0%EB%8F%99_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get product note (상품비고 조회)
// SP: DBO.TRP_S_%EA%B3%B5%EA%B8%89%EC%97%85%EC%B2%B4%EC%83%81%ED%92%88%EB%B9%84%EA%B3%A0
// payload Table1: @상품번호
// Returns first row's 상품비고 (string) or '' if none
export const getProductNote = async ({ productNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EA%B3%B5%EA%B8%89%EC%97%85%EC%B2%B4%EC%83%81%ED%92%88%EB%B9%84%EA%B3%A0`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const note = rows?.[0]?.상품비고 ?? '';
    return typeof note === 'string' ? note : String(note || '');
  } catch (error) {
    return handleApiError(error);
  }
};

// Save product note (상품비고 저장)
// SP: DBO.TRP_T_%EC%83%81%ED%92%88%EB%B9%84%EA%B3%A0%EC%A0%80%EC%9E%A5
// payload Table1: @공급자번호, @상품번호, @상품비고, @처리자
export const saveProductNote = async ({ productNo, note }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo) },
        { 컬럼명: '@상품번호', 값: Number(productNo) },
        { 컬럼명: '@상품비고', 값: String(note || '') },
        { 컬럼명: '@처리자', 값: String(userNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%81%ED%92%88%EB%B9%84%EA%B3%A0%EC%A0%80%EC%9E%A5`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Add a new hotel product (호텔 간략신규저장)
// SP: DBO.TRP_T_%ED%98%B8%ED%85%94_%EA%B0%84%EB%9E%B5%EC%8B%A0%EA%B7%9C%EC%A0%80%EC%9E%A5
// payload Table1 fields:
// @구분='I', @상품번호='NULL', @상품명, @공급자번호, @처리자, @장소번호, @공급거래처번호, @기초통화코드, @원가통화코드, @상품타입
// Returns: { table: [{ 상품번호: <number> }] }
export const addHotelProduct = async ({
  productName,
  supplierNo,
  userNo,
  placeNo,
  partnerNo,
  baseCurrencyCode,
  costCurrencyCode,
  productTypeCode,
}) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@구분', 값: 'I' },
        { 컬럼명: '@상품번호', 값: 'NULL' },
        { 컬럼명: '@상품명', 값: String(productName) },
        { 컬럼명: '@공급자번호', 값: String(supplierNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@장소번호', 값: Number(placeNo) },
        { 컬럼명: '@공급거래처번호', 값: String(partnerNo) },
        { 컬럼명: '@기초통화코드', 값: String(baseCurrencyCode) },
        { 컬럼명: '@원가통화코드', 값: String(costCurrencyCode) },
        { 컬럼명: '@상품타입', 값: String(productTypeCode) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94_%EA%B0%84%EB%9E%B5%EC%8B%A0%EA%B7%9C%EC%A0%80%EC%9E%A5`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get supplier partners list (공급거래처 리스트)
// SP: DBO.CW_S_%EA%B3%B5%EA%B8%89%EA%B1%B0%EB%9E%98%EC%B2%98
// payload Table1: @업체번호, @사용여부1=true, @사용여부2=true
// Returns: array in data.table with fields like 거래처번호, 거래처명, 대표전화, 대표자명, 대표이메일, 사용여부
export const getSupplierPartners = async () => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@사용여부1', 값: true },
        { 컬럼명: '@사용여부2', 값: true },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_S_%EA%B3%B5%EA%B8%89%EA%B1%B0%EB%9E%98%EC%B2%98`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get manual code list by kind (ex: CW지급수배업체)
// SP: DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C
// payload Table1: @종류, @공급업체번호
// Returns: array of rows with fields like 코드, 코드명
export const getManualCodes = async ({ kind, companyNoOverride } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@종류', 값: String(kind || '') , 리스트값: null},
        { 컬럼명: '@공급업체번호', 값: String(companyNoOverride ?? companyNo ?? '') , 리스트값: null},
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get shared inventory list (공유재고 리스트)
// SP: DBO.TRP_S_%EA%B3%B5%EC%9C%A0%EC%9E%AC%EA%B3%A0
// payload Table1: @공급업체번호, @상품번호
// Returns: array in data.table/Table with fields like 시리즈번호, 시리즈명, 공유색상, 종류, 편도, 공급사
export const getSharedInventoryList = async ({ productNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo) },
        { 컬럼명: '@상품번호', 값: Number(productNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EA%B3%B5%EC%9C%A0%EC%9E%AC%EA%B3%A0`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create a new supplier partner (공급거래처 신규 등록)
// SP: DBO.CW_T_%EA%B3%B5%EA%B8%89%EA%B1%B0%EB%9E%98%EC%B2%98%EC%B6%94%EA%B0%80_01
export const createSupplierPartner = async ({
  name,
  email = '',
  ceo = '',
  manager = '',
  address = '',
  addressDetail = '',
  zip = '',
  countryCode = '',
  phone = '',
  bankCode = '',
  bankName = '',
  accountNo = '',
  accountHolder = '',
}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '') },
        { 컬럼명: '@등록자', 값: String(userNo ?? '') },
        { 컬럼명: '@업체번호', 값: 'NULL' },
        { 컬럼명: '@등록기준', 값: '0' },
        { 컬럼명: '@신규업체명', 값: String(name ?? '') },
        { 컬럼명: '@아이디', 값: '' },
        { 컬럼명: '@이메일', 값: String(email ?? '') },
        { 컬럼명: '@대표자', 값: String(ceo ?? '') },
        { 컬럼명: '@담당자', 값: String(manager ?? '') },
        { 컬럼명: '@기본주소', 값: String(address ?? '') },
        { 컬럼명: '@상세주소', 값: String(addressDetail ?? '') },
        { 컬럼명: '@우편번호', 값: String(zip ?? '') },
        { 컬럼명: '@국가번호', 값: String(countryCode ?? '') },
        { 컬럼명: '@대표전화', 값: String(phone ?? '') },
        { 컬럼명: '@이메일계정모름', 값: !(email && String(email).trim()) },
        { 컬럼명: '@은행코드', 값: String(bankCode ?? '') },
        { 컬럼명: '@계좌번호', 값: String(accountNo ?? '') },
        { 컬럼명: '@예금주', 값: String(accountHolder ?? '') },
        { 컬럼명: '@은행명', 값: String(bankName ?? '') },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%EA%B3%B5%EA%B8%89%EA%B1%B0%EB%9E%98%EC%B2%98%EC%B6%94%EA%B0%80_01`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get hotel resource info (객실 자원 상세)
// SP: DBO.TRP_S_%ED%98%B8%ED%85%94%EC%9E%90%EC%9B%90%EC%A0%95%EB%B3%B4
// payload Table1: @업체번호, @자원번호
// Returns: array in data.table with fields like 자원명, 자원영문명, 기준수량, 최대수량, 어메니티내역, 추가성인요금, 추가아동요금, 기준인원초과사용여부, 기준인원초과무료아동수, ...
export const getHotelResourceInfo = async ({ resourceNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@자원번호', 값: Number(resourceNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%98%B8%ED%85%94%EC%9E%90%EC%9B%90%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get mapped content info for a hotel room resource (객실 자원-콘텐츠 정보 조회)
// SP: DBO.TRP_S_%ED%98%B8%ED%85%94%EC%9E%90%EC%9B%90%EC%BD%98%ED%85%90%EC%B8%A0%EC%A0%95%EB%B3%B4
// payload Table1: @업체번호, @자원번호
// Returns: array in data.table (fields: 선택, 컨텐츠번호, 컨텐츠명, 매핑컨텐츠, photo, 내용)
export const getHotelResourceContentInfo = async ({ resourceNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@자원번호', 값: Number(resourceNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%98%B8%ED%85%94%EC%9E%90%EC%9B%90%EC%BD%98%ED%85%90%EC%B8%A0%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete a consult note (상담기록 삭제)
// SP: DBO.TRP_T_%EC%83%81%EB%8B%B4%EA%B8%B0%EB%A1%9D%EC%A0%95%EB%B3%B4%EC%82%AD%EC%A0%9C
// payload Table1: @상담기록번호, @처리자, @업체번호
export const deleteConsultNote = async ({ recordNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@상담기록번호', 값: Number(recordNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%81%EB%8B%B4%EA%B8%B0%EB%A1%9D%EC%A0%95%EB%B3%B4%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete selected tour session events (선택DATA 삭제 - 판매된 날짜 제외)
// SP: DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%84%B8%EC%85%98%EA%B4%80%EB%A6%AC_%EC%82%AD%EC%A0%9C_%EC%84%A0%ED%83%9DDATA%ED%8C%90%EB%A7%A4%EB%90%9C%EB%82%A0%EC%A7%9C%EC%A0%9C%EC%99%B8_04
// Params: { productNo, companyNo, userNo, events: Array<{ 날짜:string, 시간:string, 자원번호:number }>, calendarUse=true }
export const deleteTourSelectedEvents = async ({ productNo, companyNo, userNo, events, calendarUse = true }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@선택행사', 값: JSON.stringify(Array.isArray(events) ? events : []), 리스트값: null },
        { 컬럼명: '@캘린더용', 값: !!calendarUse, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%84%B8%EC%85%98%EA%B4%80%EB%A6%AC_%EC%82%AD%EC%A0%9C_%EC%84%A0%ED%83%9DDATA%ED%8C%90%EB%A7%A4%EB%90%9C%EB%82%A0%EC%A7%9C%EC%A0%9C%EC%99%B8_04`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get consult notes (상담기록 목록) by 상담번호
// SP: DBO.TRP_S_%EC%83%81%EB%8B%B4%EB%B2%88%ED%98%B8_%EC%83%81%EB%8B%B4%EA%B8%B0%EB%A1%9D
// payload Table1: @상담번호
// Returns: array in data.table
export const getConsultNotes = async ({ consultNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상담번호', 값: Number(consultNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%83%81%EB%8B%B4%EB%B2%88%ED%98%B8_%EC%83%81%EB%8B%B4%EA%B8%B0%EB%A1%9D`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create or update a consult note (상담기록 추가/편집)
// SP: DBO.TRP_T_%EC%83%81%EB%8B%B4%EA%B8%B0%EB%A1%9D%EC%A0%95%EB%B3%B4
// payload Table1: @상담번호, @상담기록번호(신규 0), @내용, @처리자, @업체번호
export const saveConsultNote = async ({ consultNo, recordNo = 0, content }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@상담번호', 값: Number(consultNo) },
        { 컬럼명: '@상담기록번호', 값: Number(recordNo || 0) },
        { 컬럼명: '@내용', 값: String(content || '') },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%81%EB%8B%B4%EA%B8%B0%EB%A1%9D%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get quote prices for a product/date (성인/소아/유아 판매가 추출)
// SP: DBO.RCT_S_%EA%B0%80%EA%B2%A9%EC%98%88%EC%95%BD_02
// Params: { productNo, startDate, productTypeName? } − endDate equals startDate; 상품종류=productTypeName(default '투어'), 판매여부1/2=true, 판매된리스트만보기=false, 키워드='', 구분='3', 플랫폼/결제수수료반영=false
// Returns: { adult: number|null, child: number|null, infant: number|null, rawRow }
export const getQuotePrices = async ({ productNo, startDate, productTypeName }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@상품종류', 값: String(productTypeName || '투어'), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@시작일', 값: String(startDate || ''), 리스트값: null },
        { 컬럼명: '@종료일', 값: String(startDate || ''), 리스트값: null },
        { 컬럼명: '@판매여부1', 값: true, 리스트값: null },
        { 컬럼명: '@판매여부2', 값: true, 리스트값: null },
        { 컬럼명: '@판매된리스트만보기', 값: false, 리스트값: null },
        { 컬럼명: '@키워드', 값: '', 리스트값: null },
        { 컬럼명: '@구분', 값: '3', 리스트값: null },
        { 컬럼명: '@플랫폼수수료반영', 값: false, 리스트값: null },
        { 컬럼명: '@결제수수료반영', 값: false, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EA%B0%80%EA%B2%A9%EC%98%88%EC%95%BD_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const first = rows?.[0] || null;
    const rawTotal = first?.총판매가 ?? first?.판매가 ?? null;
    const normalize = (s) => {
      if (s == null) return null;
      const t = String(s).trim();
      if (!t) return null;
      const num = Number(t.replace(/[^0-9.-]/g, ''));
      return Number.isFinite(num) ? num : null;
    };
    let adult = null, child = null, infant = null;
    if (rawTotal) {
      const parts = String(rawTotal).split('/').map(p => p.trim());
      if (parts.length >= 3) {
        adult = normalize(parts[0]);
        child = normalize(parts[1]);
        infant = normalize(parts[2]);
      } else {
        // Single price: treat as adult only
        adult = normalize(parts[0]);
      }
    }
    const allZeroTotalSale = Array.isArray(rows) && rows.length > 0
      ? rows.every((r) => {
        const rt = r?.총판매가 ?? r?.판매가 ?? null;
        if (rt == null) return false;
        const parts = String(rt).split('/').map(p => p.trim()).filter(Boolean);
        if (parts.length >= 3) {
          const a = normalize(parts[0]);
          const c = normalize(parts[1]);
          const i = normalize(parts[2]);
          return a === 0 && c === 0 && i === 0;
        }
        const single = normalize(parts[0]);
        return single === 0;
      })
      : false;

    return { adult, child, infant, rawRow: first, allZeroTotalSale };
  } catch (error) {
    return handleApiError(error);
  }
};

// Save consult info (상담정보 저장)
// SP: DBO.TRP_T_%EC%83%81%EB%8B%B4%EC%A0%95%EB%B3%B4_01
// Required args: { consultNo, productNo, form }
// Optional args: { consultStatusCode, consultTypeCode } to override 상담상태/상담구분 코드
// Optional args: { joinCompanyName } - 조인업체명 (예약현황에서 취소가 아닌 첫 번째 주문번호)
// Table1 columns: @상담정보(JSON string), @처리자(token.userNo), @업체번호(token.companyNo), @팀컬러(productNo), @예상수익 0, @경로 '', @성인예상수익 0, @소아예상수익 0, @유아예상수익 0, @성인원가 0, @소아원가 0, @유아원가 0
export const saveConsultInfo = async ({ consultNo, productNo, form, consultStatusCode, consultTypeCode, joinCompanyName, zeroPriceToProduct }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const pn = productNo != null ? String(productNo) : '';
    const cn = consultNo != null ? Number(consultNo) : 0;
    // Compute per-person cost from 세부원가목록 if provided; fallback to 단가 필드
    const itemList = Array.isArray(form?.세부원가목록) ? form.세부원가목록 : [];
    const sumPerPerson = itemList.reduce((acc, cur) => {
      acc.adult += Number(cur?.성인 || 0);
      acc.child += Number(cur?.어린이 || 0);
      acc.infant += Number(cur?.영유아 || 0);
      return acc;
    }, { adult: 0, child: 0, infant: 0 });

    const perAdultCost = itemList.length > 0 ? sumPerPerson.adult : Number(form?.성인원가 || 0);
    const perChildCost = itemList.length > 0 ? sumPerPerson.child : Number(form?.소아원가 || 0);
    const perInfantCost = itemList.length > 0 ? sumPerPerson.infant : Number(form?.유아원가 || 0);

    const perAdultSale = Number(form?.성인판매가 || 0);
    const perChildSale = Number(form?.소아판매가 || 0);
    const perInfantSale = Number(form?.유아판매가 || 0);

    const adultCount = Number(form?.성인수 || 0);
    const childCount = Number(form?.소아수 || 0);
    const infantCount = Number(form?.유아수 || 0);

    const totalCost = perAdultCost * adultCount + perChildCost * childCount + perInfantCost * infantCount;
    const totalSale = perAdultSale * adultCount + perChildSale * childCount + perInfantSale * infantCount;
    const totalProfit = totalSale - totalCost;

    const perAdultProfit = perAdultSale - perAdultCost;
    const perChildProfit = perChildSale - perChildCost;
    const perInfantProfit = perInfantSale - perInfantCost;

    // Build 상담정보 JSON using provided editable fields and computed values
    const resolvedStatus = consultStatusCode ? String(consultStatusCode) : '420001'; // 기본: 진행중
    const resolvedType = consultTypeCode ? String(consultTypeCode) : '419001'; // 기본: 일반상담
    const info = {
      견적경로: '421001',
      상담번호: cn,
      상담구분: resolvedType,
      상담상태: resolvedStatus,
      조인업체명: joinCompanyName || '',
      담당자: '권태흥111',
      담당자번호: Number(userNo),
      판매업체: form?.판매업체 != null && String(form.판매업체).trim() !== '' ? String(form.판매업체).trim() : null,
      판매업체번호: form?.판매업체번호 != null && String(form.판매업체번호).trim() !== '' ? String(form.판매업체번호).trim() : null,
      수배처: form?.수배처 ?? null,
      수배처번호: form?.수배처번호 ?? null,
      팀컬러: [pn ? pn : ''],
      요청자명: form?.요청자명 ?? '',
      요청자휴대폰: form?.요청자휴대폰 ?? '',
      요청자이메일: form?.요청자이메일 ?? '',
      시작일: form?.시작일 ?? '',
      종료일: form?.종료일 ?? '',
      견적명: form?.견적명 ?? '',
      견적내용: form?.견적내용 ?? '',
      성인수: adultCount,
      소아수: childCount,
      유아수: infantCount,
      // 인솔자수 컬럼을 "일정표 0원 표시" 체크값 저장소로 재사용한다.
      // (일정표0원표시 커스텀 필드는 저장 SP가 버리므로, 표준 컬럼인 인솔자수(0/1)에 실어 왕복시킴)
      인솔자수: zeroPriceToProduct === true ? 1 : 0,
      성인판매가: perAdultSale,
      소아판매가: perChildSale,
      유아판매가: perInfantSale,
      일정표0원표시: zeroPriceToProduct === true,
      요청일정표: '407002',
      첨부파일리스트: Array.isArray(form?.첨부파일리스트) ? form.첨부파일리스트 : [],
      견적첨부파일리스트: Array.isArray(form?.견적첨부파일리스트) ? form.견적첨부파일리스트 : [],
      일정표주소: '',
      적용환율: [
        { 통화코드: '058001', 통화기호: '￦', 적용환율: 1, 실시간환율: 1, 평균환율: 1 },
        { 통화코드: '058002', 통화기호: '$', 적용환율: 1408.4507, 실시간환율: 1408.4507, 평균환율: 1408.4507 },
        { 통화코드: '058003', 통화기호: '€', 적용환율: 1652.8926, 실시간환율: 1652.8926, 평균환율: 1652.8926 },
        { 통화코드: '058004', 통화기호: 'A$', 적용환율: 929.368, 실시간환율: 929.368, 평균환율: 929.368 },
        { 통화코드: '058005', 통화기호: '元', 적용환율: 197.7066, 실시간환율: 197.7066, 평균환율: 197.7066 },
        { 통화코드: '058006', 통화기호: '円', 적용환율: 9.5457, 실시간환율: 9.5457, 평균환율: 9.5457 },
        { 통화코드: '058007', 통화기호: '฿', 적용환율: 43.5313, 실시간환율: 43.5313, 평균환율: 43.5313 },
        { 통화코드: '058008', 통화기호: '₫', 적용환율: 0.0533, 실시간환율: 0.0533, 평균환율: 0.0533 },
        { 통화코드: '058009', 통화기호: 'kr', 적용환율: 141.203, 실시간환율: 141.203, 평균환율: 141.203 },
        { 통화코드: '058010', 통화기호: 'BD', 적용환율: 3731.3433, 실시간환율: 3731.3433, 평균환율: 3731.3433 },
        { 통화코드: '058011', 통화기호: 'KR', 적용환율: 221.2879, 실시간환율: 221.2879, 평균환율: 221.2879 },
        { 통화코드: '058012', 통화기호: 'NZ$', 적용환율: 821.0181, 실시간환율: 821.0181, 평균환율: 821.0181 },
        { 통화코드: '058013', 통화기호: 'kr', 적용환율: 150.1727, 실시간환율: 150.1727, 평균환율: 150.1727 },
        { 통화코드: '058014', 통화기호: 'B$', 적용환율: 1091.7031, 실시간환율: 1091.7031, 평균환율: 1091.7031 },
        { 통화코드: '058015', 통화기호: '元', 적용환율: 197.2387, 실시간환율: 197.2387, 평균환율: 197.2387 },
        { 통화코드: '058016', 통화기호: 'HK$', 적용환율: 180.93, 실시간환율: 180.93, 평균환율: 180.93 },
        { 통화코드: '058017', 통화기호: 'S$', 적용환율: 1091.7031, 실시간환율: 1091.7031, 평균환율: 1091.7031 },
        { 통화코드: '058018', 통화기호: 'DH', 적용환율: 383.2886, 실시간환율: 383.2886, 평균환율: 383.2886 },
        { 통화코드: '058019', 통화기호: 'Fr', 적용환율: 1769.9115, 실시간환율: 1769.9115, 평균환율: 1769.9115 },
        { 통화코드: '058020', 통화기호: 'KD', 적용환율: 4608.2949, 실시간환율: 4608.2949, 평균환율: 4608.2949 },
        { 통화코드: '058021', 통화기호: '£', 적용환율: 1897.5332, 실시간환율: 1897.5332, 평균환율: 1897.5332 },
        { 통화코드: '058022', 통화기호: 'RM', 적용환율: 334.4482, 실시간환율: 334.4482, 평균환율: 334.4482 },
        { 통화코드: '058023', 통화기호: 'CA$', 적용환율: 1008.0645, 실시간환율: 1008.0645, 평균환율: 1008.0645 },
        { 통화코드: '058024', 통화기호: 'Rp', 적용환율: 0.085, 실시간환율: 0.085, 평균환율: 0.085 },
        { 통화코드: '058025', 통화기호: 'SR', 적용환율: 375.3754, 실시간환율: 375.3754, 평균환율: 375.3754 },
        { 통화코드: '058026', 통화기호: '₱', 적용환율: 24.309, 실시간환율: 24.309, 평균환율: 24.309 },
      ],
      통화코드: '058001',
      통화기호: '￦',
      원가구분: '401002',
      총원가: [
        { 구분: '총원가', 통화코드: '058001', 통화기호: 'KRW', 적용환율: 1, 성인: perAdultCost, 어린이: perChildCost, 영유아: perInfantCost },
      ],
      세부원가: (itemList.length > 0
        ? itemList.map((it) => ({
            원가항목: it?.원가항목 || '',
            원가항목코드: it?.원가항목코드 || '',
            통화기호: 'KRW',
            통화코드: '058001',
            환율적용방식: '전일종가',
            환율적용방식코드: '312001',
            적용환율: 1,
            성인: Number(it?.성인 || 0),
            어린이: Number(it?.어린이 || 0),
            영유아: Number(it?.영유아 || 0),
          }))
        : [
            { 원가항목: '통합원가', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '전일종가', 환율적용방식코드: '312001', 적용환율: 1, 성인: perAdultCost, 어린이: perChildCost, 영유아: perInfantCost },
          ]
      ),
      공용원가: [
        { 원가항목: '인솔자비용', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '전일종가', 환율적용방식코드: '312001', 적용환율: 1, 성인: 0, 원가비고: '' },
        { 원가항목: '가이드비용', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '전일종가', 환율적용방식코드: '312001', 적용환율: 1, 성인: 0, 원가비고: '' },
      ],
      수익계산: [
        { 항목: '인원수', 번호: 1, 통화코드: '058001', 통화기호: '￦', 성인: adultCount, 어린이: childCount, 영유아: infantCount },
        { 항목: '원가', 번호: 2, 통화코드: '058001', 통화기호: '￦', 성인: String(perAdultCost), 어린이: perChildCost, 영유아: perInfantCost },
        { 항목: '판매가', 번호: 3, 통화코드: '058001', 통화기호: '￦', 성인: perAdultSale, 어린이: perChildSale, 영유아: perInfantSale },
        { 항목: '예상수익', 번호: 4, 통화코드: '058001', 통화기호: '￦', 성인: perAdultProfit, 어린이: perChildProfit, 영유아: perInfantProfit },
      ],
    };

    const payload = {
      Table1: [
        { 컬럼명: '@상담정보', 값: JSON.stringify(info) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@팀컬러', 값: pn },
        { 컬럼명: '@예상수익', 값: totalProfit },
        { 컬럼명: '@경로', 값: '' },
        { 컬럼명: '@성인예상수익', 값: perAdultProfit },
        { 컬럼명: '@소아예상수익', 값: perChildProfit },
        { 컬럼명: '@유아예상수익', 값: perInfantProfit },
        { 컬럼명: '@성인원가', 값: perAdultCost },
        { 컬럼명: '@소아원가', 값: perChildCost },
        { 컬럼명: '@유아원가', 값: perInfantCost },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%81%EB%8B%B4%EC%A0%95%EB%B3%B4_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get consult list for Quote Management (상담리스트)
// SP: DBO.ITN_S_%EC%83%81%EB%8B%B4%EB%A6%AC%EC%8A%A4%ED%8A%B8
// payload Table1: @업체번호, @시작일, @종료일, @구분='등록일', @상태='1', @유저번호
// Returns: array from data.table (fields include 상담번호, 팀컬러, ...)
export const getConsultList = async ({ startDate, endDate } = {}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    // Defaults: last 1 year ~ today
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const end = String(endDate || toYmd(today));
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    const start = String(startDate || toYmd(oneYearAgo));

    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@시작일', 값: start },
        { 컬럼명: '@종료일', 값: end },
        { 컬럼명: '@구분', 값: '등록일' },
        { 컬럼명: '@상태', 값: '4' },
        { 컬럼명: '@유저번호', 값: String(userNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.ITN_S_%EC%83%81%EB%8B%B4%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get consult info (상담정보) by 상담번호
// SP: DBO.TRP_S_%EC%83%81%EB%8B%B4%EC%A0%95%EB%B3%B4
// payload Table1: @업체번호, @상담번호, @유저번호, @구분='U'
// Returns: first element of data.table[0].상담정보[0] if present
export const getConsultInfo = async ({ consultNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@상담번호', 값: Number(consultNo) },
        { 컬럼명: '@유저번호', 값: String(userNo) },
        { 컬럼명: '@구분', 값: 'U' },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%83%81%EB%8B%B4%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const table = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const raw = table?.[0]?.상담정보;
    let info = null;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) info = parsed[0] || null; else if (parsed && typeof parsed === 'object') info = parsed; else info = null;
      } catch (e) {
        console.warn('Failed to parse 상담정보 JSON string:', e);
        info = null;
      }
    } else if (Array.isArray(raw)) {
      info = raw[0] || null;
    } else if (raw && typeof raw === 'object') {
      info = raw;
    } else {
      // Some APIs may also include flattened fields directly in table[0]
      if (table && table[0] && typeof table[0] === 'object') {
        // Heuristic: if keys like '상담번호' exist at top-level, treat as info
        const candidate = table[0];
        if ('상담번호' in candidate || '요청자명' in candidate || '견적명' in candidate) {
          info = candidate;
        }
      }
    }

    // 사이드카(estimate_extras)에서 SP 가 저장하지 못한 부가정보(여행지 도시 등)를 병합.
    // 실패해도 상담상세는 SP 데이터만으로 정상 표시(부가정보만 누락).
    if (info) {
      try {
        const extras = await getEstimateExtras(consultNo);
        if (extras && typeof extras === 'object') {
          info = { ...info, extras, 여행지도시: extras.destination ?? extras.city_name ?? info.여행지도시 };
        }
      } catch (e) {
        console.warn('부가정보 병합 실패(무시):', e);
      }
    }
    return info;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create a new unregistered tour reservation (미등록 예약 생성)
// SP: DBO.WSP_T_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88_%EB%AF%B8%EB%93%B1%EB%A1%9D%EC%98%88%EC%95%BD
// Required inputs: { productName, durationDays, startDate, reserverName, phone, cityNo? }
// Returns raw dataset; caller will extract 주문번호 from data.table[0]
export const createUnregisteredTourReservation = async ({ productName, durationDays, startDate, reserverName, phone, cityNo }) => {
  try {
    const { companyNo, userNo, memberNo } = getUserInfo() || {};
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const todayStr = toYmd(today);
    const onlyDigitsPhone = String(phone || '').replace(/\D+/g, '');
    // Minimal traveler JSON as per example
    const autoReserveJson = [
      {
        번호: 0,
        성인수: '1',
        소아수: 0,
        유아수: 0,
        나이배열: '',
        입금가성인: 0,
        입금가소아: 0,
        입금가유아: 0,
        할인액: 0,
        예약상태: '049001',
        고객요청: '',
        원가율: 0,
      },
    ];
    // One traveler field example, with unique id
    const uniqueId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const travelerInputJson = [
      {
        번호: 0,
        필드번호: 8,
        필드명: '한글이름',
        값: '',
        유니크: uniqueId,
        나이: -1,
      },
    ];

    const payload = {
      Table1: [
        { 컬럼명: '@주문번호', 값: 'NULL' },
        { 컬럼명: '@공급자번호', 값: String(companyNo) },
        { 컬럼명: '@상품명', 값: String(productName || '') },
        { 컬럼명: '@상품타입', 값: '309200' },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@상품코드', 값: '' },
        { 컬럼명: '@도시번호', 값: String(cityNo || '54908') },
        { 컬럼명: '@소요시간타입', 값: '012001' },
        { 컬럼명: '@소요시간', 값: String(Number(durationDays) || 0) },
        { 컬럼명: '@행사시작일시', 값: String(startDate || '') },
        { 컬럼명: '@예약자', 값: String(reserverName || '') },
        { 컬럼명: '@연락처', 값: onlyDigitsPhone },
        { 컬럼명: '@단독여부', 값: false },
        { 컬럼명: '@판매업체번호', 값: Number(companyNo) },
        { 컬럼명: '@수배담당자', 값: String(memberNo || userNo || '') },
        { 컬럼명: '@T_자동예약JSON', 값: JSON.stringify(autoReserveJson) },
        { 컬럼명: '@T_입력여행객JSON', 값: JSON.stringify(travelerInputJson) },
        { 컬럼명: '@신청금납부일', 값: todayStr },
        { 컬럼명: '@잔금납부일', 값: String(startDate || '') },
        { 컬럼명: '@신청금액', 값: '0' },
        { 컬럼명: '@성인연령', 값: 12 },
        { 컬럼명: '@어린이N연령', 값: 2 },
        { 컬럼명: '@유아연령', 값: 0 },
        { 컬럼명: '@어린이N연령끝', 값: 11 },
        { 컬럼명: '@유아연령끝', 값: 2 },
        { 컬럼명: '@고객사명', 값: '' },
        { 컬럼명: '@시리즈번호', 값: 'NULL' },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88_%EB%AF%B8%EB%93%B1%EB%A1%9D%EC%98%88%EC%95%BD`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create a product select option (선택옵션 추가)
// SP: DBO.TRP_T_%EC%84%A0%ED%83%9D%EC%98%B5%EC%85%98
// Required args: { productNo, name, desc, chargeModeCode: '066001'|'066003', adultFee, childFee, infantFee }
export const createProductSelectOption = async ({ productNo, name, desc, chargeModeCode, adultFee = 0, childFee = 0, infantFee = 0 }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@구분', 값: 'I' },
        { 컬럼명: '@서비스번호', 값: 'NULL' },
        { 컬럼명: '@서비스명', 값: String(name || '') },
        { 컬럼명: '@서비스영문명', 값: '' },
        { 컬럼명: '@적용방식', 값: String(chargeModeCode || '066001') },
        { 컬럼명: '@통화', 값: '058001' },
        { 컬럼명: '@상품번호', 값: Number(productNo) },
        { 컬럼명: '@설명', 값: String(desc || '') },
        { 컬럼명: '@사용시간', 값: 0 },
        { 컬럼명: '@사전예약불가', 값: false },
        { 컬럼명: '@성인요금', 값: Number(adultFee) || 0 },
        { 컬럼명: '@소아요금', 값: Number(childFee) || 0 },
        { 컬럼명: '@유아요금', 값: Number(infantFee) || 0 },
        { 컬럼명: '@이미지경로', 값: '' },
        { 컬럼명: '@대체일정설명', 값: '' },
        { 컬럼명: '@미선택시가이드동행', 값: '1' },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%84%A0%ED%83%9D%EC%98%B5%EC%85%98`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};
// Delete a product select option (선택옵션 삭제)
// SP: DBO.TRP_T_%EC%84%A0%ED%83%9D%EC%98%B5%EC%85%98%EC%82%AD%EC%A0%9C
// Required args: { serviceNo }
export const deleteProductSelectOption = async ({ serviceNo }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@서비스번호', 값: Number(serviceNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%84%A0%ED%83%9D%EC%98%B5%EC%85%98%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update a product select option (선택옵션 수정)
// SP: DBO.TRP_T_%EC%84%A0%ED%83%9D%EC%98%B5%EC%85%98
// Required args: { serviceNo, productNo, name, desc, chargeModeCode: '066001'|'066003', adultFee, childFee, infantFee }
export const updateProductSelectOption = async ({ serviceNo, productNo, name, desc, chargeModeCode, adultFee = 0, childFee = 0, infantFee = 0 }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@구분', 값: 'U' },
        { 컬럼명: '@서비스번호', 값: Number(serviceNo) },
        { 컬럼명: '@서비스명', 값: String(name || '') },
        { 컬럼명: '@서비스영문명', 값: '' },
        { 컬럼명: '@적용방식', 값: String(chargeModeCode || '066001') },
        { 컬럼명: '@통화', 값: '058001' },
        { 컬럼명: '@상품번호', 값: Number(productNo) },
        { 컬럼명: '@설명', 값: String(desc || '') },
        { 컬럼명: '@사용시간', 값: 0 },
        { 컬럼명: '@사전예약불가', 값: false },
        { 컬럼명: '@성인요금', 값: Number(adultFee) || 0 },
        { 컬럼명: '@소아요금', 값: Number(childFee) || 0 },
        { 컬럼명: '@유아요금', 값: Number(infantFee) || 0 },
        { 컬럼명: '@이미지경로', 값: '' },
        { 컬럼명: '@대체일정설명', 값: '' },
        { 컬럼명: '@미선택시가이드동행', 값: '0' },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%84%A0%ED%83%9D%EC%98%B5%EC%85%98`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get product select options (상품 선택옵션 목록)
// SP: DBO.TRP_S_%EC%84%A0%ED%83%9D%EC%98%B5%EC%85%98
// payload Table1: @공급자번호, @상품번호
// Returns: array in data.table (fields may include 한글명, 설명, 이용요금 등)
export const getProductSelectOptions = async ({ productNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%84%A0%ED%83%9D%EC%98%B5%EC%85%98`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get reservation sale info (예약관리 - 판매정보)
// SP: DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EC%A0%95%EB%B3%B4
// payload Table1: @공급업체번호, @유저번호, @판매번호
// Returns: first row object in data.table or null
export const getReservationSaleInfo = async ({ saleNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list && list.length ? list[0] : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get tour meeting setting (투어 미팅장소 설정값 조회)
// SP: DBO.TRP_S_%ED%88%AC%EC%96%B4%EB%AF%B8%ED%8C%85%EC%9E%A5%EC%86%8C%EC%84%A4%EC%A0%95%EA%B0%92
// payload Table1: @상품번호, @일정타입번호
// Returns raw dataset; read data.table[0].투어미팅비고
export const getTourMeetingSetting = async ({ productNo, itineraryTypeNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%88%AC%EC%96%B4%EB%AF%B8%ED%8C%85%EC%9E%A5%EC%86%8C%EC%84%A4%EC%A0%95%EA%B0%92`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get supply contract list (공급계약 리스트)
// SP: DBO.TRP_S_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EB%A6%AC%EC%8A%A4%ED%8A%B8
// payload Table1: @업체번호, @상품번호
// Returns: array of supply contracts with 공급계약번호, 공급계약명
export const getSupplyContractList = async ({ productNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@상품번호', 값: String(productNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    
    // Parse the response - data.table[0].정보 contains JSON string
    const table = Array.isArray(data?.table) ? data.table : [];
    if (table.length > 0 && table[0].정보) {
      try {
        const contracts = JSON.parse(table[0].정보);
        return Array.isArray(contracts) ? contracts : [];
      } catch (e) {
        console.error('Failed to parse supply contract list:', e);
        return [];
      }
    }
    return [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Create supply contract (공급계약 추가)
// SP: DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EC%B6%94%EA%B0%80
// payload Table1: @업체번호, @상품번호, @공급계약명, @처리자
// Returns: created contract data
export const createSupplyContract = async ({ productNo, contractName }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@상품번호', 값: String(productNo) },
        { 컬럼명: '@공급계약명', 값: String(contractName) },
        { 컬럼명: '@처리자', 값: String(userNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EC%B6%94%EA%B0%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update supply contract (공급계약 편집)
// SP: DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EC%83%81%ED%92%88%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%ED%8E%B8%EC%A7%91
// payload Table1: @업체번호, @상품번호, @공급계약번호, @공급계약명, @처리자
// Returns: updated contract data
export const updateSupplyContract = async ({ productNo, contractNo, contractName }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@상품번호', 값: String(productNo) },
        { 컬럼명: '@공급계약번호', 값: Number(contractNo) },
        { 컬럼명: '@공급계약명', 값: String(contractName) },
        { 컬럼명: '@처리자', 값: String(userNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EC%83%81%ED%92%88%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%ED%8E%B8%EC%A7%91`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete supply contract (공급계약 삭제)
// SP: DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EC%82%AD%EC%A0%9C
// payload Table1: @업체번호, @상품번호, @공급계약번호, @처리자
// Returns: deletion result
export const deleteSupplyContract = async ({ productNo, contractNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@상품번호', 값: String(productNo) },
        { 컬럼명: '@공급계약번호', 값: Number(contractNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get Group Settlement Main list (행사단체 결산 메인)
// SP: DBO.WSP_S_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EA%B2%B0%EC%82%B0_%EB%A9%94%EC%9D%B8
// payload Table1: @공급업체번호, @시작일(YYYY-MM-DD), @종료일(YYYY-MM-DD), @검색구분('행사일' | '예약일' | ...), @수배담당 (or 'NULL')
// Returns: array in data.table
export const getGroupSettlementMain = async ({ startDate, endDate, searchBasis = '행사일', manager = 'NULL' } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    // default dates similar to other APIs
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const start = String(startDate || toYmd(today));
    const oneMonthLater = new Date(today);
    oneMonthLater.setMonth(today.getMonth() + 1);
    const end = String(endDate || toYmd(oneMonthLater));

    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@시작일', 값: start, 리스트값: null },
        { 컬럼명: '@종료일', 값: end, 리스트값: null },
        { 컬럼명: '@검색구분', 값: String(searchBasis), 리스트값: null },
        { 컬럼명: '@수배담당', 값: String(manager ?? 'NULL'), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EA%B2%B0%EC%82%B0_%EB%A9%94%EC%9D%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    // Standardize key: return data.table or []
    return Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get outstanding receivables summary (미수현황)
// SP: DBO.TRP_S_%EB%AF%B8%EC%88%98%ED%98%84%ED%99%A9
// payload Table1: @공급업체번호, @시작일(YYYY-MM-DD), @종료일(YYYY-MM-DD)
// Returns: array in data.table
export const getOutstandingReceivables = async ({ startDate, endDate } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const start = String(startDate || toYmd(today));
    const oneMonthLater = new Date(today);
    oneMonthLater.setMonth(today.getMonth() + 1);
    const end = String(endDate || toYmd(oneMonthLater));

    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@시작일', 값: start, 리스트값: null },
        { 컬럼명: '@종료일', 값: end, 리스트값: null },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EB%AF%B8%EC%88%98%ED%98%84%ED%99%A9`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete a tour product (상품 삭제 - 복구 불가)
// SP: DBO.TRP_T_%ED%88%AC%EC%96%B4_%EC%83%81%ED%92%88%EC%82%AD%EC%A0%9C
// payload Table1: @공급업체번호, @유저번호, @상품번호
export const deleteTourProduct = async ({ productNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo || ''), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo || ''), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%88%AC%EC%96%B4_%EC%83%81%ED%92%88%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete a pre-deposit entry by detail number (세부내역번호)
// SP: DBO.TRP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%EC%A0%84%EB%8F%84%EA%B8%88%EC%82%AD%EC%A0%9C
// payload Table1: @세부내역번호, @공급업체번호, @처리자
export const deletePreDeposit = async ({ detailNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@세부내역번호', 값: Number(detailNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%EC%A0%84%EB%8F%84%EA%B8%88%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create (add) a pre-deposit entry for a group
// SP: DBO.TRP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%EC%A0%84%EB%8F%84%EA%B8%88
// payload Table1: @행사단체번호, @전도금정보(JSON string), @공급업체번호, @처리자
export const createPreDeposit = async ({ groupNo, amount, remark = '' }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const amt = Number(amount) || 0;
    const info = {
      전도금번호: '0',
      수배번호: '',
      수배: '',
      공급거래처번호: '',
      공급거래처: '',
      가격방식: true,
      지급방법: '232001',
      은행: '',
      은행코드: '',
      기타은행: '',
      계좌번호: '',
      예금주: '',
      선택원가항목: [{ value: '233001' }],
      통화: 'KRW',
      통화코드: '058001',
      환율: 1,
      기준금액: String(amt),
      수량: 1,
      할인액: 0,
      합계액: 0,
      한화합계액: amt,
      비고: String(remark || ''),
      외환송금정보업체: '',
      외환송금정보은행: '',
      첨부파일리스트: [],
      선급금내역명: '',
      선급금내역번호: '',
    };
    const payload = {
      Table1: [
        { 컬럼명: '@행사단체번호', 값: Number(groupNo), 리스트값: null },
        { 컬럼명: '@전도금정보', 값: JSON.stringify(info), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%EC%A0%84%EB%8F%84%EA%B8%88`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    // Try to parse JSON regardless of HTTP status to extract server message
    let data = null;
    try {
      // clone() allows reading body even if not ok
      data = await response.clone().json();
    } catch (_) {
      data = null;
    }
    if (!response.ok) {
      const msg = (data && (data.table1?.[0]?.message || data.table?.[0]?.message || data.message))
        || `API error: ${response.status}`;
      throw new Error(String(msg));
    }
    // Some procedures may return a business message in table1 even on 200
    if (data && data.table1 && Array.isArray(data.table1) && data.table1[0]?.message) {
      throw new Error(String(data.table1[0].message));
    }
    // Fallback: if data wasn't parsed above (no JSON), parse now
    if (data == null) {
      data = await response.json();
    }
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// SP: DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%A0%84%EB%8F%84%EA%B8%88
// payload Table1: @판매번호
// Returns: { list: data.table || [], summary: data.table1?.[0] || null }
export const getPreDepositInfoBySaleNo = async ({ saleNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%A0%84%EB%8F%84%EA%B8%88`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const summary = Array.isArray(data?.table1) ? (data.table1[0] || null) : Array.isArray(data?.Table1) ? (data.Table1[0] || null) : null;
    return { list, summary };
  } catch (error) {
    return handleApiError(error);
  }
};

// Get order list by group number (단체번호 기준 주문 리스트)
// SP: DBO.RCT_S_%EC%83%81%ED%92%88%EC%98%88%EC%95%BD_%ED%8C%90%EB%A7%A4%EB%A6%AC%EC%8A%A4%ED%8A%B8_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EB%B2%88%ED%98%B8
// payload Table1: @행사단체번호, @취소여부1, @취소여부2
// Returns: array in data.table
export const getGroupOrderListByGroupNo = async ({ groupNo, cancel1 = '0', cancel2 = '0' }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@행사단체번호', 값: Number(groupNo), 리스트값: null },
        { 컬럼명: '@취소여부1', 값: String(cancel1), 리스트값: null },
        { 컬럼명: '@취소여부2', 값: String(cancel2), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%83%81%ED%92%88%EC%98%88%EC%95%BD_%ED%8C%90%EB%A7%A4%EB%A6%AC%EC%8A%A4%ED%8A%B8_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EB%B2%88%ED%98%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get cashbook list (출납 리스트)
// SP: DBO.WSP_S_%EC%B6%9C%EB%82%A9%EB%A6%AC%EC%8A%A4%ED%8A%B8_02
// payload Table1: @공급업체번호, @유저번호, @시작일, @종료일
// @시작일은 조회일(종료일)로부터 1년 전으로 자동 계산
// Returns: { list: data.table || [], mapping: data.table1 || [] }
export const getCashbookList = async ({ startDate, endDate } = {}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};

    // Determine query end date (조회일): use provided endDate or today
    const end = (() => {
      if (endDate) {
        const d = new Date(endDate);
        return Number.isNaN(d.getTime()) ? new Date() : d;
      }
      return new Date();
    })();

    // Determine start date: 1 year before end date
    const start = (() => {
      if (startDate) {
        const d = new Date(startDate);
        if (!Number.isNaN(d.getTime())) return d;
      }
      const s = new Date(end.getTime());
      s.setFullYear(s.getFullYear() - 1);
      return s;
    })();

    const fmtYmd = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@시작일', 값: fmtYmd(start), 리스트값: null },
        { 컬럼명: '@종료일', 값: fmtYmd(end), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%B6%9C%EB%82%A9%EB%A6%AC%EC%8A%A4%ED%8A%B8_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const mapping = Array.isArray(data?.table1) ? data.table1 : Array.isArray(data?.Table1) ? data.Table1 : [];
    return { list, mapping };
  } catch (error) {
    return handleApiError(error);
  }
};

export const submitPreDepositApproval = async ({ amount, detailNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const amt = Number(amount) || 0;
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@결재양식종류', 값: '230001', 리스트값: null },
        { 컬럼명: '@총금액', 값: String(amt), 리스트값: null },
        { 컬럼명: '@등록자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@선택리스트', 값: JSON.stringify([{ 세부내역번호: Number(detailNo) }]), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EA%B2%B0%EC%9E%AC%EB%AC%B8%EC%84%9C%EC%83%81%EC%8B%A0_%EC%9E%90%EB%8F%99%EC%83%81%EC%8B%A0`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const applyPreDepositWithdrawal = async ({ transactionNo, documentNo, preDepositNo, amount, exchangeRate = 1 }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const amt = Number(amount) || 0;
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@거래일련번호', 값: Number(transactionNo), 리스트값: null },
        { 컬럼명: '@문서번호', 값: Number(documentNo), 리스트값: null },
        { 컬럼명: '@전도금번호', 값: Number(preDepositNo), 리스트값: null },
        { 컬럼명: '@출금액', 값: String(amt), 리스트값: null },
        { 컬럼명: '@지급환율', 값: Number(exchangeRate) || 1, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%B6%9C%EA%B8%88%EC%A0%81%EC%9A%A9_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const deletePreDepositWithdrawalRegistration = async ({ preDepositNo, paymentHistoryNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@전도금번호', 값: Number(preDepositNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@공급자번호', 값: String(companyNo || ''), 리스트값: null },
        { 컬럼명: '@지불내역번호', 값: Number(paymentHistoryNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%B6%9C%EA%B8%88%EB%93%B1%EB%A1%9D%EC%82%AD%EC%A0%9C_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const cancelApprovalDocument = async ({ documentNo, opinion = '' }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@결재문서번호', 값: Number(documentNo), 리스트값: null },
        { 컬럼명: '@결재자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@결재의견', 값: String(opinion || ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EA%B2%B0%EC%9E%AC%EB%AC%B8%EC%84%9C_%EA%B2%B0%EC%9E%AC%EC%B7%A8%EC%86%8C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const cancelSubmissionDocument = async ({ documentNo, opinion = '' }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@결재문서번호', 값: Number(documentNo), 리스트값: null },
        { 컬럼명: '@결재자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@결재의견', 값: String(opinion || ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EA%B2%B0%EC%9E%AC%EB%AC%B8%EC%84%9C_%EC%83%81%EC%8B%A0%EC%B7%A8%EC%86%8C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get currency codes list (통화코드 목록)
// SP: DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C
// payload Table1: @종류='통화코드', @공급업체번호
// Returns: array in data.table
export const getCurrencyCodes = async () => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@종류', 값: '통화코드', 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get tour product list for copying (투어상품리스트 조회)
// SP: DBO.TRP_S_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88%EB%A6%AC%EC%8A%A4%ED%8A%B8
// Returns: array of products with 상품명, 도시, 상품종류 etc.
export const getTourProductListForCopy = async () => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo || ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

export const getBankReceiptProcessingRegisteredAmountInfo = async ({ type, transactionNo, transactionSeq } = {}) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@구분', 값: String(type || '입금'), 리스트값: null },
        { 컬럼명: '@거래번호', 값: transactionNo ?? null, 리스트값: null },
        { 컬럼명: '@거래일련번호', 값: transactionSeq ?? null, 리스트값: null },
      ],
    };

    const procedureName = encodeURIComponent('DBO.TRP_S_통장내역_입금처리등록액_01');
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${procedureName}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Copy a product for tour usage (상품 카피 - 투어 전용)
// SP: DBO.CW_T_%EC%83%81%ED%92%88%EC%B9%B4%ED%94%BC_%ED%88%AC%EC%96%B4%EC%A0%84%EC%9A%A9_01
// payload Table1: @상품종류명, @원본상품번호, @상품명, @유저번호, @소요시간, @소요시간타입('012001'|'012002'), @통화코드
// Note: @상품종류명 should be '기타' for quotes, '투어' for regular products
export const copyTourProduct = async ({ productTypeName = '기타', originalProductNo, productName, duration, durationType, currencyCode }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@상품종류명', 값: String(productTypeName) },
        { 컬럼명: '@원본상품번호', 값: Number(originalProductNo) },
        { 컬럼명: '@상품명', 값: String(productName || '') },
        { 컬럼명: '@유저번호', 값: String(userNo || '') },
        { 컬럼명: '@소요시간', 값: Number(duration) || 0 },
        { 컬럼명: '@소요시간타입', 값: String(durationType || '012001') },
        { 컬럼명: '@통화코드', 값: String(currencyCode || '058001') },
        { 컬럼명: '@복사옵션', 값: '3' },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%EC%83%81%ED%92%88%EC%B9%B4%ED%94%BC_%ED%88%AC%EC%96%B4%EC%A0%84%EC%9A%A9_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Resolve productId (상품번호) by saleNo using schedule-save SP
// SP: DBO.WSP_S_%ED%8C%90%EB%A7%A4%EB%B2%88%ED%98%B8_%EC%9D%BC%EC%A0%95%ED%91%9C_%EB%8B%A4%EB%A5%B8%EC%9D%B4%EB%A6%84%EC%9C%BC%EB%A1%9C%EC%A0%80%EC%9E%A5
// payload Table1: @판매번호
// Returns: first row with fields incl. 상품번호, 프리뷰도메인, ...
export const getProductIdBySaleNo = async ({ saleNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%ED%8C%90%EB%A7%A4%EB%B2%88%ED%98%B8_%EC%9D%BC%EC%A0%95%ED%91%9C_%EB%8B%A4%EB%A5%B8%EC%9D%B4%EB%A6%84%EC%9C%BC%EB%A1%9C%EC%A0%80%EC%9E%A5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows?.[0] || null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update cancel fee only (취소료 수정)
// SP: DBO.CW_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%B7%A8%EC%86%8C%EB%A3%8C%EC%88%98%EC%A0%95
// payload Table1: @공급업체번호, @처리자, @판매번호, @취소료
export const updateCancelFee = async ({ saleNo, cancelFee }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@취소료', 값: String(Number(cancelFee) || 0), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%B7%A8%EC%86%8C%EB%A3%8C%EC%88%98%EC%A0%95`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateAgencyCommission = async ({ saleNo, amount }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@금액', 값: String(Number(amount) || 0), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EB%8C%80%EB%A6%AC%EC%A0%90%EC%BB%A4%EB%AF%B8%EC%85%98%EC%88%98%EC%A0%95`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update orderer (주문자) information for an order
// SP: DBO.TRP_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%A3%BC%EB%AC%B8_%EC%A3%BC%EB%AC%B8%EC%9E%90%EC%A0%95%EB%B3%B4
// payload Table1: @공급업체번호, @주문번호, @주문자, @주문자휴대폰, @주문자이메일, @처리자
export const updateOrdererInfo = async ({ orderNo, ordererName, ordererPhone, ordererEmail }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@주문번호', 값: String(orderNo), 리스트값: null },
        { 컬럼명: '@주문자', 값: String(ordererName || ''), 리스트값: null },
        { 컬럼명: '@주문자휴대폰', 값: String(ordererPhone || ''), 리스트값: null },
        { 컬럼명: '@주문자이메일', 값: String(ordererEmail || ''), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%A3%BC%EB%AC%B8_%EC%A3%BC%EB%AC%B8%EC%9E%90%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateOrderAgency = async ({ orderNo, agencyNo, agencyName }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@주문번호', 값: String(orderNo), 리스트값: null },
        { 컬럼명: '@대리점명', 값: String(agencyName || ''), 리스트값: null },
        { 컬럼명: '@대리점번호', 값: Number(agencyNo), 리스트값: null },
        { 컬럼명: '@수정자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC%EB%8C%80%EB%A6%AC%EC%A0%90%EC%88%98%EC%A0%95`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update reservation sale status with explicit level codes
// SP: DBO.TRP_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EC%83%81%ED%83%9C
// Params: { saleNo, level1, level2='', level3='' }
export const updateSaleStatusAdvanced = async ({ saleNo, level1, level2 = '', level3 = '' }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@예약상태1', 값: String(level1 || ''), 리스트값: null },
        { 컬럼명: '@예약상태2', 값: String(level2 || ''), 리스트값: null },
        { 컬럼명: '@예약상태3', 값: String(level3 || ''), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EC%83%81%ED%83%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Restore a previously canceled reservation (예약 복원)
// SP: DBO.TRP_T_%EC%B7%A8%EC%86%8C%EC%83%81%ED%92%88%EC%98%88%EC%95%BD%EB%B3%B5%EC%9B%90
// payload Table1: @판매번호, @처리자
export const restoreReservation = async ({ saleNo }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%B7%A8%EC%86%8C%EC%83%81%ED%92%88%EC%98%88%EC%95%BD%EB%B3%B5%EC%9B%90`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get cancel-related info (예약상태 및 취소료 가져오기)
// SP: DBO.TRP_S_%EC%98%88%EC%95%BD%EC%83%81%ED%83%9C%EB%B0%8F%EC%B7%A8%EC%86%8C%EB%A3%8C%EA%B0%80%EC%A0%B8%EC%98%A4%EA%B8%B0
// payload Table1: @판매번호
// Returns: first row object in data.table or null
export const getCancelInfo = async ({ saleNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%98%88%EC%95%BD%EC%83%81%ED%83%9C%EB%B0%8F%EC%B7%A8%EC%86%8C%EB%A3%8C%EA%B0%80%EC%A0%B8%EC%98%A4%EA%B8%B0`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list?.[0] || null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Execute sale cancel with fee (판매 취소 저장)
// SP: DBO.TRP_T_%ED%8C%90%EB%A7%A4_%ED%8C%90%EB%A7%A4%EC%B7%A8%EC%86%8C
// payload Table1: @판매번호, @부과취소료, @처리자
export const cancelSale = async ({ saleNo, cancelFee }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@부과취소료', 값: Number(cancelFee) || 0, 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%8C%90%EB%A7%A4_%ED%8C%90%EB%A7%A4%EC%B7%A8%EC%86%8C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      let serverMsg = '';
      try {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const errJson = await response.json();
          const first = errJson?.table1?.[0] || errJson?.Table1?.[0] || errJson?.table?.[0] || errJson?.Table?.[0] || null;
          serverMsg = first?.message || first?.메시지 || errJson?.message || errJson?.메시지 || errJson?.MESSAGE || '';
          if (!serverMsg && errJson && typeof errJson === 'object') {
            serverMsg = JSON.stringify(errJson);
          }
        } else {
          const text = await response.text();
          serverMsg = text;
          try {
            const errJson = JSON.parse(text);
            const first = errJson?.table1?.[0] || errJson?.Table1?.[0] || errJson?.table?.[0] || errJson?.Table?.[0] || null;
            serverMsg = first?.message || first?.메시지 || errJson?.message || errJson?.메시지 || errJson?.MESSAGE || serverMsg;
          } catch (_) { }
        }
      } catch (_) { }
      const err = new Error(serverMsg || `API error: ${response.status}`);
      err.status = response.status;
      err.apiMessage = serverMsg;
      throw err;
    }
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get receipt detail by transaction number (수납리스트 상세: 거래번호 기반)
// SP: DBO.TRP_S_%EC%88%98%EB%82%A9%EB%A6%AC%EC%8A%A4%ED%8A%B8_%EA%B1%B0%EB%9E%98%EB%B2%88%ED%98%B8
// payload Table1: @거래번호, @판매번호
// Returns: first row from data.table or null
export const getReceiptDetailByTransactionNo = async ({ saleNo, transactionNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@거래번호', 값: String(transactionNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%88%98%EB%82%A9%EB%A6%AC%EC%8A%A4%ED%8A%B8_%EA%B1%B0%EB%9E%98%EB%B2%88%ED%98%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows?.[0] || null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Apply receipt amount into deposit list (수납입금적용)
// SP: DBO.TRP_T_%EC%88%98%EB%82%A9%EC%9E%85%EA%B8%88%EC%A0%81%EC%9A%A9
// payload Table1: @공급업체번호, @거래번호, @유저번호, @판매번호, @입금액2, @외화손익2, @사용통화, @비고, @현금영수증자진발급제외
export const applyReceiptToDeposit = async ({ saleNo, transactionNo, amount, note = '', currencyCode, cashReceiptSelfExclude = false }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@거래번호', 값: String(transactionNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@입금액2', 값: String(amount), 리스트값: null },
        { 컬럼명: '@외화손익2', 값: 0, 리스트값: null },
        { 컬럼명: '@사용통화', 값: String(currencyCode || '058001'), 리스트값: null },
        { 컬럼명: '@비고', 값: String(note || ''), 리스트값: null },
        { 컬럼명: '@현금영수증자진발급제외', 값: !!cashReceiptSelfExclude, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%88%98%EB%82%A9%EC%9E%85%EA%B8%88%EC%A0%81%EC%9A%A9`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};
// Delete an order payment (입금 삭제)
// SP: DBO.WSP_T_%EC%A3%BC%EB%AC%B8%EA%B2%B0%EC%A0%9C_%EC%82%AD%EC%A0%9C_01
// payload Table1: @공급자번호, @유저번호, @결제번호
export const deleteOrderPayment = async ({ paymentNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@결제번호', 값: Number(paymentNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%A3%BC%EB%AC%B8%EA%B2%B0%EC%A0%9C_%EC%82%AD%EC%A0%9C_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get sale deposit (입금) list for a sale
// SP: DBO.WSP_S_%ED%8C%90%EB%A7%A4_%EC%9E%85%EA%B8%88%ED%98%84%ED%99%A9
// payload Table1: @판매번호
// Returns: array in data.table
export const getSaleDepositList = async ({ saleNo }) => {
  try {
    const { userNo, companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%9E%85%EA%B8%88%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const summary = Array.isArray(data?.table1) ? data.table1[0] : null;
    return { list, summary };
  } catch (error) {
    return handleApiError(error);
  }
};

// Get receipt list (수납 리스트)
// SP: DBO.WSP_S_%EC%88%98%EB%82%A9%EB%A6%AC%EC%8A%A4%ED%8A%B8_01
// payload Table1: @공급업체번호, @유저번호, @판매번호, @시작일(YYYY-MM-DD), @종료일(YYYY-MM-DD)
// Returns: array from data.table (ignore table1 per spec)
export const getReceiptList01 = async ({ saleNo, startDate, endDate }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@시작일', 값: String(startDate), 리스트값: null },
        { 컬럼명: '@종료일', 값: String(endDate), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%88%98%EB%82%A9%EB%A6%AC%EC%8A%A4%ED%8A%B8_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    // Only use primary table per spec
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete receipt record (수납 내역 삭제)
// SP: DBO.CW_T_%EC%88%98%EB%82%A9%EB%82%B4%EC%97%AD%EC%82%AD%EC%A0%9C
// payload Table1: @거래일련번호, @공급업체번호, @유저번호
// transactionNo should be the numeric 거래일련번호 field from the receipt list
export const deleteReceiptRecord = async ({ transactionNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@거래일련번호', 값: Number(transactionNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%EC%88%98%EB%82%A9%EB%82%B4%EC%97%AD%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create new receipt record (수납 등록)
// SP: DBO.CW_T_%EC%88%98%EB%82%A9%EA%B4%80%EB%A6%AC
// payload Table1: @공급업체번호, @유저번호, @구분, @거래일련번호, @결제수단, @거래일시, @환율, @통화코드, @수납액, @거래금액, @계좌번호, @은행, @기타은행, @카드, @비고1, @비고2, @숨김여부
export const createReceiptRecord = async ({ transactionDate, receiptAmount, depositor, note }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@구분', 값: 'I', 리스트값: null },
        { 컬럼명: '@거래일련번호', 값: 'NULL', 리스트값: null },
        { 컬럼명: '@결제수단', 값: '254001', 리스트값: null },
        { 컬럼명: '@거래일시', 값: String(transactionDate), 리스트값: null },
        { 컬럼명: '@환율', 값: 1, 리스트값: null },
        { 컬럼명: '@통화코드', 값: '058001', 리스트값: null },
        { 컬럼명: '@수납액', 값: Number(receiptAmount), 리스트값: null },
        { 컬럼명: '@거래금액', 값: Number(receiptAmount), 리스트값: null },
        { 컬럼명: '@계좌번호', 값: '계좌번호모름', 리스트값: null },
        { 컬럼명: '@은행', 값: '256BCC', 리스트값: null },
        { 컬럼명: '@기타은행', 값: '', 리스트값: null },
        { 컬럼명: '@카드', 값: '', 리스트값: null },
        { 컬럼명: '@비고1', 값: String(depositor || ''), 리스트값: null },
        { 컬럼명: '@비고2', 값: String(note || ''), 리스트값: null },
        { 컬럼명: '@숨김여부', 값: false, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%EC%88%98%EB%82%A9%EA%B4%80%EB%A6%AC`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Toggle traveler list edit lock (명단편집 불가 여부 저장)
// SP: DBO.WSP_T_%ED%8C%90%EB%A7%A4_%EB%AA%85%EB%8B%A8%EB%9D%BD%EC%97%AC%EB%B6%80
// payload Table1: [ { @상태: bool }, { @판매번호: number } ]
export const toggleTravelerEditLock = async ({ saleNo, locked }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상태', 값: !!locked, 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%ED%8C%90%EB%A7%A4_%EB%AA%85%EB%8B%A8%EB%9D%BD%EC%97%AC%EB%B6%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save remaining travelers after deletion with minimal traveler JSON
// SP: DBO.CW_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%97%AC%ED%96%89%EA%B0%9D%EB%B3%80%EA%B2%BD
// Payload Table1 example:
// [ { @유저번호 }, { @판매번호 }, { @여행객_JSON: stringified array of { 여행객번호, 필드번호, 필드값, 연령 } }, { @성인수 }, { @나이배열 } ]
export const saveReservationTravelersAfterDelete = async ({ saleNo, minimalTravelerRows, adultCount = 0, ageArray = '' }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@여행객_JSON', 값: JSON.stringify(minimalTravelerRows || []), 리스트값: null },
        { 컬럼명: '@성인수', 값: Number(adultCount), 리스트값: null },
        { 컬럼명: '@나이배열', 값: String(ageArray || ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%97%AC%ED%96%89%EA%B0%9D%EB%B3%80%EA%B2%BD`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save reservation traveler changes (판매상태 및 여행객 수정 - 텍스 포함)
// SP: DBO.TRP_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EC%83%81%ED%83%9C%EB%B0%8F%EC%97%AC%ED%96%89%EA%B0%9D%EC%88%98%EC%A0%95_%ED%83%9D%EC%8A%A4%ED%8F%AC%ED%95%A8_14
// Params: { saleNo, travelerFieldRows, travelerAmounts, travelerOrders }
// - travelerFieldRows: array of { 여행객번호, 필드번호, 값 }
// - travelerAmounts: array of { 여행객번호, 상품금액, 공급단가, 택스및유류 }
// - travelerOrders: array of { 여행객번호, 여행객종류, 선발권동의, 명단확인, 순번 }
export const saveReservationTravelerChanges = async ({ saleNo, travelerFieldRows, travelerAmounts, travelerOrders }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@예약상태1', 값: '', 리스트값: null },
        { 컬럼명: '@예약상태2', 값: '', 리스트값: null },
        { 컬럼명: '@예약상태3', 값: '', 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@여행객_JSON', 값: JSON.stringify(travelerFieldRows || []), 리스트값: null },
        { 컬럼명: '@여행객상품금액_JSON', 값: JSON.stringify(travelerAmounts || []), 리스트값: null },
        { 컬럼명: '@여행객순번_JSON', 값: JSON.stringify(travelerOrders || []), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EC%83%81%ED%83%9C%EB%B0%8F%EC%97%AC%ED%96%89%EA%B0%9D%EC%88%98%EC%A0%95_%ED%83%9D%EC%8A%A4%ED%8F%AC%ED%95%A8_14`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get reservation traveler list by sale number (예약관리 - 여행객리스트)
// SP: DBO.CW_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%97%AC%ED%96%89%EA%B0%9D%EB%A6%AC%EC%8A%A4%ED%8A%B8
// payload Table1: @공급업체번호, @유저번호, @판매번호
// Returns: array in data.table (each item is a traveler)
export const getReservationTravelerListBySaleNo = async ({ saleNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%97%AC%ED%96%89%EA%B0%9D%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    // Normalize table position
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
  return list;
} catch (error) {
  return handleApiError(error);
}
};

// Save traveler passport copy path (여권사본경로) for a specific traveler
// SP: DBO.RCT_T_여권사본저장
// Params: { saleNo, travelerNo, filePath }
export const saveTravelerPassportCopy = async ({ saleNo, travelerNo, filePath }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@여행객번호', 값: Number(travelerNo), 리스트값: null },
        { 컬럼명: '@여권사본경로', 값: String(filePath || ''), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@변경자', 값: String(userNo), 리스트값: null },
      ],
    };

    const procedureName = encodeURIComponent('DBO.RCT_T_여권사본저장');
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${procedureName}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get reservation logs by sale number (예약기록 - 판매번호 기준)
// SP: DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B8%B0%EB%A1%9D_%ED%8C%90%EB%A7%A4%EB%B2%88%ED%98%B8
// payload Table1: @판매번호, @업체번호, @유저번호
// Returns: array from data.table or data.Table
export const getReservationLogsBySaleNo = async ({ saleNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B8%B0%EB%A1%9D_%ED%8C%90%EB%A7%A4%EB%B2%88%ED%98%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update reservation memo log (예약 메모 기록 수정)
// SP: DBO.TRP_T_%EC%98%88%EC%95%BD%EB%A9%94%EB%AA%A8%EA%B8%B0%EB%A1%9D%EC%88%98%EC%A0%95
// payload Table1: @참조번호, @업체번호, @유저번호, @메모, @판매업체조회가능, @수배업체조회가능
export const updateReservationMemoLog = async ({ referenceNo, memo, vendorViewable = true, dispatcherViewable = true }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@참조번호', 값: Number(referenceNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@메모', 값: String(memo ?? ''), 리스트값: null },
        { 컬럼명: '@판매업체조회가능', 값: Boolean(vendorViewable), 리스트값: null },
        { 컬럼명: '@수배업체조회가능', 값: Boolean(dispatcherViewable), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%98%88%EC%95%BD%EB%A9%94%EB%AA%A8%EA%B8%B0%EB%A1%9D%EC%88%98%EC%A0%95`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete reservation memo log (예약 메모 기록 삭제)
// SP: DBO.TRP_T_%EC%98%88%EC%95%BD%EB%A9%94%EB%AA%A8%EA%B8%B0%EB%A1%9D%EC%82%AD%EC%A0%9C
// payload Table1: @참조번호, @유저번호
export const deleteReservationMemoLog = async ({ referenceNo }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@참조번호', 값: Number(referenceNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%98%88%EC%95%BD%EB%A9%94%EB%AA%A8%EA%B8%B0%EB%A1%9D%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create reservation memo log (예약 메모 기록 등록)
// SP: DBO.TRP_T_%EC%98%88%EC%95%BD%EB%A9%94%EB%AA%A8%EA%B8%B0%EB%A1%9D
// payload Table1: @판매번호, @업체번호, @유저번호, @메모, @판매업체조회가능(true), @수배업체조회가능(true)
export const createReservationMemoLog = async ({ saleNo, memo, vendorViewable = true, dispatcherViewable = true }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@메모', 값: String(memo ?? ''), 리스트값: null },
        { 컬럼명: '@판매업체조회가능', 값: Boolean(vendorViewable), 리스트값: null },
        { 컬럼명: '@수배업체조회가능', 값: Boolean(dispatcherViewable), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%98%88%EC%95%BD%EB%A9%94%EB%AA%A8%EA%B8%B0%EB%A1%9D`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Generate customer direct input URL info (예약정보 입력요청 URL 생성)
// SP: DBO.CW_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EA%B3%A0%EA%B0%9D%EC%A7%81%EC%A0%91%EC%9E%85%EB%A0%A5URL
// payload Table1: @판매번호
// Returns: first row from data.table containing { 판매번호, 주문번호, 유니크키, 휴대폰번호 }
export const getReservationDirectInputUrl = async ({ saleNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EA%B3%A0%EA%B0%9D%EC%A7%81%EC%A0%91%EC%9E%85%EB%A0%A5URL`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows?.[0] || null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get order info for a single order number (예약 관리 - 주문정보)
// SP: DBO.TRP_S_%EC%A3%BC%EB%AC%B8%EC%A1%B0%ED%9A%8C_%EC%A3%BC%EB%AC%B8%EC%A0%95%EB%B3%B4
// payload Table1: @업체번호, @주문번호, @유저번호
// Returns: first row object in data.table or null
export const getOrderInfo = async ({ orderNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@주문번호', 값: Number(orderNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%A3%BC%EB%AC%B8%EC%A1%B0%ED%9A%8C_%EC%A3%BC%EB%AC%B8%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : [];
    return list && list.length ? list[0] : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Landbox: 간편일정 목록 가져오기
// SP: DBO.ITN_S_%EA%B0%84%ED%8E%B8%EC%9D%BC%EC%A0%95
// payload Table1: @업체번호, @즐겨찾기
// Returns: array in data.table (fields include 일정번호, 일정명, 상품가, 방문도시, ...)
export const getLandboxSimpleSchedules = async ({ favorite = 2 } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@즐겨찾기', 값: Number(favorite), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.ITN_S_%EA%B0%84%ED%8E%B8%EC%9D%BC%EC%A0%95`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list;
  } catch (error) {
    return handleApiError(error);
  }
};

// Landbox: 일정표 상세 HTML 가져오기
// SP: DBO.ITN_S_%EC%9D%BC%EC%A0%95%ED%91%9C
// payload Table1: @일정번호, @공유여부
// Returns: first row object (contains 내용 HTML)
export const getLandboxScheduleDetailHtml = async ({ scheduleNo, shared = 0 }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@일정번호', 값: Number(scheduleNo), 리스트값: null },
        { 컬럼명: '@공유여부', 값: String(shared), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.ITN_S_%EC%9D%BC%EC%A0%95%ED%91%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows?.[0] || null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save schedule confirmation flags for a group (일정확정 관련 저장)
// SP: DBO.WSP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%EC%9D%BC%EC%A0%95%ED%99%95%EC%A0%95%EC%97%AC%EB%B6%80_01
// payload Table1: @행사단체번호, @확정여부(bool), @확정확정표시여부(bool), @처리자(userNo)
export const saveGroupScheduleConfirm = async ({ groupNo, scheduleConfirm, scheduleConfirmDisplay }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@행사단체번호', 값: Number(groupNo), 리스트값: null },
        { 컬럼명: '@확정여부', 값: !!scheduleConfirm, 리스트값: null },
        { 컬럼명: '@확정확정표시여부', 값: !!scheduleConfirmDisplay, 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%EC%9D%BC%EC%A0%95%ED%99%95%EC%A0%95%EC%97%AC%EB%B6%80_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save departure confirmation flags for a group (출발확정 관련 저장)
// SP: DBO.WSP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%EC%B6%9C%EB%B0%9C%ED%99%95%EC%A0%95%EC%97%AC%EB%B6%80_01
// payload Table1: @행사단체번호, @출발확정여부(bool), @출발확정확정표시여부(bool), @처리자(userNo)
export const saveGroupDepartureConfirm = async ({ groupNo, departureConfirm, departureConfirmDisplay }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@행사단체번호', 값: Number(groupNo), 리스트값: null },
        { 컬럼명: '@출발확정여부', 값: !!departureConfirm, 리스트값: null },
        { 컬럼명: '@출발확정확정표시여부', 값: true, 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%EC%B6%9C%EB%B0%9C%ED%99%95%EC%A0%95%EC%97%AC%EB%B6%80_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update group settlement status (행사단체 마감상태 변경)
// SP: DBO.WSP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EB%A7%88%EA%B0%90
// payload: @업체번호, @처리자, @선택리스트(JSON array with 행사단체번호), @마감상태(0=미마감, 1=마감)
export const updateGroupSettlementStatus = async ({ groupNo, settlementStatus }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@선택리스트', 값: JSON.stringify([{ 행사단체번호: Number(groupNo) }]) },
        { 컬럼명: '@마감상태', 값: String(settlementStatus) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EB%A7%88%EA%B0%90`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    let data = null;
    try {
      data = await response.clone().json();
    } catch (_) {
      data = null;
    }
    if (!response.ok) {
      const msg = (data && (data.table1?.[0]?.message || data.table?.[0]?.message || data.message))
        || `API error: ${response.status}`;
      throw new Error(String(msg));
    }
    if (data && data.table1 && Array.isArray(data.table1) && data.table1[0]?.message) {
      throw new Error(String(data.table1[0].message));
    }
    if (data == null) {
      data = await response.json();
    }
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get group event information (단체정보 상세보기 - 행사단체정보)
// SP: DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EC%A0%95%EB%B3%B4
// payload Table1: @공급업체번호, @유저번호, @판매번호, @단체번호='NULL'
// Returns: first row object in data.table or null
export const getGroupEventInfo = async ({ saleNo, groupNo = null }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@단체번호', 값: groupNo == null ? 'NULL' : String(groupNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : [];
    return list && list.length ? list[0] : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get group profit/detail info for a sale (단체정보 상세보기 - 손익 정보)
// SP: DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EB%8B%A8%EC%B2%B4%EC%86%90%EC%9D%B5
// payload Table1: @공급업체번호, @유저번호, @판매번호
// Returns: first row object in data.table or null
export const getGroupProfitDetail = async ({ saleNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EB%8B%A8%EC%B2%B4%EC%86%90%EC%9D%B5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : [];
    return list && list.length ? list[0] : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// SP: DBO.TRP_S_예약관리_기타수익관리
// payload Table1: @판매번호
// Returns: list of other-income rows for the sale's event group
export const getOtherIncomeListBySaleNo = async ({ saleNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EA%B8%B0%ED%83%80%EC%88%98%EC%9D%B5%EA%B4%80%EB%A6%AC`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// SP: DBO.TRP_T_기타수익
// payload Table1: @기타수익번호('NULL'=insert), @행사단체번호, @항목, @담당자, @통화코드, @금액, @적용환율, @처리자, @비고, @세금계산서여부
export const saveOtherIncome = async ({
  otherIncomeNo = null,
  groupNo,
  item,
  manager = '',
  currencyCode = '058001',
  amount,
  exchangeRate = 1,
  remark = '',
  taxInvoice = false,
}) => {
  try {
    const { userNo } = getUserInfo() || {};
    // ERP GetDataSet expects the literal string 'NULL' for nullable FK insert (JS null often becomes 0 → silent UPDATE 0 rows)
    const otherIncomeNoValue = (otherIncomeNo == null || otherIncomeNo === '' || otherIncomeNo === 'NULL')
      ? 'NULL'
      : String(otherIncomeNo);
    const payload = {
      Table1: [
        { 컬럼명: '@기타수익번호', 값: otherIncomeNoValue, 리스트값: null },
        { 컬럼명: '@행사단체번호', 값: Number(groupNo), 리스트값: null },
        { 컬럼명: '@항목', 값: String(item || ''), 리스트값: null },
        { 컬럼명: '@담당자', 값: String(manager || ''), 리스트값: null },
        { 컬럼명: '@통화코드', 값: String(currencyCode || '058001'), 리스트값: null },
        { 컬럼명: '@금액', 값: Number(amount) || 0, 리스트값: null },
        { 컬럼명: '@적용환율', 값: Number(exchangeRate) || 1, 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo || ''), 리스트값: null },
        { 컬럼명: '@비고', 값: String(remark || ''), 리스트값: null },
        { 컬럼명: '@세금계산서여부', 값: !!taxInvoice, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EA%B8%B0%ED%83%80%EC%88%98%EC%9D%B5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    let data = null;
    try {
      data = await response.clone().json();
    } catch (_) {
      data = null;
    }
    if (!response.ok) {
      const msg = (data && (data.table1?.[0]?.message || data.table?.[0]?.message || data.message))
        || `API error: ${response.status}`;
      throw new Error(String(msg));
    }
    if (data && data.table1 && Array.isArray(data.table1) && data.table1[0]?.message) {
      throw new Error(String(data.table1[0].message));
    }
    if (data == null) {
      data = await response.json();
    }
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// SP: DBO.TRP_T_기타수익삭제
// payload Table1: @기타수익번호
export const deleteOtherIncome = async ({ otherIncomeNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@기타수익번호', 값: Number(otherIncomeNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EA%B8%B0%ED%83%80%EC%88%98%EC%9D%B5%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    let data = null;
    try {
      data = await response.clone().json();
    } catch (_) {
      data = null;
    }
    if (!response.ok) {
      const msg = (data && (data.table1?.[0]?.message || data.table?.[0]?.message || data.message))
        || `API error: ${response.status}`;
      throw new Error(String(msg));
    }
    if (data && data.table1 && Array.isArray(data.table1) && data.table1[0]?.message) {
      throw new Error(String(data.table1[0].message));
    }
    if (data == null) {
      data = await response.json();
    }
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Apply receipt amount to other income (기타수익 입금적용)
// SP: DBO.WSP_T_기타수익입금적용
// payload Table1: @공급업체번호, @거래번호, @유저번호, @기타수익번호, @입금액, @비고, @현금영수증자진발급제외
export const applyReceiptToOtherIncome = async ({
  otherIncomeNo,
  transactionNo,
  amount,
  note = '',
  cashReceiptSelfExclude = false,
}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@거래번호', 값: String(transactionNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@기타수익번호', 값: Number(otherIncomeNo), 리스트값: null },
        { 컬럼명: '@입금액', 값: Number(amount) || 0, 리스트값: null },
        { 컬럼명: '@비고', 값: String(note || ''), 리스트값: null },
        { 컬럼명: '@현금영수증자진발급제외', 값: !!cashReceiptSelfExclude, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EA%B8%B0%ED%83%80%EC%88%98%EC%9D%B5%EC%9E%85%EA%B8%88%EC%A0%81%EC%9A%A9`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    let data = null;
    try {
      data = await response.clone().json();
    } catch (_) {
      data = null;
    }
    if (!response.ok) {
      const msg = (data && (data.table1?.[0]?.message || data.table?.[0]?.message || data.message))
        || `API error: ${response.status}`;
      throw new Error(String(msg));
    }
    if (data && data.table1 && Array.isArray(data.table1) && data.table1[0]?.message) {
      throw new Error(String(data.table1[0].message));
    }
    if (data == null) {
      data = await response.json();
    }
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get deposit list applied to other income (기타수익 입금리스트)
// SP: DBO.WSP_S_입금리스트_기타수익
// payload Table1: @기타수익번호, @공급업체번호, @유저번호
// Returns: { list, summary }
export const getOtherIncomeDepositList = async ({ otherIncomeNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@기타수익번호', 값: Number(otherIncomeNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%9E%85%EA%B8%88%EB%A6%AC%EC%8A%A4%ED%8A%B8_%EA%B8%B0%ED%83%80%EC%88%98%EC%9D%B5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const summary = Array.isArray(data?.table1) ? data.table1[0] : (Array.isArray(data?.Table1) ? data.Table1[0] : null);
    return { list, summary };
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete other-income payment (기타수익 입금 삭제)
// SP: DBO.WSP_T_주문결제_기타수익_삭제
// payload Table1: @결제번호, @유저번호, @공급자번호
export const deleteOtherIncomePayment = async ({ paymentNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@결제번호', 값: Number(paymentNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@공급자번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%A3%BC%EB%AC%B8%EA%B2%B0%EC%A0%9C_%EA%B8%B0%ED%83%80%EC%88%98%EC%9D%B5_%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    let data = null;
    try {
      data = await response.clone().json();
    } catch (_) {
      data = null;
    }
    if (!response.ok) {
      const msg = (data && (data.table1?.[0]?.message || data.table?.[0]?.message || data.message))
        || `API error: ${response.status}`;
      throw new Error(String(msg));
    }
    if (data && data.table1 && Array.isArray(data.table1) && data.table1[0]?.message) {
      throw new Error(String(data.table1[0].message));
    }
    if (data == null) {
      data = await response.json();
    }
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Upload a file to S3 via backend endpoint
// Endpoint: POST /Upload/uploadS3
// Body (application/json): { File: base64String, FileName: string, FilePath: string }
export const uploadS3 = async ({ file, fileName, filePath }) => {
  try {
    if (!file) throw new Error('업로드할 파일이 없습니다.');
    const url = `${API_BASE_URL}/Upload/uploadS3`;
    // Convert to base64 (without data URL prefix)
    const toBase64 = (blob) => new Promise((resolve, reject) => {
      try {
        if (typeof blob === 'string') { resolve(blob); return; }
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const idx = result.indexOf(',');
          resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      } catch (e) { reject(e); }
    });
    const base64 = await toBase64(file);
    const payload = {
      File: base64,
      FileName: String(fileName || (file && file.name) || 'file'),
      FilePath: String(filePath || ''),
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`파일 업로드 실패: ${response.status}`);
    const data = await response.json();
    // Expected: { url: 'https://kr.object.iwinv.kr/orgin/1/EventGroup/72371/filename.ext' }
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const uploadS3Ocr = async ({ file, fileName, filePath, key, isUpload = false }) => {
  try {
    if (!file) throw new Error('업로드할 파일이 없습니다.');
    const url = `${API_BASE_URL}/Upload/UploadFileEncryptBytesS3_Ocr2`;
    const toBase64 = (blob) => new Promise((resolve, reject) => {
      try {
        if (typeof blob === 'string') { resolve(blob); return; }
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const idx = result.indexOf(',');
          resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      } catch (e) { reject(e); }
    });
    const base64 = await toBase64(file);
    const payload = {
      File: base64,
      FileName: String(fileName || (file && file.name) || 'file'),
      FilePath: String(filePath || ''),
      IsUpload: !!isUpload,
      Key: key == null ? '' : String(key),
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`OCR 처리 실패: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get reservation price info (예약관리 - 가격정보)
// SP: DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EA%B0%80%EA%B2%A9%EC%A0%95%EB%B3%B4
// payload Table1: @공급업체번호, @유저번호, @판매번호
// Returns: first row object in data.table or null
export const getReservationPriceInfo = async ({ saleNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EA%B0%80%EA%B2%A9%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : [];
    return list && list.length ? list[0] : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get reservation extra services (예약관리 - 추가서비스)
// SP: DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%B6%94%EA%B0%80%EC%84%9C%EB%B9%84%EC%8A%A4
// payload Table1: @판매번호
// Returns: array in data.table
export const getReservationExtraServices = async ({ saleNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%EC%B6%94%EA%B0%80%EC%84%9C%EB%B9%84%EC%8A%A4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    if (Array.isArray(data?.table)) return data.table;
    if (Array.isArray(data?.Table)) return data.Table;
    if (Array.isArray(data?.table1)) return data.table1;
    if (Array.isArray(data?.Table1)) return data.Table1;
    return [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Save group information including attachment fields
// SP: DBO.TRP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EC%A0%95%EB%B3%B4
// Required values example in payload Table1:
// @유저번호, @행사단체번호, @행사단체명, @수배담당번호, @행사시작일, @행사종료일, @첨부파일명, @첨부파일경로, @비고, @전도금마감, @신규예약자동조인마감, @랜드사번호
export const saveGroupInfoAttachment = async ({
  groupNo,
  groupName,
  managerNo,
  startDate,
  endDate,
  attachFileName,
  attachFilePath,
  remark = '',
  preDepositClose = false,
  autoJoinClose = '',
  landCompanyNo = '',
}) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@행사단체번호', 값: Number(groupNo), 리스트값: null },
        { 컬럼명: '@행사단체명', 값: String(groupName || ''), 리스트값: null },
        { 컬럼명: '@수배담당번호', 값: managerNo == null || managerNo === '' ? '' : Number(managerNo), 리스트값: null },
        { 컬럼명: '@행사시작일', 값: String(startDate || ''), 리스트값: null },
        { 컬럼명: '@행사종료일', 값: String(endDate || ''), 리스트값: null },
        { 컬럼명: '@첨부파일명', 값: String(attachFileName || ''), 리스트값: null },
        { 컬럼명: '@첨부파일경로', 값: String(attachFilePath || ''), 리스트값: null },
        { 컬럼명: '@비고', 값: String(remark || ''), 리스트값: null },
        { 컬럼명: '@전도금마감', 값: !!preDepositClose, 리스트값: null },
        { 컬럼명: '@신규예약자동조인마감', 값: String(autoJoinClose || ''), 리스트값: null },
        { 컬럼명: '@랜드사번호', 값: String(landCompanyNo || ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get reservation sale status for an order (예약관리 - 주문별 판매상태/취소여부)
// SP: DBO.TRP_S_%EC%A3%BC%EB%AC%B8%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EB%A6%AC%EC%8A%A4%ED%8A%B8
// payload Table1: @업체번호, @주문번호
// Returns: first row object in data.table or null
export const getOrderSaleStatus = async ({ orderNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@주문번호', 값: Number(orderNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%A3%BC%EB%AC%B8%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list && list.length ? list[0] : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update reservation sale status (예약상태 변경)
// SP: DBO.TRP_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EC%83%81%ED%83%9C
// Params: { saleNo, level2: '', '049011', or '049031' }
// - 예약접수: level2 = ''
// - 예약확정: level2 = '049011'
// - 예약불가: level2 = '049031'
// Always send: @예약상태1='049001', @예약상태3=''
export const updateSaleStatus = async ({ saleNo, level2 }) => {
  try {
    const { userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@예약상태1', 값: '049001', 리스트값: null },
        { 컬럼명: '@예약상태2', 값: String(level2 || ''), 리스트값: null },
        { 컬럼명: '@예약상태3', 값: '', 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%98%88%EC%95%BD%EA%B4%80%EB%A6%AC_%ED%8C%90%EB%A7%A4%EC%83%81%ED%83%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get reservation traveler list (예약관리 화면 - 여행객 리스트)
// SP: DBO.WSP_S_%EC%98%88%EC%95%BD%ED%98%84%ED%99%A9_%EC%97%AC%ED%96%89%EA%B0%9D%EB%A6%AC%EC%8A%A4%ED%8A%B8
// Params:
// - searchBasis: '1' 예약일 기준, '2' 행사일 기준 (default '2')
// - startDate, endDate: YYYY-MM-DD. If omitted, apply defaults:
//   예약일 기준(1): 시작일=today, 종료일=one month ago
//   행사일 기준(2): 시작일=today, 종료일=one month later
// - deletion/cancel flags fixed as per spec
// Returns: array in data.table
export const getReservationTravelerList = async ({ searchBasis = '2', startDate, endDate } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    let start;
    let end;
    if (startDate && endDate) {
      start = String(startDate);
      end = String(endDate);
    } else {
      if (String(searchBasis) === '1') {
        // 예약일 기준: 시작일=한달전, 종료일=오늘
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 1);
        start = toYmd(oneMonthAgo);
        end = toYmd(today);
      } else {
        // 행사일 기준: 시작일=오늘, 종료일=한달후
        const oneMonthLater = new Date(today);
        oneMonthLater.setMonth(today.getMonth() + 1);
        start = toYmd(today);
        end = toYmd(oneMonthLater);
      }
    }

    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@검색기준', 값: String(searchBasis), 리스트값: null },
        { 컬럼명: '@시작일', 값: start, 리스트값: null },
        { 컬럼명: '@종료일', 값: end, 리스트값: null },
        { 컬럼명: '@삭제여부1', 값: false, 리스트값: null },
        { 컬럼명: '@삭제여부2', 값: false, 리스트값: null },
        { 컬럼명: '@취소여부1', 값: '0', 리스트값: null },
        { 컬럼명: '@취소여부2', 값: '1', 리스트값: null },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%98%88%EC%95%BD%ED%98%84%ED%99%A9_%EC%97%AC%ED%96%89%EA%B0%9D%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// payload Table1 required:
// @공급자번호, @검색기준='2', @시작일(YYYY-MM-DD), @종료일(YYYY-MM-DD), @삭제여부1=false, @삭제여부2=false, @취소여부1='0', @취소여부2='1', @상품번호
// Returns: array in data.table
export const getOrderStatusListByProductNo = async ({ startDate, endDate, searchBasis = '2', productNo } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    // compute default dates if not provided
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const start = String(startDate || toYmd(today));
    const oneYearLater = new Date(today);
    oneYearLater.setFullYear(today.getFullYear() + 1);
    const end = String(endDate || toYmd(oneYearLater));

    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@검색기준', 값: String(searchBasis), 리스트값: null },
        { 컬럼명: '@시작일', 값: start, 리스트값: null },
        { 컬럼명: '@종료일', 값: end, 리스트값: null },
        { 컬럼명: '@삭제여부1', 값: false, 리스트값: null },
        { 컬럼명: '@삭제여부2', 값: false, 리스트값: null },
        { 컬럼명: '@취소여부1', 값: '0', 리스트값: null },
        { 컬럼명: '@취소여부2', 값: '1', 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%98%88%EC%95%BD%ED%98%84%ED%99%A9_02_%EC%83%81%ED%92%88%EB%B2%88%ED%98%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// payload Table1 required:
// @공급자번호, @검색기준='2', @시작일(YYYY-MM-DD), @종료일(YYYY-MM-DD), @삭제여부1=false, @삭제여부2=false, @취소여부1='0', @취소여부2='1'
// Returns: array in data.table
// TRPLITE 주문리스트 (주문일 기준)
// SP: DBO.TRPLITE_S_주문리스트_주문일
// payload Table1: @업체번호, @시작일, @종료일, @상태, @유저번호
const fetchTrpLiteOrderList = async ({ procedureSuffix, startDate, endDate, status = '1' } = {}) => {
  const { companyNo, userNo } = getUserInfo() || {};
  const today = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const start = String(startDate || toYmd(today));
  const oneMonthLater = new Date(today);
  oneMonthLater.setMonth(today.getMonth() + 1);
  const end = String(endDate || toYmd(oneMonthLater));

  const payload = {
    Table1: [
      { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
      { 컬럼명: '@시작일', 값: start, 리스트값: null },
      { 컬럼명: '@종료일', 값: end, 리스트값: null },
      { 컬럼명: '@상태', 값: String(status), 리스트값: null },
      { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
    ],
  };
  const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRPLITE_S_%EC%A3%BC%EB%AC%B8%EB%A6%AC%EC%8A%A4%ED%8A%B8_${procedureSuffix}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`API error: ${response.status}`);
  const data = await response.json();
  return Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
};

// TRPLITE 주문리스트 (주문일/예약일 기준)
// SP: DBO.TRPLITE_S_주문리스트_주문일
export const getTrpLiteOrderListByOrderDate = async ({ startDate, endDate, status = '1' } = {}) => {
  try {
    return await fetchTrpLiteOrderList({
      procedureSuffix: '%EC%A3%BC%EB%AC%B8%EC%9D%BC',
      startDate,
      endDate,
      status,
    });
  } catch (error) {
    return handleApiError(error);
  }
};

// TRPLITE 주문리스트 (출발일 기준)
// SP: DBO.TRPLITE_S_주문리스트_출발일
export const getTrpLiteOrderListByDepartureDate = async ({ startDate, endDate, status = '1' } = {}) => {
  try {
    return await fetchTrpLiteOrderList({
      procedureSuffix: '%EC%B6%9C%EB%B0%9C%EC%9D%BC',
      startDate,
      endDate,
      status,
    });
  } catch (error) {
    return handleApiError(error);
  }
};

export const getOrderStatusList = async ({ startDate, endDate, searchBasis = '2' } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    // compute default dates if not provided
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const start = String(startDate || toYmd(today));
    const oneYearLater = new Date(today);
    oneYearLater.setFullYear(today.getFullYear() + 1);
    const end = String(endDate || toYmd(oneYearLater));

    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@검색기준', 값: String(searchBasis), 리스트값: null },
        { 컬럼명: '@시작일', 값: start, 리스트값: null },
        { 컬럼명: '@종료일', 값: end, 리스트값: null },
        { 컬럼명: '@삭제여부1', 값: false, 리스트값: null },
        { 컬럼명: '@삭제여부2', 값: false, 리스트값: null },
        { 컬럼명: '@취소여부1', 값: '0', 리스트값: null },
        { 컬럼명: '@취소여부2', 값: '1', 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%98%88%EC%95%BD%ED%98%84%ED%99%A9_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

export const saveSaleMemo = async ({ saleNo, memo } = {}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
        { 컬럼명: '@판매비고', 값: String(memo ?? ''), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%8C%90%EB%A7%A4%EB%B9%84%EA%B3%A0%EC%A0%80%EC%9E%A5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update close status (마감여부) for selected events from calendar
// SP: DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%84%B8%EC%85%98%EA%B4%80%EB%A6%AC_%EC%84%A0%ED%83%9DDATA%EB%A7%88%EA%B0%90%EC%84%A4%EC%A0%95_03
// Params: { productNo, companyNo, userNo, closed, events: Array<{ 날짜:string, 시간:string, 자원번호:number }>, calendarUse=true, seriesApply=true }
export const saveTourSessionCloseSetting = async ({ productNo, companyNo, userNo, closed, events, calendarUse = true, seriesApply = true }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        // Spec indicates '@시리즈적용여부' should be fixed to '1'
        { 컬럼명: '@시리즈적용여부', 값: seriesApply ? '1' : '0', 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@마감여부', 값: !!closed, 리스트값: null },
        { 컬럼명: '@선택행사', 값: JSON.stringify(events || []), 리스트값: null },
        { 컬럼명: '@캘린더용', 값: !!calendarUse, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%84%B8%EC%85%98%EA%B4%80%EB%A6%AC_%EC%84%A0%ED%83%9DDATA%EB%A7%88%EA%B0%90%EC%84%A4%EC%A0%95_03`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get tour product resources (항공패턴/자원 목록)
// SP: DBO.RCT_S_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EB%A6%AC%EC%8A%A4%ED%8A%B8
// payload Table1: @업체번호, @상품번호
export const getTourProductResourceList = async ({ companyNo, productNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    // Normalize possible table keys
    const candidates = [data?.table, data?.table1, data?.Table, data?.Table1, data?.Table0, data?.empt0];
    for (const c of candidates) {
      if (Array.isArray(c) && c.length) return c;
    }
    // Fall back to empty array if nothing matches
    return Array.isArray(data?.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get tour rate plan list for a resource (요금제/가격옵션 목록)
// SP: DBO.RCT_S_%ED%88%AC%EC%96%B4%EC%9A%94%EA%B8%88%EC%A0%9C%EB%A6%AC%EC%8A%A4%ED%8A%B8_02
// payload Table1: @업체번호, @상품번호, @자원번호
export const getTourRatePlanList02 = async ({ companyNo, productNo, resourceNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@자원번호', 값: Number(resourceNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%ED%88%AC%EC%96%B4%EC%9A%94%EA%B8%88%EC%A0%9C%EB%A6%AC%EC%8A%A4%ED%8A%B8_02`;
    // Simple retry for transient failures (e.g., network hiccups or cold starts)
    const attempt = async () => {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      return response.json();
    };
    let data = null;
    let lastErr = null;
    for (let i = 0; i < 3; i++) {
      try {
        data = await attempt();
        break;
      } catch (e) {
        lastErr = e;
        try { console.warn('getTourRatePlanList02 retry', i + 1, { productNo, resourceNo }); } catch (_) {}
        // backoff: 150ms, 300ms
        await new Promise(r => setTimeout(r, 150 * (i + 1)));
      }
    }
    if (!data) throw lastErr || new Error('Unknown error fetching rate plan list');
    // Normalize possible table keys
    const candidates = [data?.table, data?.table1, data?.Table, data?.Table1, data?.Table0, data?.empt0];
    for (const c of candidates) {
      if (Array.isArray(c) && c.length) return c;
    }
    return Array.isArray(data?.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get product types for tours by supplier
// SP: DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C
// payload Table1: @종류='투어상품타입', @공급업체번호='{companyNo}'
export const getSupplierProductTypes = async ({ companyNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@종류', 값: '투어상품타입', 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get currency codes by supplier
// SP: DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C
// payload Table1: @종류='통화코드', @공급업체번호='{companyNo}'
export const getSupplierCurrencies = async ({ companyNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@종류', 값: '통화코드', 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Add a new tour product
// SP: DBO.CW_T_%ED%88%AC%EC%96%B4_%EC%83%81%ED%92%88%EC%B6%94%EA%B0%80
// payload Table1 fields: @구분='I', @공급자번호, @상품번호='NULL', @상품명, @처리자, @도시번호, @공급거래처번호, @소요시간, @소요시간타입(일:012001, 시간:012002), @통화코드, @상품타입
export const addTourProduct = async ({
  supplierNo,
  userNo,
  companyNo,
  productName,
  cityNo,
  durationValue,
  durationUnitCode, // '012001' or '012002'
  currencyCode, // e.g., '058001'
  productTypeCode, // e.g., '309200'
}) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@구분', 값: 'I' },
        { 컬럼명: '@공급자번호', 값: String(supplierNo) },
        { 컬럼명: '@상품번호', 값: 'NULL' },
        { 컬럼명: '@상품명', 값: String(productName) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@도시번호', 값: String(cityNo) },
        { 컬럼명: '@공급거래처번호', 값: String(companyNo) },
        { 컬럼명: '@소요시간', 값: String(durationValue) },
        { 컬럼명: '@소요시간타입', 값: String(durationUnitCode) },
        { 컬럼명: '@통화코드', 값: String(currencyCode) },
        { 컬럼명: '@상품타입', 값: String(productTypeCode) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_T_%ED%88%AC%EC%96%B4_%EC%83%81%ED%92%88%EC%B6%94%EA%B0%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      let serverMsg = '';
      try {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const errJson = await response.json();
          serverMsg = errJson?.메시지 || errJson?.message || errJson?.MESSAGE || errJson?.오류 || errJson?.error || '';
          if (!serverMsg && errJson && typeof errJson === 'object') {
            const first = errJson?.table?.[0] || errJson?.table1?.[0] || errJson?.Table?.[0] || errJson?.Table1?.[0] || errJson?.result?.[0] || null;
            if (first && typeof first === 'object') {
              serverMsg = first?.메시지 || first?.message || first?.MESSAGE || first?.오류 || first?.error || '';
            }
            if (!serverMsg) {
              serverMsg = JSON.stringify(errJson);
            }
          }
        } else {
          serverMsg = await response.text();
        }
      } catch (_) {}
      const err = new Error(serverMsg || `HTTP ${response.status}`);
      err.status = response.status;
      err.apiMessage = serverMsg;
      throw err;
    }
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// SP: DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%84%B8%EC%85%98%EA%B4%80%EB%A6%AC_%EC%84%A0%ED%83%9DDATA%EC%9E%AC%EA%B3%A0%EC%88%98%EC%A0%95_01
// Params: { productNo, companyNo, userNo, stockCount, events: Array<{ 날짜:string, 시간:string, 자원번호:number }>, calendarUse=true, seriesUpdate=false }
export const saveTourSelectedStock = async ({ productNo, companyNo, userNo, stockCount, events, calendarUse = true, seriesUpdate = false }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@시리즈재고업데이트', 값: !!seriesUpdate, 리스트값: null },
        { 컬럼명: '@재고수', 값: String(stockCount), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@선택행사', 값: JSON.stringify(events || []), 리스트값: null },
        { 컬럼명: '@캘린더용', 값: !!calendarUse, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%88%AC%EC%96%B4%EC%84%B8%EC%85%98%EA%B4%80%EB%A6%AC_%EC%84%A0%ED%83%9DDATA%EC%9E%AC%EA%B3%A0%EC%88%98%EC%A0%95_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save tour price info (@가격정보 with only changed fields)
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EA%B0%80%EA%B2%A9_%ED%88%AC%EC%96%B4_03
// payload Table1: @가격정보 (stringified JSON), @처리자 (userNo), @공급업체번호 (companyNo), @합산가격여부 (bool)
export const saveTourPriceInfo = async ({ priceInfoJson, userNo, companyNo, 합산가격여부 = false }) => {
  try {
    // Normalize @가격정보 payload: force Multi Day Tour (085002)
    // priceInfoJson is expected to be a stringified JSON object.
    let normalizedPriceInfoJson = String(priceInfoJson || '');
    try {
      const obj = JSON.parse(normalizedPriceInfoJson);
      if (obj && typeof obj === 'object') {
        obj.상품일정타입 = 'Multi Day Tour';
        obj.상품일정타입코드 = '085002';
        normalizedPriceInfoJson = JSON.stringify(obj);
      }
    } catch (_) {
      // If parsing fails, fall back to original string to avoid blocking saves.
    }

    const payload = {
      Table1: [
        { 컬럼명: '@가격정보', 값: normalizedPriceInfoJson, 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@합산가격여부', 값: !!합산가격여부, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EA%B0%80%EA%B2%A9_%ED%88%AC%EC%96%B4_03`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// payload Table1: @업체번호, @상품번호, @가격옵션번호, @처리자, @기본, @판매조건, @쇼핑
export const updateTourRatePlan02 = async ({ companyNo, productNo, priceOptionNo, userNo, 기본, 판매조건, 쇼핑 = '[]' }) => {
  try {
    const defaultSellingConditions = "[{\"코드번호\":\"385002\",\"코드명\":\"숙박등급\",\"사용여부\":false,\"사용시조건값코드\":\"387999\",\"미사용시디폴트코드\":\"387999\",\"사용시조건값1\":null,\"사용시조건값2\":null,\"옵션\":[{\"코드번호\":\"387001\",\"코드명\":\"불포함\",\"유무\":false,\"값\":\"387999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"387002\",\"코드명\":\"3성급\",\"유무\":false,\"값\":\"387999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"387003\",\"코드명\":\"4성급\",\"유무\":false,\"값\":\"387999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"387004\",\"코드명\":\"5성급\",\"유무\":false,\"값\":\"387999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"387005\",\"코드명\":\"6성급 이상\",\"유무\":false,\"값\":\"387999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"387999\",\"코드명\":\"미정\",\"유무\":false,\"값\":\"387999\",\"값1\":\"\",\"값2\":\"\"}]},{\"코드번호\":\"385003\",\"코드명\":\"숙박지식사\",\"사용여부\":false,\"사용시조건값코드\":\"388999\",\"미사용시디폴트코드\":\"388999\",\"사용시조건값1\":null,\"사용시조건값2\":null,\"옵션\":[{\"코드번호\":\"388001\",\"코드명\":\"불포함\",\"유무\":false,\"값\":\"388999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"388002\",\"코드명\":\"조식포함\",\"유무\":false,\"값\":\"388999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"388003\",\"코드명\":\"하프보드\",\"유무\":false,\"값\":\"388999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"388004\",\"코드명\":\"풀보드\",\"유무\":false,\"값\":\"388999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"388999\",\"코드명\":\"미정\",\"유무\":false,\"값\":\"388999\",\"값1\":\"\",\"값2\":\"\"}]},{\"코드번호\":\"385004\",\"코드명\":\"항공좌석등급\",\"사용여부\":false,\"사용시조건값코드\":\"389002\",\"미사용시디폴트코드\":\"389002\",\"사용시조건값1\":null,\"사용시조건값2\":null,\"옵션\":[{\"코드번호\":\"389001\",\"코드명\":\"불포함\",\"유무\":false,\"값\":\"389002\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"389002\",\"코드명\":\"이코노미\",\"유무\":false,\"값\":\"389002\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"389003\",\"코드명\":\"프리미엄이코노미\",\"유무\":false,\"값\":\"389002\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"389004\",\"코드명\":\"비즈니스\",\"유무\":false,\"값\":\"389002\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"389005\",\"코드명\":\"퍼스트\",\"유무\":false,\"값\":\"389002\",\"값1\":\"\",\"값2\":\"\"}]},{\"코드번호\":\"385005\",\"코드명\":\"마일리지적립\",\"사용여부\":false,\"사용시조건값코드\":\"390001\",\"미사용시디폴트코드\":\"390001\",\"사용시조건값1\":null,\"사용시조건값2\":null,\"옵션\":[{\"코드번호\":\"390001\",\"코드명\":\"적립불가\",\"유무\":false,\"값\":\"390001\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"390002\",\"코드명\":\"적립가능\",\"유무\":false,\"값\":\"390001\",\"값1\":\"\",\"값2\":\"\"}]},{\"코드번호\":\"385006\",\"코드명\":\"여행자보험\",\"사용여부\":false,\"사용시조건값코드\":\"391002\",\"미사용시디폴트코드\":\"391002\",\"사용시조건값1\":null,\"사용시조건값2\":null,\"옵션\":[{\"코드번호\":\"391001\",\"코드명\":\"불포함\",\"유무\":false,\"값\":\"391002\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"391002\",\"코드명\":\"포함\",\"유무\":false,\"값\":\"391002\",\"값1\":\"\",\"값2\":\"\"}]},{\"코드번호\":\"385007\",\"코드명\":\"여행지비자\",\"사용여부\":false,\"사용시조건값코드\":\"392001\",\"미사용시디폴트코드\":\"392001\",\"사용시조건값1\":null,\"사용시조건값2\":null,\"옵션\":[{\"코드번호\":\"392001\",\"코드명\":\"불필요\",\"유무\":false,\"값\":\"392001\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"392002\",\"코드명\":\"포함\",\"유무\":false,\"값\":\"392001\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"392003\",\"코드명\":\"불포함\",\"유무\":false,\"값\":\"392001\",\"값1\":\"\",\"값2\":\"\"}]},{\"코드번호\":\"385008\",\"코드명\":\"가이드/기사팁\",\"사용여부\":false,\"사용시조건값코드\":\"393002\",\"미사용시디폴트코드\":\"393002\",\"사용시조건값1\":null,\"사용시조건값2\":null,\"옵션\":[{\"코드번호\":\"393001\",\"코드명\":\"노팁\",\"유무\":false,\"값\":\"393002\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"393002\",\"코드명\":\"불포함\",\"유무\":false,\"값\":\"393002\",\"값1\":\"\",\"값2\":\"\"}]},{\"코드번호\":\"385009\",\"코드명\":\"쇼핑정보\",\"사용여부\":false,\"사용시조건값코드\":\"394999\",\"미사용시디폴트코드\":\"394999\",\"사용시조건값1\":null,\"사용시조건값2\":null,\"옵션\":[{\"코드번호\":\"394001\",\"코드명\":\"노쇼핑\",\"유무\":false,\"값\":\"394999\",\"값1\":\"\",\"값2\":\"\"},{\"코드번호\":\"394999\",\"코드명\":\"쇼핑방문있음\",\"유무\":false,\"값\":\"394999\",\"값1\":\"\",\"값2\":\"\"}]}]";
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@가격옵션번호', 값: Number(priceOptionNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@기본', 값: JSON.stringify(기본 || {}), 리스트값: null },
        { 컬럼명: '@판매조건', 값: String(판매조건 || defaultSellingConditions), 리스트값: null },
        { 컬럼명: '@쇼핑', 값: String(쇼핑 == null ? '[]' : 쇼핑), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%ED%88%AC%EC%96%B4%EC%9A%94%EA%B8%88%EC%A0%9C_%EC%88%98%EC%A0%95_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Bulk update sale prices by selected price record IDs (가격번호 일괄 업데이트)
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EA%B0%80%EA%B2%A9_%ED%8C%90%EB%A7%A4%EA%B0%80_%EC%9D%BC%EA%B4%84
// payload Table1: @상품번호, @공급업체번호, @처리자, @선택가격번호리스트 (stringified JSON array)
// The list entries format: [{ 가격번호: string|number, 성인: string|number, 어린이: string|number, 영유아: string|number }, ...]
export const saveSalePriceBulkByPriceNos = async ({ productNo, companyNo, userNo, selected }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@선택가격번호리스트', 값: JSON.stringify(Array.isArray(selected) ? selected : []), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EA%B0%80%EA%B2%A9_%ED%8C%90%EB%A7%A4%EA%B0%80_%EC%9D%BC%EA%B4%84`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const savePriceNameBulkByPriceNos = async ({ productNo, companyNo, userNo, selected, priceName }) => {
  try {
    const list = (Array.isArray(selected) ? selected : [])
      .map((x) => {
        if (x == null) return null;
        if (typeof x === 'object') return x;
        const n = Number(x);
        return Number.isFinite(n) ? { 가격번호: n } : null;
      })
      .filter(Boolean);

    const payload = {
      Table1: [
        { 컬럼명: '@선택가격번호리스트', 값: JSON.stringify(list), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@가격명', 값: String(priceName ?? ''), 리스트값: null },
      ],
    };
    const procedureName = encodeURIComponent('DBO.RCT_T_상품가격_가격명');
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${procedureName}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Hide (delete) selected product price/stock by price numbers
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EA%B0%80%EA%B2%A9_%EC%88%A8%EA%B9%80
// payload Table1: @상품번호, @공급업체번호, @처리자, @선택가격번호리스트 (stringified JSON array)
// The list entries format: [{ 가격번호: string|number }, ...]
export const hideProductPricesByPriceNos = async ({ productNo, companyNo, userNo, selected }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@선택가격번호리스트', 값: JSON.stringify(Array.isArray(selected) ? selected : []), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EA%B0%80%EA%B2%A9_%EC%88%A8%EA%B9%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Unmap one or more images from a content item (컨텐츠 이미지 매핑 삭제)
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%82%AD%EC%A0%9C
// Params: { contentNo, userNo, imageNos: number[] }
export const deleteContentImageMapping = async ({ contentNo, userNo, imageNos }) => {
  try {
    const list = (imageNos || []).map((n) => ({ 이미지번호: Number(n) }));
    const payload = {
      Table1: [
        { 컬럼명: '@컨텐츠번호', 값: Number(contentNo), 리스트값: null },
        { 컬럼명: '@이미지리스트', 값: JSON.stringify(list), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Map one or more images to a content item (컨텐츠 이미지 매핑)
// SP: DBO.TRP_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%EC%9D%B4%EB%AF%B8%EC%A7%80%EB%A7%A4%ED%95%91
// Params: { contentNo, productNo, userNo, imageNos: number[] }
export const mapImagesToContent = async ({ contentNo, productNo, userNo, imageNos }) => {
  try {
    const list = (imageNos || []).map((n) => ({ 이미지번호: Number(n) }));
    const payload = {
      Table1: [
        { 컬럼명: '@컨텐츠번호', 값: Number(contentNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@이미지리스트', 값: JSON.stringify(list), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%EC%9D%B4%EB%AF%B8%EC%A7%80%EB%A7%A4%ED%95%91`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create new product content skeleton and return created content info (including content number)
// Prefer dedicated 신규 생성 SP, and fall back to 기존 저장 SP(NULL 컨텐츠번호)
export const createNewProductContent = async ({
  productNo,
  companyNo,
  userNo,
  contentTypeCode,
  title,
}) => {
  try {
    // 1) Try dedicated 신규 생성 SP: DBO.RCT_T_상품콘텐츠신규생성
    //    Minimal required params inferred from historical usage
    const payloadNew = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@컨텐츠종류코드', 값: String(contentTypeCode || ''), 리스트값: null },
        { 컬럼명: '@컨텐츠명', 값: String(title || ''), 리스트값: null },
      ],
    };
    const urlNew = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%EC%8B%A0%EA%B7%9C%EC%83%9D%EC%84%B1`;
    try {
      const resNew = await fetch(urlNew, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payloadNew),
      });
      if (resNew.ok) {
        const dataNew = await resNew.json();
        // If SP is present and returns data, use it directly
        return dataNew;
      }
      // If not ok, fall through to fallback
      console.warn('createNewProductContent: 신규생성 SP 응답이 정상적이지 않아 fallback을 시도합니다.', resNew.status);
    } catch (e1) {
      console.warn('createNewProductContent: 신규생성 SP 호출 실패, fallback 진행:', e1);
    }

    // 2) Fallback: Use the existing '상품정보콘텐츠저장' SP with NULL contentNo to create a new content entry
    const payload = {
      Table1: [
        { 컬럼명: '@컨텐츠명', 값: String(title || ''), 리스트값: null },
        { 컬럼명: '@노출여부', 값: true, 리스트값: null },
        { 컬럼명: '@대리점만노출', 값: false, 리스트값: null },
        { 컬럼명: '@컨텐츠내용', 값: '', 리스트값: null },
        { 컬럼명: '@컨텐츠내용HTML', 값: '', 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@컨텐츠번호', 값: 'NULL', 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@이미지매핑사용여부', 값: true, 리스트값: null },
        { 컬럼명: '@파일첨부여부', 값: false, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%A0%95%EB%B3%B4%EC%BD%98%ED%85%90%EC%B8%A0%EC%A0%80%EC%9E%A5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save product content info (스마트에디터 콘텐츠 저장)
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%A0%95%EB%B3%B4%EC%BD%98%ED%85%90%EC%B8%A0%EC%A0%80%EC%9E%A5
// Params: { productNo, contentNo, companyNo, userNo, title, text, html, expose=true, dealerOnly=false, imageMappingUse=true, fileAttach=false }
// Delete product content
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%EC%82%AD%EC%A0%9C
// Params: { contentNo, resourceNo, userNo }
export const deleteProductContent = async ({
  contentNo,
  resourceNo = null,
  userNo,
}) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@컨텐츠번호', 값: Number(contentNo), 리스트값: null },
        { 컬럼명: '@자원번호', 값: resourceNo || 'NULL', 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const saveProductContentInfo = async ({
  productNo,
  contentNo,
  companyNo,
  userNo,
  title,
  text,
  html,
  expose = true,
  dealerOnly = false,
  imageMappingUse = true,
  fileAttach = false,
}) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@컨텐츠명', 값: String(title || ''), 리스트값: null },
        { 컬럼명: '@노출여부', 값: !!expose, 리스트값: null },
        { 컬럼명: '@대리점만노출', 값: !!dealerOnly, 리스트값: null },
        { 컬럼명: '@컨텐츠내용', 값: (typeof text === 'undefined' ? null : String(text)), 리스트값: null },
        { 컬럼명: '@컨텐츠내용HTML', 값: (typeof html === 'undefined' ? null : String(html)), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@컨텐츠번호', 값: (contentNo == null ? 'NULL' : Number(contentNo)), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@이미지매핑사용여부', 값: !!imageMappingUse, 리스트값: null },
        { 컬럼명: '@파일첨부여부', 값: !!fileAttach, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%A0%95%EB%B3%B4%EC%BD%98%ED%85%90%EC%B8%A0%EC%A0%80%EC%9E%A5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete a schedule/place image
// SP: DBO.WSP_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%82%AD%EC%A0%9C
// payload Table1: @공급자번호, @이미지번호, @상품일정번호, @옵션일정번호, @유저번호, @장소번호
export const deleteScheduleImage = async ({ imageNo, supplierNo, placeNo = 0, scheduleNo = -1, optionScheduleNo = -1, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(supplierNo), 리스트값: null },
        { 컬럼명: '@이미지번호', 값: Number(imageNo), 리스트값: null },
        { 컬럼명: '@상품일정번호', 값: Number(scheduleNo), 리스트값: null },
        { 컬럼명: '@옵션일정번호', 값: Number(optionScheduleNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@장소번호', 값: Number(placeNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save hotel itinerary edits (숙박 편집 저장)
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%88%99%EB%B0%95_02
// Params: { productNo, itineraryTypeNo, dayNo, placeNo, placeName, bigo, planNo, planName, prevPlaceName, prevBigo, resourceNo }
//   plans?: Array<{ planNo, planName, prevPlaceName?, prevBigo? }>
//     - 옵션. 제공 시 한 번의 호출로 같은 일차의 모든 요금제에 동일한 호텔을 등록한다.
//       (@숙박리스트, @요금제상태리스트에 plans 항목 전체를 함께 보냄)
export const saveHotelItineraryEdits = async ({ productNo, itineraryTypeNo, dayNo, placeNo = null, placeName = '', bigo = '', planNo, planName, prevPlaceName = '', prevBigo = '', resourceNo, plans }) => {
  try {
    const planEntries = Array.isArray(plans) && plans.length > 0
      ? plans
      : [{ planNo, planName, prevPlaceName, prevBigo }];

    const 숙박리스트 = planEntries.map((p) => ({
      date: `Day${Number(dayNo)}`,
      plan: String(p.planNo),
      planNo: String(p.planNo),
      planName: String(p.planName || ''),
      placeName: String(p.prevPlaceName || ''),
      kind: '331002',
      bigo: String(p.prevBigo || ''),
    }));
    const 요금제상태리스트 = planEntries.map((p) => ({
      planName: String(p.planName || ''),
      planNo: String(p.planNo),
      planStatus: true,
    }));

    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@장소번호', 값: placeNo == null ? 'NULL' : Number(placeNo), 리스트값: null },
        { 컬럼명: '@장소명', 값: String(placeName || ''), 리스트값: null },
        { 컬럼명: '@비고', 값: String(bigo || ''), 리스트값: null },
        { 컬럼명: '@숙박구분', 값: String(placeName ? '2' : ''), 리스트값: null },
        { 컬럼명: '@숙박리스트', 값: JSON.stringify(숙박리스트), 리스트값: null },
        { 컬럼명: '@요금제상태리스트', 값: JSON.stringify(요금제상태리스트), 리스트값: null },
        { 컬럼명: '@선택자원번호', 값: String(resourceNo || ''), 리스트값: null },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%88%99%EB%B0%95_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save a single meal edit for a specific day
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%8B%9D%EC%82%AC
// Params: { productNo, itineraryTypeNo, dayNo, planLabel: '조식'|'중식'|'석식', oldContent, newContent }
export const saveItineraryMeal = async ({ productNo, itineraryTypeNo, dayNo, planLabel, oldContent, newContent }) => {
  try {
    // Use only the user-entered text for @식사명 (no day/label prefix)
    const mealName = String(newContent || '');
    const list = [
      {
        date: `Day${Number(dayNo)}`,
        plan: String(planLabel),
        content: {
          planNo: String(planLabel),
          planName: String(planLabel),
          // Use newContent (parsed meal value) for placeName
          placeName: String(newContent || ''),
        },
      },
    ];
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@식사명', 값: String(mealName), 리스트값: null },
        { 컬럼명: '@식사리스트', 값: JSON.stringify(list), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%8B%9D%EC%82%AC`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Add a new itinerary schedule item to obtain 상품일정번호
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%B6%94%EA%B0%80
// Params: { productNo, itineraryTypeNo, day, productTypeCode, name, selectedScheduleNo }
export const addItinerarySchedule = async ({ productNo, itineraryTypeNo, day, productTypeCode, name, selectedScheduleNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@일차', 값: Number(day), 리스트값: null },
        { 컬럼명: '@상품종류', 값: '038001', 리스트값: null },
        { 컬럼명: '@일정명', 값: String(name || ''), 리스트값: null },
        { 컬럼명: '@추가구분', 값: 'DOWN', 리스트값: null },
        { 컬럼명: '@선택상품일정번호', 값: (selectedScheduleNo ? Number(selectedScheduleNo) : ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%B6%94%EA%B0%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete an itinerary schedule item
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%82%AD%EC%A0%9C
// Params: { scheduleNo, itineraryTypeNo, productNo, userNo }
export const deleteItinerarySchedule = async ({ scheduleNo, itineraryTypeNo, productNo, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품일정번호', 값: Number(scheduleNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save itinerary content (name, description) for a specific schedule item
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%BD%98%ED%85%90%EC%B8%A0%EC%9E%90%EB%8F%99%EC%A0%80%EC%9E%A5
// Params: { scheduleNo, itineraryTypeNo, productNo, name, content, userNo }
export const saveItineraryContent = async ({ scheduleNo, itineraryTypeNo, productNo, name, content, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품일정번호', 값: Number(scheduleNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정명', 값: String(name ?? ''), 리스트값: null },
        { 컬럼명: '@일정내용', 값: String(content ?? ''), 리스트값: null },
        { 컬럼명: '@소요시간', 값: '00:00', 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%BD%98%ED%85%90%EC%B8%A0%EC%9E%90%EB%8F%99%EC%A0%80%EC%9E%A5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get up to many images for a specific schedule item/place
// SP: DBO.WSP_S_%EC%9E%A5%EC%86%8C%EB%93%B1%EB%A1%9D%ED%98%84%ED%99%A9_%ED%8C%8C%EC%9D%BC%EC%9D%B4%EB%AF%B8%EC%A7%80_01
// payload Table1: @상품일정번호, @상품옵션일정번호=-1, @공급자번호, @장소번호(없으면 0)
export const getSchedulePlaceImages = async ({ scheduleNo, optionScheduleNo = -1, supplierNo, placeNo = 0 }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품일정번호', 값: Number(scheduleNo) },
        { 컬럼명: '@상품옵션일정번호', 값: Number(optionScheduleNo) },
        { 컬럼명: '@공급자번호', 값: String(supplierNo) },
        { 컬럼명: '@장소번호', 값: Number(placeNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%9E%A5%EC%86%8C%EB%93%B1%EB%A1%9D%ED%98%84%ED%99%A9_%ED%8C%8C%EC%9D%BC%EC%9D%B4%EB%AF%B8%EC%A7%80_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // expect { empt0: [...], table1: [ { 업체번호, 이미지경로, ... } ] }
  } catch (error) {
    return handleApiError(error);
  }
};

// Get content images by content number (컨텐츠 이미지 조회)
// SP: DBO.WSP_S_%ED%98%B8%ED%85%94%EB%93%B1%EB%A1%9D%ED%98%84%ED%99%A9_%EC%BB%A8%ED%85%90%EC%B8%A0%EC%9D%B4%EB%AF%B8%EC%A7%80
// payload Table1: @컨텐츠번호
export const getContentImagesByContentNo = async ({ contentNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@컨텐츠번호', 값: Number(contentNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%ED%98%B8%ED%85%94%EB%93%B1%EB%A1%9D%ED%98%84%ED%99%A9_%EC%BB%A8%ED%85%90%EC%B8%A0%EC%9D%B4%EB%AF%B8%EC%A7%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    // Common API returns { table: [...] } for lists
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Step 1: Get itinerary type for a product
// SP: DBO.RCT_S_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85
// payload Table1: @상품번호, @일정타입번호='NULL'
export const getItineraryType = async ({ productNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: 'NULL', 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // { table: [ { 일정타입번호, 일정타입명 } ], table1: [ { 미팅값 } ] }
  } catch (error) {
    return handleApiError(error);
  }
};

// Get all itinerary types for a product (including disabled ones)
// SP: DBO.RCT_S_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85
// payload Table1: @상품번호, @일정타입번호=NULL, @사용중지포함=1
export const getItineraryTypeList = async ({ productNo, includeDisabled = true }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: 'NULL', 리스트값: null },
        { 컬럼명: '@사용중지포함', 값: includeDisabled ? 1 : 0, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // { table: [ { 일정타입번호, 일정타입명, 일정타입박수, 일정타입일수, 상품일정타입, 사용여부 } ] }
  } catch (error) {
    return handleApiError(error);
  }
};

// Create or update an itinerary type
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85
// payload Table1: @상품번호, @일정타입번호(null=신규), @일정타입명, @등록자, @박수, @일수, @삭제여부
export const saveItineraryTypeInfo = async ({ productNo, itineraryTypeNo, typeName, userNo, nights, days, disabled }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: itineraryTypeNo != null ? Number(itineraryTypeNo) : 'NULL', 리스트값: null },
        { 컬럼명: '@일정타입명', 값: String(typeName || ''), 리스트값: null },
        { 컬럼명: '@등록자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@박수', 값: nights != null ? Number(nights) : 'NULL', 리스트값: null },
        { 컬럼명: '@일수', 값: days != null ? Number(days) : 'NULL', 리스트값: null },
        { 컬럼명: '@삭제여부', 값: disabled ? 1 : 0, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // { table: [{ 일정타입번호, 일정타입명, 일정타입박수, 일정타입일수, 일정타입사용여부 }] }
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete (soft) an itinerary type
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85%EC%82%AD%EC%A0%9C
// payload Table1: @상품번호, @일정타입번호, @등록자
export const deleteItineraryType = async ({ productNo, itineraryTypeNo, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@등록자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Clone an itinerary type
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85%EB%B3%B5%EC%A0%9C
// payload Table1: @상품번호, @일정타입번호, @일정타입명, @등록자, @박수, @일수
export const cloneItineraryType = async ({ productNo, itineraryTypeNo, typeName, userNo, nights, days }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@일정타입명', 값: String(typeName || ''), 리스트값: null },
        { 컬럼명: '@등록자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@가격번호', 값: 'NULL', 리스트값: null },
        { 컬럼명: '@가격번호그룹', 값: 'NULL', 리스트값: null },
        { 컬럼명: '@프라이빗', 값: 0, 리스트값: null },
        { 컬럼명: '@예약관리생성', 값: 0, 리스트값: null },
        { 컬럼명: '@박수', 값: nights != null ? Number(nights) : 'NULL', 리스트값: null },
        { 컬럼명: '@일수', 값: days != null ? Number(days) : 'NULL', 리스트값: null },
        { 컬럼명: '@삭제여부', 값: 0, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85%EB%B3%B5%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // { table: [{ 일정타입번호, 일정타입명, 일정타입박수, 일정타입일수, 일정타입사용여부 }] }
  } catch (error) {
    return handleApiError(error);
  }
};

// Get active itinerary types for a product (simple list for dropdowns)
// SP: DBO.WSP_S_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85%EB%A6%AC%EC%8A%A4%ED%8A%B8
// payload Table1: @상품번호
export const getItineraryTypeSimpleList = async ({ productNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // { table: [ { 일정타입번호, 일정타입명 } ] }
  } catch (error) {
    return handleApiError(error);
  }
};

// Step 2: Get itinerary detail by itinerary type number
// SP: DBO.RCT_S_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95_02
// payload Table1: @상품번호, @일정타입번호
export const getItineraryDetail = async ({ productNo, itineraryTypeNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // raw dataset containing table, table1..table5
  } catch (error) {
    return handleApiError(error);
  }
};

// Save itinerary order for a given day
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%88%9C%EC%84%9C%EB%B3%80%EA%B2%BD
// Params: { productNo, itineraryTypeNo, dayNo, orderList: Array<{ 상품일정번호: number, 순서: number }> }
export const saveItineraryOrder = async ({ productNo, itineraryTypeNo, dayNo, orderList }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@일차', 값: Number(dayNo), 리스트값: null },
        { 컬럼명: '@일정리스트', 값: JSON.stringify(orderList || []), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%88%9C%EC%84%9C%EB%B3%80%EA%B2%BD`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update itinerary day count (일정 일수 변경)
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85
// Params: { productNo, itineraryTypeNo, itineraryTypeName, userNo, nights, days }
// 일수를 변경하면 해당 일수만큼의 일차가 생성됨
export const updateItineraryDayCount = async ({ productNo, itineraryTypeNo, itineraryTypeName, userNo, nights, days }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@일정타입명', 값: String(itineraryTypeName || '대표일정'), 리스트값: null },
        { 컬럼명: '@등록자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@박수', 값: Number(nights || 0), 리스트값: null },
        { 컬럼명: '@일수', 값: String(days), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // { table: [{ 일정타입번호, 일정타입명, 일정타입박수, 일정타입일수 }] }
  } catch (error) {
    return handleApiError(error);
  }
};

export const swapItineraryDays = async ({ productNo, itineraryTypeNo, sourceDay, targetDay }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@원본일차', 값: Number(sourceDay), 리스트값: null },
        { 컬럼명: '@대상일차', 값: Number(targetDay), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%9D%BC%EC%B0%A8%EB%A7%9E%EB%B0%94%EA%BE%B8%EA%B8%B0`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save visit keywords order (주요 방문지 키워드 순서)
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85%EB%B0%A9%EB%AC%B8%ED%82%A4%EC%9B%8C%EB%93%9C
// Params: { productNo, itineraryTypeNo, keywords }
export const saveItineraryVisitKeywords = async ({ productNo, itineraryTypeNo, keywords }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@방문지키워드', 값: String(keywords || ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85%EB%B0%A9%EB%AC%B8%ED%82%A4%EC%9B%8C%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save tour meeting remark (투어 미팅장소 비고 저장)
// SP: DBO.RCT_T_%ED%88%AC%EC%96%B4%EB%AF%B8%ED%8C%85%EC%9E%A5%EC%86%8C%EC%84%A4%EC%A0%95%EA%B0%92_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85
// payload Table1 (hardcoded per spec):
//  @일정타입번호, @상품번호, @미팅장소타입("329002"), @미팅시간방식("330002"), @미팅시간타입("012003"), @미팅시간(0),
//  @미팅지정시각("00:00"), @장소번호("NULL"), @투어미팅비고(HTML)
export const saveTourMeetingRemark = async ({ productNo, itineraryTypeNo, remarkHtml }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@미팅장소타입', 값: '329002', 리스트값: null },
        { 컬럼명: '@미팅시간방식', 값: '330002', 리스트값: null },
        { 컬럼명: '@미팅시간타입', 값: '012003', 리스트값: null },
        { 컬럼명: '@미팅시간', 값: 0, 리스트값: null },
        { 컬럼명: '@미팅지정시각', 값: '00:00', 리스트값: null },
        { 컬럼명: '@장소번호', 값: 'NULL', 리스트값: null },
        { 컬럼명: '@투어미팅비고', 값: String(remarkHtml || ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%ED%88%AC%EC%96%B4%EB%AF%B8%ED%8C%85%EC%9E%A5%EC%86%8C%EC%84%A4%EC%A0%95%EA%B0%92_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%83%80%EC%9E%85`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Download selected common images into the product's gallery
// SP: DBO.WSP_T_%EA%B3%B5%EC%9A%A9%EC%9D%B4%EB%AF%B8%EC%A7%80%EB%82%B4%EB%A0%A4%EB%B0%9B%EA%B8%B0+
// Params: { companyNo, userNo, productNo, imageNos: number[] }
export const downloadCommonImagesToProduct = async ({ companyNo, userNo, productNo, imageNos }) => {
  try {
    const list = (imageNos || []).map((n) => ({ 이미지번호: Number(n) }));
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@이미지리스트', 값: JSON.stringify(list), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EA%B3%B5%EC%9A%A9%EC%9D%B4%EB%AF%B8%EC%A7%80%EB%82%B4%EB%A0%A4%EB%B0%9B%EA%B8%B0+`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Fetch product gallery images (after downloading common images)
// SP: DBO.RCT_S_%EC%83%81%ED%92%88%EA%B0%A4%EB%9F%AC%EB%A6%AC
// Params: { companyNo, productNo }
export const getProductGallery = async ({ companyNo, productNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%83%81%ED%92%88%EA%B0%A4%EB%9F%AC%EB%A6%AC`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get representative image for a product (노출순서 1 우선)
// SP: DBO.RCT_S_%EC%83%81%ED%92%88_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80
// payload Table1: @공급자번호, @상품번호
// Returns: string | null (image URL)
export const getProductRepresentativeImage = async ({ companyNo, productNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%83%81%ED%92%88_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data.table) ? data.table : [];
    if (!list.length) return null;
    // Prefer 노출순서 === 1, fallback to first row
    const preferred = list.find((r) => Number(r?.노출순서) === 1) || list[0];
    return preferred?.이미지경로2 || preferred?.이미지경로 || preferred?.원본이미지경로 || null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Upload a file to S3 via API using Base64 JSON (returns { url })
// Params: { fileBase64: string (no data: prefix), fileName: string, filePath: string }
export const uploadAllSizeS3 = async ({ fileBase64, fileName, filePath }) => {
  try {
    const url = `${API_BASE_URL}/Upload/uploadAllSizeS3`;
    const payload = {
      File: fileBase64,
      FileName: fileName,
      FilePath: filePath,
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`Upload error: ${response.status}`);
    const data = await response.json();
    return data; // { url: 'https://...' }
  } catch (error) {
    return handleApiError(error);
  }
};

// Get all cities by supplier (토큰의 companyNo)
// SP: DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C
// payload Table1: @종류='도시', @공급업체번호='{companyNo}'
export const getSupplierCities = async ({ companyNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@종류', 값: '도시', 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get airports by supplier (토큰의 companyNo)
// SP: DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C
// payload Table1: @종류='공항', @공급업체번호='{companyNo}'
export const getSupplierAirports = async ({ companyNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@종류', 값: '공항', 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    // expected shape: { table: [ { 코드, 코드명 } ] }
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

export const getFlightScheduleByFlightNo = async ({ flightNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@편명', 값: String(flightNo || ''), 리스트값: null },
      ],
    };
    const procedureName = encodeURIComponent('DBO.WSP_S_항공스케줄_편명_01');
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${procedureName}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get tax cost items (세무 원가 항목) for tours by supplier
// SP: DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C
// payload Table1: @종류='233', @공급업체번호='{companyNo}'
// Returns: Array<{ 코드: string, 코드명: string }>
export const getSupplierTaxCostItems = async ({ companyNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@종류', 값: '233', 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get city info by product number
// SP: DBO.TRP_S_%EC%83%81%ED%92%88%EB%B2%88%ED%98%B8_%EB%8F%84%EC%8B%9C%EC%A0%95%EB%B3%B4
// payload Table1: @상품번호
export const getProductCity = async ({ productNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: String(productNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EB%B2%88%ED%98%B8_%EB%8F%84%EC%8B%9C%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    // expected: { table: [ { 도시번호, 도시명 } ] }
    const row = Array.isArray(data.table) && data.table.length ? data.table[0] : null;
    return row || null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get tour stock list by resource and date range (투어재고리스트_자원기간_상품번호)
// SP: DBO.TRP_S_%ED%88%AC%EC%96%B4%EC%9E%AC%EA%B3%A0%EB%A6%AC%EC%8A%A4%ED%8A%B8_%EC%9E%90%EC%9B%90%EA%B8%B0%EA%B0%84_%EC%83%81%ED%92%88%EB%B2%88%ED%98%B8
// payload Table1: @업체번호, @상품번호, @시작일, @종료일, @시작시간, @자원번호
// Returns: { table: [{ 재고: JSON string with array of date objects }] }
export const getTourStockList = async ({ companyNo, productNo, startDate, endDate, startTime = '00:00', resourceNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@상품번호', 값: Number(productNo) },
        { 컬럼명: '@시작일', 값: String(startDate) },
        { 컬럼명: '@종료일', 값: String(endDate) },
        { 컬럼명: '@시작시간', 값: String(startTime) },
        { 컬럼명: '@자원번호', 값: Number(resourceNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%88%AC%EC%96%B4%EC%9E%AC%EA%B3%A0%EB%A6%AC%EC%8A%A4%ED%8A%B8_%EC%9E%90%EC%9B%90%EA%B8%B0%EA%B0%84_%EC%83%81%ED%92%88%EB%B2%88%ED%98%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get calendar price/stock by month range for a product
// SP: DBO.RCT_S_%EA%B0%80%EA%B2%A9%EC%98%88%EC%95%BD_02
// payload Table1: @상품종류, @상품번호, @공급업체번호, @시작일, @종료일, @판매여부1, @판매여부2, @판매된리스트만보기, @키워드, @구분, @플랫폼수수료반영, @결제수수료반영
export const getPriceStockCalendar = async ({
  productTypeName,
  productNo,
  companyNo,
  startDate,
  endDate,
  판매여부1 = true,
  판매여부2 = true,
  판매된리스트만보기 = false,
  키워드 = '',
  구분 = '3',
  플랫폼수수료반영 = false,
  결제수수료반영 = false,
}) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품종류', 값: String(productTypeName || '투어'), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@시작일', 값: String(startDate), 리스트값: null },
        { 컬럼명: '@종료일', 값: String(endDate), 리스트값: null },
        { 컬럼명: '@판매여부1', 값: !!판매여부1, 리스트값: null },
        { 컬럼명: '@판매여부2', 값: !!판매여부2, 리스트값: null },
        { 컬럼명: '@판매된리스트만보기', 값: !!판매된리스트만보기, 리스트값: null },
        { 컬럼명: '@키워드', 값: String(키워드 || ''), 리스트값: null },
        { 컬럼명: '@구분', 값: String(구분 || '3'), 리스트값: null },
        { 컬럼명: '@플랫폼수수료반영', 값: !!플랫폼수수료반영, 리스트값: null },
        { 컬럼명: '@결제수수료반영', 값: !!결제수수료반영, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EA%B0%80%EA%B2%A9%EC%98%88%EC%95%BD_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // raw dataset (shape depends on SP)
  } catch (error) {
    return handleApiError(error);
  }
};

// Fetch detailed price registration info for a given price plan
// SP: DBO.RCT_S_%EA%B0%80%EA%B2%A9%EC%98%88%EC%95%BD%EB%93%B1%EB%A1%9D%EC%A0%95%EB%B3%B4_01
// payload Table1: @상품번호, @공급업체번호, @가격번호
export const getPriceReservationInfo = async ({ productNo, companyNo, priceNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@가격번호', 값: Number(priceNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EA%B0%80%EA%B2%A9%EC%98%88%EC%95%BD%EB%93%B1%EB%A1%9D%EC%A0%95%EB%B3%B4_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get common images by city number
// SP: DBO.WSP_S_%EA%B3%B5%EC%9A%A9%EC%9D%B4%EB%AF%B8%EC%A7%80+
// payload Table1: @도시번호
export const getCommonImagesByCity = async ({ cityNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@도시번호', 값: String(cityNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%EA%B3%B5%EC%9A%A9%EC%9D%B4%EB%AF%B8%EC%A7%80+`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete a representative image
// SP: DBO.WSP_T_%EC%83%81%ED%92%88_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%82%AD%EC%A0%9C_01
// Params: { imageNo, productNo, userNo }
export const deleteRepresentativeImage = async ({ imageNo, productNo, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@이미지번호', 값: String(imageNo) },
        { 컬럼명: '@상품번호', 값: String(productNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%83%81%ED%92%88_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%82%AD%EC%A0%9C_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get price information for reservation (tour or hotel)
// Tour SP:  DBO.TS_S_%EA%B0%80%EA%B2%A9%EB%B2%88%ED%98%B8_%EA%B0%80%EA%B2%A9%EC%A0%95%EB%B3%B4_%ED%88%AC%EC%96%B4_TRP%EC%9A%A9
// Hotel SP: DBO.TS_S_%EA%B0%80%EA%B2%A9%EB%B2%88%ED%98%B8_%EA%B0%80%EA%B2%A9%EC%A0%95%EB%B3%B4_TRP%EC%9A%A9
// payload Table1: @업체번호, @가격번호, @박수, @성인수, @나이배열, @객실수
// Returns: { table: [...] } with price and reservation details
export const getPriceInfoForReservation = async ({ companyNo, priceNo, nights = 1, adultCount = 1, ageArray = '', roomCount = 1, productTypeName }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@가격번호', 값: Number(priceNo) },
        { 컬럼명: '@박수', 값: Number(nights) },
        { 컬럼명: '@성인수', 값: Number(adultCount) },
        { 컬럼명: '@나이배열', 값: String(ageArray) },
        { 컬럼명: '@객실수', 값: Number(roomCount) },
      ],
    };
    const isHotel = String(productTypeName || '').trim() === '호텔';
    const procedureName = isHotel
      ? 'DBO.TS_S_%EA%B0%80%EA%B2%A9%EB%B2%88%ED%98%B8_%EA%B0%80%EA%B2%A9%EC%A0%95%EB%B3%B4_TRP%EC%9A%A9'
      : 'DBO.TS_S_%EA%B0%80%EA%B2%A9%EB%B2%88%ED%98%B8_%EA%B0%80%EA%B2%A9%EC%A0%95%EB%B3%B4_%ED%88%AC%EC%96%B4_TRP%EC%9A%A9';
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${procedureName}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Submit reservation for tour
// SP: DBO.TS_T_%EC%98%88%EC%95%BD%ED%95%98%EA%B8%B0
// payload Table1: 다양한 예약 정보 파라미터
// Returns: { table: [...] } with reservation result
export const submitReservation = async ({
  priceNo,
  priceOptionNo,
  startDate,
  ordererName,
  ordererPhone,
  ordererEmail,
  adultCount,
  childCount,
  childAges,
  nights = 1,
  roomCount = 1,
  selectedOptions = [],
  totalAmount,
  companyNo,
  productTypeName,
  agencyCode, // 대리점 코드 (선택적)
}) => {
  try {
    const { userNo } = getUserInfo() || {};
    if (!userNo) throw new Error('User information is missing');
    
    // 나이배열 정렬 (오름차순)
    const sortedChildAges = [...childAges].sort((a, b) => a - b).join(',');
    const productTypeCode = String(productTypeName || '').trim() === '호텔' ? '001001' : '001002';
    
    // 판매업체번호: 대리점 선택 시 대리점 코드, 미선택 시 로그인 업체번호
    const salesCompanyNo = agencyCode ? String(agencyCode) : String(companyNo);
    
    const payload = {
      Table1: [
        { 컬럼명: '@상품종류', 값: String(productTypeCode) },
        { 컬럼명: '@대표가격번호', 값: Number(priceNo) },
        { 컬럼명: '@대표가격옵션번호', 값: Number(priceOptionNo) },
        { 컬럼명: '@행사시작일시', 값: String(startDate) },
        { 컬럼명: '@예약자', 값: String(ordererName) },
        { 컬럼명: '@연락처', 값: String(ordererPhone) },
        { 컬럼명: '@이메일', 값: String(ordererEmail) },
        { 컬럼명: '@성인수', 값: Number(adultCount) },
        { 컬럼명: '@아동수', 값: Number(childCount) },
        { 컬럼명: '@나이배열', 값: String(sortedChildAges) },
        { 컬럼명: '@박수', 값: Number(nights) },
        { 컬럼명: '@객실수', 값: Number(roomCount) },
        { 컬럼명: '@선택옵션', 값: JSON.stringify(selectedOptions) },
        { 컬럼명: '@결제하실금액', 값: Number(totalAmount) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@판매업체번호', 값: salesCompanyNo },
      ],
    };
    
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TS_T_%EC%98%88%EC%95%BD%ED%95%98%EA%B8%B0`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save representative image order (drag-and-drop result)
// SP: DBO.RCT_T_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%88%9C%EC%84%9C%EC%A0%80%EC%9E%A5
// Params: { productNo: number|string, orderList: Array<{ 이미지번호: number, 순서: number }> }
export const saveRepresentativeImageOrder = async ({ productNo, orderList }) => {
  try {
    const userInfo = getUserInfo();
    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(userInfo.companyNo), 리스트값: null },
        { 컬럼명: '@순서리스트', 값: JSON.stringify(orderList || []), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%88%9C%EC%84%9C%EC%A0%80%EC%9E%A5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Register uploaded image info (returns table with 이미지번호 등)
// SP: DBO.TRP_T_%EC%83%81%ED%92%88_%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%97%85%EB%A1%9C%EB%93%9C
export const registerUploadedImage = async ({ companyNo, userNo, productNo, fileName, imagePath }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@파일명', 값: String(fileName), 리스트값: null },
        { 컬럼명: '@이미지경로', 값: String(imagePath), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%81%ED%92%88_%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%97%85%EB%A1%9C%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // expect { table: [ { 이미지번호, ... } ] }
  } catch (error) {
    return handleApiError(error);
  }
};

// Save as representative image
// SP: DBO.WSP_T_%EC%83%81%ED%92%88_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80%ED%8C%8C%EC%9D%BC%EC%A0%80%EC%9E%A5
export const saveRepresentativeImage = async ({ imageNo, productNo, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@이미지번호', 값: Number(imageNo) },
        { 컬럼명: '@상품번호', 값: Number(productNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%83%81%ED%92%88_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80%ED%8C%8C%EC%9D%BC%EC%A0%80%EC%9E%A5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save uploaded image to a schedule (일정 이미지 선택 시)
// SP: DBO.WSP_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95_%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%97%85%EB%A1%9C%EB%93%9C_%EA%B0%A4%EB%9F%AC%EB%A6%AC%EC%B9%B4%ED%94%BC
// payload Table1: @이미지번호, @처리자, @상품일정번호
export const saveScheduleImageFromGallery = async ({ imageNo, scheduleNo, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@이미지번호', 값: Number(imageNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@상품일정번호', 값: Number(scheduleNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95_%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%97%85%EB%A1%9C%EB%93%9C_%EA%B0%A4%EB%9F%AC%EB%A6%AC%EC%B9%B4%ED%94%BC`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save uploaded image to a place within a schedule (장소 이미지 선택 시)
// SP: DBO.WSP_T_%EC%9E%A5%EC%86%8C_%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%97%85%EB%A1%9C%EB%93%9C_%EA%B0%A4%EB%9F%AC%EB%A6%AC%EC%B9%B4%ED%94%BC_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95
// payload Table1: @이미지번호, @처리자, @장소번호, @상품일정번호
export const savePlaceImageFromGalleryToSchedule = async ({ imageNo, placeNo, scheduleNo, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@이미지번호', 값: Number(imageNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@장소번호', 값: Number(placeNo) },
        { 컬럼명: '@상품일정번호', 값: Number(scheduleNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%9E%A5%EC%86%8C_%EC%9D%B4%EB%AF%B8%EC%A7%80%EC%97%85%EB%A1%9C%EB%93%9C_%EA%B0%A4%EB%9F%AC%EB%A6%AC%EC%B9%B4%ED%94%BC_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get single request field info (includes 리스트값) by 필드번호
// SP: DBO.TRP_S_%ED%95%84%EB%93%9C%EC%A0%95%EB%B3%B4
// payload Table1: @공급업체번호, @필드번호
export const getRequestFieldInfo = async ({ fieldNo }) => {
  try {
    const userInfo = getUserInfo();
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(userInfo.companyNo) },
        { 컬럼명: '@필드번호', 값: String(fieldNo) },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%95%84%EB%93%9C%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    // Try to normalize: many SPs return data.table; fallback to raw
    return data.table || data || [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Insert a new request field (first-time save)
// SP: DBO.TRP_T_%ED%95%84%EB%93%9C
// Required params in payload Table1:
// @구분(I), @공급자번호, @상품종류, @필드번호(NULL), @적용대상구분(016001), @데이터타입, @필드명, @필드명2, @표시여부, @필수여부, @변경자, @필드설명, @리스트값
export const insertRequestField = async ({ productTypeName, field }) => {
  try {
    const userInfo = getUserInfo();

    const productTypeCode = productTypeName === '투어' ? '001002' : productTypeName === '호텔' ? '001002' : '001002';
    const typeMap = {
      '텍스트': '017001',
      '리스트': '017002',
      '체크박스': '017003',
    };
    const dataTypeCode = /^\d{6}$/.test(String(field.데이터타입)) ? String(field.데이터타입) : (typeMap[field.데이터타입] || '017001');
    const listValue = (field.데이터타입 === '리스트' || dataTypeCode === '017002') && Array.isArray(field.옵션목록)
      ? field.옵션목록.join('\n')
      : '';

    const payload = {
      Table1: [
        { 컬럼명: '@구분', 값: 'I' },
        { 컬럼명: '@공급자번호', 값: String(userInfo.companyNo) },
        { 컬럼명: '@상품종류', 값: productTypeCode },
        { 컬럼명: '@필드번호', 값: 'NULL' },
        { 컬럼명: '@적용대상구분', 값: '016001' },
        { 컬럼명: '@데이터타입', 값: dataTypeCode },
        { 컬럼명: '@필드명', 값: String(field.필드라벨 || field.필드명 || '') },
        { 컬럼명: '@필드명2', 값: String(field.필드명2 || '') },
        { 컬럼명: '@표시여부', 값: !!field.표시여부 },
        { 컬럼명: '@필수여부', 값: !!(field.표시여부 ? true : field.필수여부) },
        { 컬럼명: '@변경자', 값: String(userInfo.userNo) },
        { 컬럼명: '@필드설명', 값: String(field.필드설명 || '') },
        { 컬럼명: '@리스트값', 값: listValue },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%95%84%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete a place from itinerary schedule
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%9E%A5%EC%86%8C%EC%82%AD%EC%A0%9C
// payload Table1: @상품일정번호, @상품번호, @일차, @장소명, @장소번호, @처리자
export const deleteItineraryPlace = async ({ scheduleNo, productNo, day, placeName, placeNo }) => {
  try {
    const userInfo = getUserInfo();
    const payload = {
      Table1: [
        { 컬럼명: '@상품일정번호', 값: Number(scheduleNo) },
        { 컬럼명: '@상품번호', 값: Number(productNo) },
        { 컬럼명: '@일차', 값: Number(day) },
        { 컬럼명: '@장소명', 값: String(placeName || '') },
        { 컬럼명: '@장소번호', 값: Number(placeNo) },
        { 컬럼명: '@처리자', 값: Number(userInfo?.userNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%9E%A5%EC%86%8C%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete a request field by 필드번호
// SP: DBO.TRP_T_%ED%95%84%EB%93%9C%EC%82%AD%EC%A0%9C
// payload Table1: @공급업체번호, @필드번호, @변경자
export const deleteRequestField = async ({ fieldNo }) => {
  try {
    const userInfo = getUserInfo();
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(userInfo.companyNo) },
        { 컬럼명: '@필드번호', 값: String(fieldNo) },
        { 컬럼명: '@변경자', 값: String(userInfo.userNo) },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%95%84%EB%93%9C%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get product type list using provided stored procedure (투어상품타입 목록)
export const getProductTypes = async () => {
  try {
    const userInfo = getUserInfo();

    const payload = {
      "Table1": [
        { "컬럼명": "@종류", "값": "투어상품타입", "리스트값": null },
        { "컬럼명": "@공급업체번호", "값": String(userInfo.companyNo), "리스트값": null }
      ]
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.table || [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get hotel product type list (호텔상품타입 목록)
// SP: DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C with @종류='호텔상품타입'
export const getHotelProductTypes = async () => {
  try {
    const userInfo = getUserInfo();
    const payload = {
      Table1: [
        { 컬럼명: '@종류', 값: '호텔상품타입', 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(userInfo.companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data.table || [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get product code list for management (상품코드 관리용 목록)
// SP: DBO.RCT_S_%EC%83%81%ED%92%88%ED%83%80%EC%9E%85%EB%A6%AC%EC%8A%A4%ED%8A%B8
// payload Table1: @업체번호
// Returns rows like: { 상품종류, 구분, 구분코드, 고정, 알파벳 }
export const getProductCodeList = async () => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo ?? '') , 리스트값: null},
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%83%81%ED%92%88%ED%83%80%EC%9E%85%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateProductCodeAlphabet = async ({ code, alphabet }) => {
  throw new Error('상품코드 알파벳 수정 API가 아직 구성되지 않았습니다. 업데이트용 프로시저 정보를 제공해 주세요.');
};

export const getTravelRegionCategoryList = async () => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo ?? ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%97%AC%ED%96%89%EC%A7%80%EB%8C%80%EB%B6%84%EB%A5%98%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return rows;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save selected images for a schedule (체크한 이미지 반영)
// SP: DBO.WSP_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95_%EC%9D%B4%EB%AF%B8%EC%A7%80
// payload Table1: @상품일정번호, @이미지리스트 (JSON string like [{"이미지번호":"62753"}, ...])
export const saveScheduleSelectedImages = async ({ scheduleNo, imageNos }) => {
  try {
    const list = (imageNos || []).map((n) => ({ 이미지번호: String(n) }));
    const payload = {
      Table1: [
        { 컬럼명: '@상품일정번호', 값: Number(scheduleNo) },
        { 컬럼명: '@이미지리스트', 값: JSON.stringify(list) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95_%EC%9D%B4%EB%AF%B8%EC%A7%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Copy an itinerary schedule to a specific day
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95_%EC%9D%BC%EC%A0%95%EB%B2%88%ED%98%B8%EB%B3%B5%EC%A0%9C
// payload Table1: @상품번호, @일정타입번호, @일차, @선택상품일정번호
export const copyScheduleToDay = async ({ productNo, itineraryTypeNo, day, selectedScheduleNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@일차', 값: Number(day), 리스트값: null },
        { 컬럼명: '@선택상품일정번호', 값: Number(selectedScheduleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95_%EC%9D%BC%EC%A0%95%EB%B2%88%ED%98%B8%EB%B3%B5%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};
export const copyItineraryDayToDay = async ({
  sourceProductNo,
  sourceItineraryTypeNo,
  sourceDay,
  targetProductNo,
  targetItineraryTypeNo,
  targetDay,
  userNo,
}) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@원본상품번호', 값: Number(sourceProductNo), 리스트값: null },
        { 컬럼명: '@원본일정타입번호', 값: (sourceItineraryTypeNo == null ? 'NULL' : Number(sourceItineraryTypeNo)), 리스트값: null },
        { 컬럼명: '@원본일차', 값: Number(sourceDay), 리스트값: null },
        { 컬럼명: '@대상일차', 값: Number(targetDay), 리스트값: null },
        { 컬럼명: '@대상상품번호', 값: Number(targetProductNo), 리스트값: null },
        { 컬럼명: '@대상일정타입번호', 값: Number(targetItineraryTypeNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%9D%BC%EC%B0%A8%EB%B3%B5%EC%82%AC%ED%9B%84%EB%B6%99%EC%97%AC%EB%84%A3%EA%B8%B0`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Search supplier place list by Google place selection
// SP: DBO.TRP_S_팝업장소리스트_공급업체용
// payload Table1: @공급업체번호, @구글키, @장소명, @위도, @경도, @검색키워드
const filterSupplierPlacesWithImages = (data) => {
  if (!data || typeof data !== 'object') return data;
  const hasImages = (row) => Number(row?.이미지수 ?? row?.ImageCount ?? row?.imageCount ?? 0) > 0;
  const filtered = { ...data };
  for (const key of ['table', 'table1']) {
    if (Array.isArray(filtered[key])) {
      filtered[key] = filtered[key].filter(hasImages);
    }
  }
  return filtered;
};

export const getSupplierPlacesByGoogle = async ({ supplierNo, googleKey, placeName, latitude, longitude, keyword }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(supplierNo) },
        { 컬럼명: '@구글키', 값: String(googleKey || '') },
        { 컬럼명: '@장소명', 값: String(placeName || '') },
        { 컬럼명: '@위도', 값: Number(latitude) },
        { 컬럼명: '@경도', 값: Number(longitude) },
        { 컬럼명: '@검색키워드', 값: String((keyword ?? '')) },
      ],
    };
    // Note: encoded procedure name for 안전. Adjust if server expects a different route.
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%8C%9D%EC%97%85%EC%9E%A5%EC%86%8C%EB%A6%AC%EC%8A%A4%ED%8A%B8_%EA%B3%B5%EA%B8%89%EC%97%85%EC%B2%B4%EC%9A%A9`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return filterSupplierPlacesWithImages(data);
  } catch (error) {
    return handleApiError(error);
  }
};

// Add a place to itinerary schedule
// SP: DBO.RCT_T_상품일정장소추가
// payload Table1: { @장소명: string }, { @장소번호: number }, { @상품일정번호: number }
export const addItineraryPlace = async ({ scheduleNo, placeNo, placeName }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@장소명', 값: String(placeName || '') },
        { 컬럼명: '@장소번호', 값: Number(placeNo) },
        { 컬럼명: '@상품일정번호', 값: Number(scheduleNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%EC%9E%A5%EC%86%8C%EC%B6%94%EA%B0%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Fetch 콘텐츠 메인 리스트 by 상품번호
// SP: DBO.RCT_S_%EC%BD%98%ED%85%90%EC%B8%A0%EB%A9%94%EC%9D%B8%EB%A6%AC%EC%8A%A4%ED%8A%B8
// payload Table1: @상품번호
export const getContentMainList = async ({ productNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%BD%98%ED%85%90%EC%B8%A0%EB%A9%94%EC%9D%B8%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Map hotel room resource to a content (객실 자원-콘텐츠 매핑 저장)
// SP: DBO.TRP_T_%ED%98%B8%ED%85%94%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EC%BD%98%ED%85%90%EC%B8%A0%EB%A7%A4%ED%95%91
// payload Table1: @자원번호, @컨텐츠번호
export const mapHotelResourceContent = async ({ resourceNo, contentNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@자원번호', 값: Number(resourceNo), 리스트값: null },
        { 컬럼명: '@컨텐츠번호', 값: Number(contentNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EC%BD%98%ED%85%90%EC%B8%A0%EB%A7%A4%ED%95%91`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save content order (콘텐츠 순서 변경 저장)
// SP: DBO.RCT_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%EC%88%9C%EC%84%9C%EB%B3%80%EA%B2%BD
// payload Table1: @컨텐츠리스트(JSON String), @상품번호, @업체번호, @유저번호
export const saveContentOrder = async ({ productNo, contentList }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@컨텐츠리스트', 값: JSON.stringify(contentList || []), 리스트값: null },
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo || ''), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo || ''), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%EC%88%9C%EC%84%9C%EB%B3%80%EA%B2%BD`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get representative images for a product (대표이미지 목록)
// SP: DBO.RCT_S_%EC%83%81%ED%92%88_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80
// payload Table1: @공급자번호, @상품번호
export const getRepresentativeImages = async (productNo) => {
  try {
    const userInfo = getUserInfo();

    const payload = {
      Table1: [
        { 컬럼명: '@공급자번호', 값: String(userInfo.companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%83%81%ED%92%88_%EB%8C%80%ED%91%9C%EC%9D%B4%EB%AF%B8%EC%A7%80`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data.table) ? data.table : [];
    // sort by '노출순서' ascending
    return list.slice().sort((a, b) => {
      const aa = Number(a?.노출순서 ?? 0);
      const bb = Number(b?.노출순서 ?? 0);
      return aa - bb;
    });
  } catch (error) {
    return handleApiError(error);
  }
};

// Save a single field immediately when changed
// SP: DBO.TRP_T_%ED%95%84%EB%93%9C
// Required params in payload Table1:
// @구분(U), @공급자번호, @상품종류, @필드번호, @적용대상구분(016001), @데이터타입, @필드명, @필드명2, @표시여부, @필수여부, @변경자, @필드설명, @리스트값
// Notes:
// - 상품종류: '투어' -> '001002', '호텔' -> '001002' (per user spec)
// - 리스트값: newline joined when 데이터타입 is '리스트'; otherwise empty
export const saveSingleRequestField = async ({
  productTypeName,
  field,
}) => {
  try {
    const userInfo = getUserInfo();

    // 상품종류 코드 매핑 (둘 다 001002 로 지정됨)
    const productTypeCode = productTypeName === '투어' ? '001002' : productTypeName === '호텔' ? '001002' : '001002';

    // 데이터타입 코드 매핑: 정확한 코드 맵이 없으므로 서버 기대값에 맞게 전달 필요
    // If field.데이터타입 already contains code like '017002', pass-through; otherwise map from label
    const typeMap = {
      '텍스트': '017001',
      '리스트': '017002',
      '체크박스': '017003',
    };
    const dataTypeCode = /^\d{6}$/.test(String(field.데이터타입))
      ? String(field.데이터타입)
      : (typeMap[field.데이터타입] || '017001');

    const listValue = (field.데이터타입 === '리스트' || dataTypeCode === '017002') && Array.isArray(field.옵션목록)
      ? field.옵션목록.join('\n')
      : '';

    const payload = {
      Table1: [
        { 컬럼명: '@구분', 값: 'U' },
        { 컬럼명: '@공급자번호', 값: String(userInfo.companyNo) },
        { 컬럼명: '@상품종류', 값: productTypeCode },
        { 컬럼명: '@필드번호', 값: String(field.필드번호) },
        { 컬럼명: '@적용대상구분', 값: '016001' },
        { 컬럼명: '@데이터타입', 값: dataTypeCode },
        { 컬럼명: '@필드명', 값: String(field.필드라벨 || field.필드명 || '') },
        { 컬럼명: '@필드명2', 값: String(field.필드명2 || '') },
        { 컬럼명: '@표시여부', 값: !!field.표시여부 },
        { 컬럼명: '@필수여부', 값: !!(field.표시여부 ? true : field.필수여부) },
        { 컬럼명: '@변경자', 값: String(userInfo.userNo) },
        { 컬럼명: '@필드설명', 값: String(field.필드설명 || '') },
        { 컬럼명: '@리스트값', 값: listValue },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%95%84%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Fetch 상세 정보 for a product
// SP: DBO.TRP_S_%ED%88%AC%EC%96%B4_%EC%83%81%EC%84%B8%EC%A0%95%EB%B3%B4
// payload Table1: @공급업체번호, @상품번호
export const getProductDetailExtra = async (productNo) => {
  try {
    const userInfo = getUserInfo();
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(userInfo.companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null }
      ]
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%88%AC%EC%96%B4_%EC%83%81%EC%84%B8%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return {
      detail: Array.isArray(data.table) && data.table.length ? data.table[0] : null,
      requestFields: Array.isArray(data.table1) ? data.table1 : []
    };
  } catch (error) {
    return handleApiError(error);
  }
};

// Save 상세 정보 for a product
// SP: DBO.TRP_T_%ED%88%AC%EC%96%B4_%EC%83%81%EC%84%B8%EC%A0%95%EB%B3%B4
// payload Table1: @공급업체번호, @유저번호, @상품번호, @상세정보(JSON), @예약시받을정보(JSON Array)
export const saveProductDetailExtra = async ({ productNo, detail, requestFields }) => {
  try {
    const userInfo = getUserInfo();
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(userInfo.companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userInfo.userNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@상세정보', 값: JSON.stringify(detail || {}), 리스트값: null },
        { 컬럼명: '@예약시받을정보', 값: JSON.stringify(requestFields || []), 리스트값: null }
      ]
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%88%AC%EC%96%B4_%EC%83%81%EC%84%B8%EC%A0%95%EB%B3%B4_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data; // e.g., { table: [...] }
  } catch (error) {
    return handleApiError(error);
  }
};

// Get manager list using provided stored procedure (담당자 목록)
export const getManagers = async () => {
  try {
    const userInfo = getUserInfo();

    const payload = {
      "Table1": [
        { "컬럼명": "@종류", "값": "담당자", "리스트값": null },
        { "컬럼명": "@공급업체번호", "값": String(userInfo.companyNo), "리스트값": null }
      ]
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.table || [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get product list from API
export const getProductList = async () => {
  try {
    const userInfo = getUserInfo();
    
    // Prepare payload for the API call
    const payload = {
      "Table1": [
        { "컬럼명": "@공급자번호", "값": userInfo.companyNo },
        { "컬럼명": "@유저번호", "값": userInfo.userNo }
      ]
    };
    
    // Make API call to get product list
    const response = await fetch(`${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%88%AC%EC%96%B4%EC%83%81%ED%92%88%EB%A6%AC%EC%8A%A4%ED%8A%B8`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data.table || [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get city list using provided stored procedure (도시 목록)
export const getCities = async () => {
  try {
    const userInfo = getUserInfo();

    const payload = {
      "Table1": [
        { "컬럼명": "@종류", "값": "도시", "리스트값": null },
        { "컬럼명": "@공급업체번호", "값": String(userInfo.companyNo), "리스트값": null }
      ]
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.table || [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Save flight itinerary for a product schedule
// SP: DBO.RCT_T_상품일정항공여정
// payload Table1: @상품번호, @일정타입번호, @항공여정 (JSON string)
// Expected format: [{"일차":1,"순서":1,"편명":"oz2211","출발공항":"ICN","도착공항":"DAD","구분":"217001","출발시간":"10:25","도착시간":"13:25","소요시간":"05:00"}]
export const saveFlightItinerary = async ({ productNo, itineraryTypeNo, flightItinerary }) => {
  try {
    // Flight data is already in the correct format from DetailPageSettings.js
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: Number(productNo), 리스트값: null },
        { 컬럼명: '@일정타입번호', 값: Number(itineraryTypeNo), 리스트값: null },
        { 컬럼명: '@항공여정', 값: JSON.stringify(flightItinerary), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EC%9D%BC%EC%A0%95%ED%95%AD%EA%B3%B5%EC%97%AC%EC%A0%95`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create a new quote (신규 견적 등록)
// SP: DBO.ITN_T_%EC%83%81%EB%8B%B4%EC%A0%80%EC%9E%A5
// This creates both product and quote registration
// Required args: { productNo, userNo, companyNo, form }
export const createNewQuote = async ({ productNo, userNo, companyNo, form }) => {
  try {
    const pn = String(productNo);
    
    // Build 상담정보 JSON with hardcoded defaults and user inputs
    const 상담정보 = {
      견적경로: '',
      상담번호: 0,
      상담구분: '419001',
      상담상태: '420001',
      조인업체명: '',
      담당자: '',
      담당자번호: '',
      판매업체: form?.판매업체 != null && String(form.판매업체).trim() !== '' ? String(form.판매업체).trim() : '',
      판매업체번호: form?.판매업체번호 != null && String(form.판매업체번호).trim() !== '' ? String(form.판매업체번호).trim() : '',
      수배처: '',
      수배처번호: '',
      팀컬러: [pn], // 상품번호를 팀컬러로 설정
      요청자명: form?.요청자명 ?? '',
      요청자휴대폰: form?.요청자휴대폰 ?? '',
      요청자이메일: form?.요청자이메일 ?? '',
      시작일: form?.시작일 ?? '',
      종료일: form?.종료일 ?? '',
      견적명: form?.견적명 ?? '',
      견적내용: form?.견적내용 ?? '',
      고객명단: '',
      성인수: Number(form?.성인수 ?? 1),
      소아수: Number(form?.소아수 ?? 1),
      유아수: Number(form?.유아수 ?? 1),
      인솔자수: 0,
      성인판매가: 0,
      소아판매가: 0,
      유아판매가: 0,
      요청일정표: '407002',
      첨부파일리스트: [],
      견적첨부파일리스트: [],
      일정표주소: '',
      적용환율: [
        { 통화코드: '058001', 통화기호: '￦', 적용환율: 1, 실시간환율: 1, 평균환율: 1 },
        { 통화코드: '058002', 통화기호: '$', 적용환율: 1408.4507, 실시간환율: 1408.4507, 평균환율: 1408.4507 },
        { 통화코드: '058003', 통화기호: '€', 적용환율: 1652.8926, 실시간환율: 1652.8926, 평균환율: 1652.8926 },
        { 통화코드: '058004', 통화기호: 'A$', 적용환율: 929.368, 실시간환율: 929.368, 평균환율: 929.368 },
        { 통화코드: '058005', 통화기호: '元', 적용환율: 197.7066, 실시간환율: 197.7066, 평균환율: 197.7066 },
        { 통화코드: '058006', 통화기호: '円', 적용환율: 9.5457, 실시간환율: 9.5457, 평균환율: 9.5457 },
        { 통화코드: '058007', 통화기호: '฿', 적용환율: 43.5313, 실시간환율: 43.5313, 평균환율: 43.5313 },
        { 통화코드: '058008', 통화기호: '₫', 적용환율: 0.0533, 실시간환율: 0.0533, 평균환율: 0.0533 },
        { 통화코드: '058009', 통화기호: 'kr', 적용환율: 141.203, 실시간환율: 141.203, 평균환율: 141.203 },
        { 통화코드: '058010', 통화기호: 'BD', 적용환율: 3731.3433, 실시간환율: 3731.3433, 평균환율: 3731.3433 },
        { 통화코드: '058011', 통화기호: 'KR', 적용환율: 221.2879, 실시간환율: 221.2879, 평균환율: 221.2879 },
        { 통화코드: '058012', 통화기호: 'NZ$', 적용환율: 821.0181, 실시간환율: 821.0181, 평균환율: 821.0181 },
        { 통화코드: '058013', 통화기호: 'kr', 적용환율: 150.1727, 실시간환율: 150.1727, 평균환율: 150.1727 },
        { 통화코드: '058014', 통화기호: 'B$', 적용환율: 1091.7031, 실시간환율: 1091.7031, 평균환율: 1091.7031 },
        { 통화코드: '058015', 통화기호: '元', 적용환율: 197.2387, 실시간환율: 197.2387, 평균환율: 197.2387 },
        { 통화코드: '058016', 통화기호: 'HK$', 적용환율: 180.93, 실시간환율: 180.93, 평균환율: 180.93 },
        { 통화코드: '058017', 통화기호: 'S$', 적용환율: 1091.7031, 실시간환율: 1091.7031, 평균환율: 1091.7031 },
        { 통화코드: '058018', 통화기호: 'DH', 적용환율: 383.2886, 실시간환율: 383.2886, 평균환율: 383.2886 },
        { 통화코드: '058019', 통화기호: 'Fr', 적용환율: 1769.9115, 실시간환율: 1769.9115, 평균환율: 1769.9115 },
        { 통화코드: '058020', 통화기호: 'KD', 적용환율: 4608.2949, 실시간환율: 4608.2949, 평균환율: 4608.2949 },
        { 통화코드: '058021', 통화기호: '£', 적용환율: 1897.5332, 실시간환율: 1897.5332, 평균환율: 1897.5332 },
        { 통화코드: '058022', 통화기호: 'RM', 적용환율: 334.4482, 실시간환율: 334.4482, 평균환율: 334.4482 },
        { 통화코드: '058023', 통화기호: 'CA$', 적용환율: 1008.0645, 실시간환율: 1008.0645, 평균환율: 1008.0645 },
        { 통화코드: '058024', 통화기호: 'Rp', 적용환율: 0.085, 실시간환율: 0.085, 평균환율: 0.085 },
        { 통화코드: '058025', 통화기호: 'SR', 적용환율: 375.3754, 실시간환율: 375.3754, 평균환율: 375.3754 },
        { 통화코드: '058026', 통화기호: '₱', 적용환율: 24.309, 실시간환율: 24.309, 평균환율: 24.309 },
      ],
      통화코드: '058001',
      통화기호: '￦',
      원가구분: '2',
      총원가: [
        { 구분: '총원가', 통화코드: '058001', 통화기호: 'KRW', 적용환율: '1', 성인: 0, 어린이: 0, 영유아: 0 },
      ],
      세부원가: [
        { 원가항목: '항공료', 원가항목코드: '', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '', 환율적용방식코드: '', 적용환율: '1', 성인: 0, 어린이: 0, 영유아: 0, 원가비고: '' },
        { 원가항목: '유류&TAX', 원가항목코드: '', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '', 환율적용방식코드: '', 적용환율: '1', 성인: 0, 어린이: 0, 영유아: 0, 원가비고: '' },
        { 원가항목: '지상비', 원가항목코드: '', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '', 환율적용방식코드: '', 적용환율: '1', 성인: 0, 어린이: 0, 영유아: 0, 원가비고: '' },
        { 원가항목: '센딩', 원가항목코드: '', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '', 환율적용방식코드: '', 적용환율: '1', 성인: 0, 어린이: 0, 영유아: 0, 원가비고: '' },
        { 원가항목: '보험', 원가항목코드: '', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '', 환율적용방식코드: '', 적용환율: '1', 성인: 0, 어린이: 0, 영유아: 0, 원가비고: '' },
      ],
      공용원가: [
        { 원가항목: '인솔자비용', 원가항목코드: '', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '', 환율적용방식코드: '', 적용환율: '1', 성인: 0, 어린이: 0, 영유아: 0, 원가비고: '' },
        { 원가항목: '가이드비용', 원가항목코드: '', 통화기호: 'KRW', 통화코드: '058001', 환율적용방식: '', 환율적용방식코드: '', 적용환율: '1', 성인: 0, 어린이: 0, 영유아: 0, 원가비고: '' },
      ],
      수익계산: [
        { 항목: '인원수', 번호: 1, 통화코드: '058001', 통화기호: '￦', 성인: Number(form?.성인수 ?? 1), 어린이: Number(form?.소아수 ?? 1), 영유아: Number(form?.유아수 ?? 1) },
        { 항목: '원가', 번호: 2, 통화코드: '058001', 통화기호: '￦', 성인: 0, 어린이: 0, 영유아: 0 },
        { 항목: '판매가', 번호: 3, 통화코드: '058001', 통화기호: '￦', 성인: 0, 어린이: 0, 영유아: 0 },
        { 항목: '예상수익', 번호: 4, 통화코드: '058001', 통화기호: '￦', 성인: 0, 어린이: 0, 영유아: 0 },
      ],
      상담입출금: [
        {
          상담번호: 0,
          일시: new Date().toISOString(),
          구분: '입금',
          입출대상: '',
          내역: '',
          금액: 0,
        },
      ],
      고객번호: 0,
      일정번호: 0,
    };

    const payload = {
      Table1: [
        { 컬럼명: '@상담정보', 값: JSON.stringify(상담정보) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@예상수익', 값: 0 },
        { 컬럼명: '@성인원가', 값: 0 },
        { 컬럼명: '@소아원가', 값: 0 },
        { 컬럼명: '@유아원가', 값: 0 },
        { 컬럼명: '@견적경로', 값: '' },
        { 컬럼명: '@입금총액', 값: 0 },
        { 컬럼명: '@출금총액', 값: 0 },
        { 컬럼명: '@총수익', 값: 0 },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.ITN_T_%EC%83%81%EB%8B%B4%EC%A0%80%EC%9E%A5`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get hotel room product list (호텔룸상품리스트)
// SP: DBO.TRP_S_%ED%98%B8%ED%85%94%EB%A3%B8%EC%83%81%ED%92%88%EB%A6%AC%EC%8A%A4%ED%8A%B8
// Params: { productNo }
export const getHotelRoomList = async ({ productNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@상품번호', 값: String(productNo) },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%98%B8%ED%85%94%EB%A3%B8%EC%83%81%ED%92%88%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    
    // Parse the JSON string in the response
    const tableData = data?.table?.[0] || data?.Table?.[0];
    if (tableData?.정보) {
      try {
        const parsedInfo = JSON.parse(tableData.정보);
        return parsedInfo;
      } catch (e) {
        console.error('Failed to parse room list:', e);
        return [];
      }
    }
    
    return [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete hotel resource (호텔자원 삭제)
// SP: DBO.TRP_T_%ED%98%B8%ED%85%94%EC%9E%90%EC%9B%90_%EC%82%AD%EC%A0%9C
// Params: { productNo, resourceNo }
export const deleteHotelResource = async ({ productNo, resourceNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@자원번호', 값: Number(resourceNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94%EC%9E%90%EC%9B%90_%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create hotel product resource (호텔상품자원생성)
// SP: DBO.TRP_T_%ED%98%B8%ED%85%94%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EC%83%9D%EC%84%B1
// Params: { productNo, resourceName, resourceNo }
export const createHotelProductResource = async ({ productNo, resourceName, resourceNo = null }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    
    const payload = {
      Table1: [
        { 컬럼명: '@자원명', 값: String(resourceName) },
        { 컬럼명: '@상품번호', 값: String(productNo) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@유저번호', 값: String(userNo) },
        { 컬럼명: '@자원번호', 값: resourceNo === null ? 'NULL' : String(resourceNo) },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94%EC%83%81%ED%92%88%EC%9E%90%EC%9B%90%EC%83%9D%EC%84%B1`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save room type (객실타입 저장)
// SP: DBO.TRP_T_%EA%B0%9D%EC%8B%A4%ED%83%80%EC%9E%85
// Params: { productNo, roomData }
export const saveRoomType = async ({ productNo, roomData }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    
    // 1. 먼저 자원번호 획득 (신규 생성 또는 기존 자원번호 사용)
    let resourceNo = roomData.자원번호;
    
    if (!resourceNo) {
      // 신규 객실인 경우 자원번호 생성
      const resourceResult = await createHotelProductResource({
        productNo: productNo,
        resourceName: roomData.객실명,
        resourceNo: null
      });
      
      // 응답에서 자원번호 추출
      const resourceData = resourceResult?.table?.[0] || resourceResult?.Table?.[0];
      resourceNo = resourceData?.자원번호;
      
      if (!resourceNo) {
        throw new Error('자원번호 생성에 실패했습니다.');
      }
}
    
    // 2. 자원번호를 사용하여 객실타입 저장
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: String(productNo) },
        { 컬럼명: '@자원번호', 값: Number(resourceNo) },
        { 컬럼명: '@자원한글명', 값: String(roomData.객실명 || '') },
        { 컬럼명: '@자원영문명', 값: String(roomData.영문명 || '') },
        { 컬럼명: '@기준인원', 값: Number(roomData.기준인원 || 2) },
        { 컬럼명: '@최대인원', 값: String(roomData.최대인원 || 2) },
        { 컬럼명: '@성인최대인원', 값: Number(roomData.최대인원 || roomData.기준인원 || 2) },
        { 컬럼명: '@최대아동수', 값: Math.max(0, (Number(roomData.최대인원 || 0) - Number(roomData.기준인원 || 0))) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@키워드', 값: String(roomData.어메니티 || '') },
        { 컬럼명: '@추가인원불가', 값: !roomData.추가비용부과 },
        { 컬럼명: '@공급자번호', 값: String(companyNo) },
        { 컬럼명: '@성인요금', 값: Number(roomData.성인추가금 || 0) },
        { 컬럼명: '@소아요금', 값: Number(roomData.아동추가금 || 0) },
        { 컬럼명: '@유아요금', 값: 0 },
        { 컬럼명: '@성인원가', 값: Number(roomData.성인추가금 || 0) },
        { 컬럼명: '@소아원가', 값: Number(roomData.아동추가금 || 0) },
        { 컬럼명: '@유아원가', 값: 0 },
        { 컬럼명: '@무료성인', 값: Number(roomData.기준인원 || 0) },
        { 컬럼명: '@무료인원', 값: Number(0) },
        { 컬럼명: '@기준인원초과사용여부', 값: !!roomData.추가비용부과 },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EA%B0%9D%EC%8B%A4%ED%83%80%EC%9E%85`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    
    // 저장된 자원번호를 포함하여 반환
    return { ...data, 자원번호: resourceNo };
  } catch (error) {
    return handleApiError(error);
  }
};

// Get hotel room product detail by price option number (호텔객실상품_가격옵션번호)
// SP: DBO.TRP_S_%ED%98%B8%ED%85%94%EA%B0%9D%EC%8B%A4%EC%83%81%ED%92%88_%EA%B0%80%EA%B2%A9%EC%98%B5%EC%85%98%EB%B2%88%ED%98%B8
// Params: { priceOptionNo }
export const getHotelRoomProductDetail = async ({ priceOptionNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@가격옵션번호', 값: String(priceOptionNo), 리스트값: null },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%98%B8%ED%85%94%EA%B0%9D%EC%8B%A4%EC%83%81%ED%92%88_%EA%B0%80%EA%B2%A9%EC%98%B5%EC%85%98%EB%B2%88%ED%98%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save hotel room product and included services (호텔객실상품및포함서비스)
// SP: DBO.TRP_T_%ED%98%B8%ED%85%94%EA%B0%9D%EC%8B%A4%EC%83%81%ED%92%88%EB%B0%8F%ED%8F%AC%ED%95%A8%EC%84%9C%EB%B9%84%EC%8A%A4_100+
// Params: { productNo, resourceNo, priceOptionNo, priceOptionName, refundable, confirmationType, supplierNo, productData }
export const saveHotelRoomProduct = async ({ 
  productNo, 
  resourceNo, 
  priceOptionNo, 
  priceOptionName, 
  refundable, 
  confirmationType, 
  supplierNo,
  includedServices 
}) => {
  try {
    const { userNo } = getUserInfo() || {};
    
    // 포함서비스를 JSON 문자열로 변환
    const servicesJson = JSON.stringify(includedServices.map(service => ({
      서비스번호: service.서비스번호 || 0,
      서비스명: service.서비스명 || '',
      무료대상: service.무료대상 || '기준인원',
      무료성인수: service.무료성인수 || 0,
      무료아동수: service.무료아동수 || 0,
      성인추가비용: service.성인원가 || 0,
      아동추가비용: service.아동원가 || 0,
      마크업요율: service.마크업요율 || 0,
      환산환율: service.환산환율 || 1,
      성인판매가추가비용: service.성인판매가 || 0,
      아동판매가추가비용: service.아동판매가 || 0,
      성인추가비용판매가환산: service.성인판매가 || 0,
      아동추가비용판매가환산: service.아동판매가 || 0
    })));
    
    const payload = {
      Table1: [
        { 컬럼명: '@상품번호', 값: String(productNo) },
        { 컬럼명: '@자원번호', 값: String(resourceNo) },
        { 컬럼명: '@가격옵션번호', 값: priceOptionNo ? String(priceOptionNo) : '0' },
        { 컬럼명: '@가격옵션명', 값: priceOptionName },
        { 컬럼명: '@환불불가', 값: !refundable },
        { 컬럼명: '@확정처리방식', 값: confirmationType },
        { 컬럼명: '@공급자번호', 값: String(supplierNo) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@선택포함서비스', 값: servicesJson }
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94%EA%B0%9D%EC%8B%A4%EC%83%81%ED%92%88%EB%B0%8F%ED%8F%AC%ED%95%A8%EC%84%9C%EB%B9%84%EC%8A%A4_100+`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Create default hotel room product (호텔객실상품_디폴트생성)
// SP: DBO.TRP_T_%ED%98%B8%ED%85%94%EA%B0%9D%EC%8B%A4%EC%83%81%ED%92%88_%EB%94%94%ED%8F%B4%ED%8A%B8%EC%83%9D%EC%84%B1
// Params: { productNo, resourceNo, productName }
export const createDefaultHotelRoomProduct = async ({ productNo, resourceNo, productName }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@객실상품명', 값: productName, 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
        { 컬럼명: '@자원번호', 값: String(resourceNo), 리스트값: null }
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94%EA%B0%9D%EC%8B%A4%EC%83%81%ED%92%88_%EB%94%94%ED%8F%B4%ED%8A%B8%EC%83%9D%EC%84%B1`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Delete hotel room product (호텔객실상품_삭제)
// SP: DBO.TRP_T_%ED%98%B8%ED%85%94%EA%B0%9D%EC%8B%A4%EC%83%81%ED%92%88_%EC%82%AD%EC%A0%9C
// Params: { productNo, priceOptionNo }
export const deleteHotelRoomProduct = async ({ productNo, priceOptionNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@가격옵션번호', 값: String(priceOptionNo), 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null }
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94%EA%B0%9D%EC%8B%A4%EC%83%81%ED%92%88_%EC%82%AD%EC%A0%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get supply contract pricing table number (공급계약요금표 번호 조회)
// SP: DBO.TRP_S_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EC%9A%94%EA%B8%88%ED%91%9C
// payload Table1: @공급계약번호
// Returns: 요금표번호, 요금표명, etc.
export const getSupplyContractPricingTableNo = async ({ contractNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@공급계약번호', 값: Number(contractNo) }
      ]
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EC%9A%94%EA%B8%88%ED%91%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const table = Array.isArray(data?.table) ? data.table : [];
    return table.length > 0 ? table[0] : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get supply contract pricing table information (공급계약요금표 정보 조회)
// SP: DBO.TRP_S_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EC%9A%94%EA%B8%88%ED%91%9C%EC%A0%95%EB%B3%B4
// payload Table1: @요금표번호
// Returns: 요금표, 설정데이터, 컬럼순서
export const getSupplyContractPricingInfo = async ({ pricingTableNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@요금표번호', 값: Number(pricingTableNo) }
      ]
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EC%9A%94%EA%B8%88%ED%91%9C%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const table = Array.isArray(data?.table) ? data.table : [];
    return table.length > 0 ? table[0] : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Save hotel pricing table data (호텔 요금표 저장)
// SP: DBO.TRP_T_%ED%98%B8%ED%85%94_%EC%9A%94%EA%B8%88%ED%91%9C%EC%A0%80%EC%9E%A5
// payload Table1: @업체번호, @상품번호, @공급계약번호, @요금표데이터, @처리자, @파일명, @파일번호, @파일구분, @설정데이터
export const saveHotelPricingTable = async ({ companyNo, productNo, contractNo, pricingTableData, settingsData, pricingTableNo, fileName = '1', fileType = '2' }) => {
  try {
    // 매개변수 유효성 검사
if (!companyNo) throw new Error('companyNo가 없습니다');
    if (!productNo) throw new Error('productNo가 없습니다');
    if (!contractNo && contractNo !== 0) throw new Error('contractNo가 없습니다');
    if (!pricingTableData) throw new Error('pricingTableData가 없습니다');
    
    const { userNo } = getUserInfo() || {};
    if (!userNo) throw new Error('사용자 정보를 찾을 수 없습니다');
    
    // 데이터 준비
    const pricingTableDataStr = typeof pricingTableData === 'string' ? 
      pricingTableData : JSON.stringify(pricingTableData);
    
    const settingsDataStr = typeof settingsData === 'string' ? 
      settingsData : JSON.stringify(settingsData);
    
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@상품번호', 값: String(productNo) },
        { 컬럼명: '@공급계약번호', 값: Number(contractNo) },
        { 컬럼명: '@요금표데이터', 값: pricingTableDataStr },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@파일명', 값: String(fileName) },
        // 신규 생성 시 파일번호는 'NULL' 문자열이어야 함
        { 컬럼명: '@파일번호', 값: (pricingTableNo ? Number(pricingTableNo) : 'NULL') },
        { 컬럼명: '@파일구분', 값: String(fileType) },
        { 컬럼명: '@설정데이터', 값: settingsDataStr }
      ]
    };
const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94_%EC%9A%94%EA%B8%88%ED%91%9C%EC%A0%80%EC%9E%A5`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status}, ${errorText}`);
      }
      
      const data = await response.json();
return data;
    } catch (fetchError) {
      console.error('🔴 Fetch 오류:', fetchError);
      throw fetchError;
    }
  } catch (error) {
    console.error('🔴 saveHotelPricingTable 오류:', error);
    return handleApiError(error);
  }
};

export const getUserCompanyInfo = async () => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: Number(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%9C%A0%EC%A0%80%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const table = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return table.length > 0 ? table[0] : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get company primary domain (업체 대표도메인 조회)
// SP: DBO.EZIST_S_업체대표도메인
// payload Table1: @업체번호
// Returns: string like "tripbox.co.kr" or null
export const getCompanyPrimaryDomain = async ({ companyNo } = {}) => {
  try {
    const { companyNo: tokenCompanyNo } = getUserInfo() || {};
    const effectiveCompanyNo = companyNo ?? tokenCompanyNo;
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: Number(effectiveCompanyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.EZIST_S_%EC%97%85%EC%B2%B4%EB%8C%80%ED%91%9C%EB%8F%84%EB%A9%94%EC%9D%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const table = Array.isArray(data?.table) ? data.table : [];
    const domain = table.length > 0 ? (table[0]?.대표도메인 ?? null) : null;
    return domain || null;
  } catch (error) {
    return handleApiError(error);
  }
};
export const getSaleCustomerRequestItems = async ({ saleNo }) => {
  try {
    if (!saleNo) throw new Error('판매번호가 없습니다.');
    const payload = {
      Table1: [
        { 컬럼명: '@판매번호', 값: Number(saleNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%ED%8C%90%EB%A7%A4_%EA%B3%A0%EA%B0%9D%EC%9A%94%EC%B2%AD%EC%82%AC%ED%95%AD`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get disbursement history (출납관리)
// SP: DBO.CW_S_%EC%B6%9C%EB%82%A9%EA%B4%80%EB%A6%AC
// payload Table1: @공급업체번호, @유저번호, @시작일, @종료일
export const getDisbursementHistory = async ({ startDate, endDate } = {}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo ?? ''), 리스트값: null },
        { 컬럼명: '@시작일', 값: startDate || '', 리스트값: null },
        { 컬럼명: '@종료일', 값: endDate || '', 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%B6%9C%EB%82%A9%EA%B4%80%EB%A6%AC`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// Get receipt history (수납관리)
// SP: DBO.CW_S_%EC%88%98%EB%82%A9%EA%B4%80%EB%A6%AC
// payload Table1: @공급업체번호, @유저번호, @시작일, @종료일
export const getReceiptHistory = async ({ startDate, endDate } = {}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo ?? ''), 리스트값: null },
        { 컬럼명: '@시작일', 값: startDate || '', 리스트값: null },
        { 컬럼명: '@종료일', 값: endDate || '', 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%88%98%EB%82%A9%EA%B4%80%EB%A6%AC`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.table) ? data.table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

// 예치금 조회 (SP: DBO.RCT_S_예치금)
// table: 기간 내 입출금 내역, table1: 잔액·출금가능액·가상계좌 요약
// 첫 결과셋이 0건이면 API가 table 대신 empt0(전부 null인 한 줄)로 돌려주므로 빈 배열로 처리됨
export const getDepositHistory = async ({ startDate, endDate } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo ?? ''), 리스트값: null },
        { 컬럼명: '@거래시작일', 값: startDate || '', 리스트값: null },
        { 컬럼명: '@거래종료일', 값: endDate || '', 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${encodeURIComponent('DBO.RCT_S_예치금')}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const history = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const summaryRows = Array.isArray(data?.table1) ? data.table1 : Array.isArray(data?.Table1) ? data.Table1 : [];
    return { history, summary: summaryRows[0] || null };
  } catch (error) {
    return handleApiError(error);
  }
};

// Create Marit reservation batch (마리트 예약 일괄 생성)
// 예약일별로 그룹핑된 데이터를 받아서 각각 미등록상품예약으로 생성
export const createMaritReservationBatch = async (reservationGroups, options = {}) => {
  const results = [];
  const errors = [];
  const { onProgress, checkpoints } = options || {};

  for (let i = 0; i < reservationGroups.length; i++) {
    const group = reservationGroups[i];
    const cp = checkpoints && (checkpoints[i] || checkpoints[group?.reservationDate] || null);
    const completedSteps = (cp && cp.completedSteps) ? { ...cp.completedSteps } : { step1: false, step1b: false, step2: false, step3: false };
    
    try {
if (typeof onProgress === 'function') {
        try { onProgress({ groupIndex: i, status: 'init', step: 'init', productName: group?.productName }); } catch (_) {}
      }
      
      // 1. 주문 생성 (첫 번째 여행객 정보를 주문자로 사용)
      const firstTraveler = group.travelers[0];
      
      // 날짜 형식을 YYYY-MM-DD로 변환
      const formatDateForAPI = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const formattedStartDate = formatDateForAPI(group.eventStartDate);
let orderNo = cp?.orderNo || null;
      if (!orderNo && !completedSteps.step1) {
        let existingOrder = null;
        try {
          const list = await getOrderStatusList({ startDate: formattedStartDate, endDate: formattedStartDate });
          if (Array.isArray(list)) {
            const codeMatch = (s) => {
              const m = String(s || '').match(/\((\d+)\)/);
              return m ? m[1] : null;
            };
            const excelCode = codeMatch(group.productName);
            const codeKeys = ['상품코드','판매상품코드','ProductCode','productCode'];
            const nameKeys = ['판매상품명','상품명','ProductName','productName'];
            const dateKeys = ['행사시작일','출발일','StartDate','startDate'];
            const normalizeName = (nm) => String(nm || '').replace(/\s*-\s*\d{4}-\d{2}-\d{2}$/,'').trim();
            const excelName = normalizeName(group.productName);
            // First, try strict product code match (most reliable)
            if (excelCode) {
              const byCode = list.find((row) => codeKeys.some((k) => String(row?.[k] ?? '') === String(excelCode)) && dateKeys.some((dk) => String(row?.[dk] ?? '').startsWith(formattedStartDate)) || codeKeys.some((k) => String(row?.[k] ?? '') === String(excelCode)));
              if (byCode) existingOrder = byCode;
            }
            // Fallback: normalized product name match (strip trailing date suffix)
            if (!existingOrder) {
              existingOrder = list.find((row) => {
                const rowName = nameKeys.map((k) => normalizeName(row?.[k])).find((v) => !!v);
                return rowName && rowName === excelName;
              });
            }
          }
        } catch (_) {}

        if (existingOrder && (existingOrder.주문번호 != null || existingOrder.orderNo != null || existingOrder.OrderNo != null)) {
          orderNo = existingOrder.주문번호 ?? existingOrder.orderNo ?? existingOrder.OrderNo;
completedSteps.step1 = true;
          if (typeof onProgress === 'function') onProgress({ groupIndex: i, step: 'step1', status: 'success', orderNo });
        } else {
          const orderResult = await createUnregisteredTourReservation({
            productName: group.productName,
            durationDays: calculateDurationDays(group.eventStartDate),
            startDate: formattedStartDate,
            reserverName: firstTraveler.name,
            phone: firstTraveler.phone
          });

          if (!orderResult?.table?.[0]?.주문번호) {
            throw Object.assign(new Error('주문 생성 실패: 주문번호를 받지 못했습니다.'), { step: 'step1' });
          }

          orderNo = orderResult.table[0].주문번호;
          completedSteps.step1 = true;
          if (typeof onProgress === 'function') onProgress({ groupIndex: i, step: 'step1', status: 'success', orderNo });
        }
      } else {
}

      if (!completedSteps.step1b) {
        try {
          await updateOrdererInfo({
            orderNo,
            ordererName: firstTraveler?.name || '',
            ordererPhone: firstTraveler?.phone || '',
            ordererEmail: firstTraveler?.email || ''
          });
completedSteps.step1b = true;
          if (typeof onProgress === 'function') onProgress({ groupIndex: i, step: 'step1b', status: 'success', orderNo });
        } catch (e) {
          console.warn('주문자 정보 이메일 업데이트 실패(무시 가능):', e?.message || e);
          // 이메일 업데이트 실패는 치명적이지 않으므로 계속 진행, 하지만 체크포인트에 반영
        }
      } else {
}

      // 2. 여행객 정보 업데이트 (모든 여행객 정보 반영)
      if (group.travelers && group.travelers.length > 0) {
        // 주문번호로 판매번호 조회
let saleNo = cp?.saleNo || null;
        if (!saleNo) {
          const orderSaleInfo = await getOrderSaleStatus({ orderNo });
const saleNoCandidate = orderSaleInfo?.판매번호 ?? orderSaleInfo?.주문판매번호 ?? orderSaleInfo?.saleNo ?? orderSaleInfo?.SaleNo ?? null;
          if (!orderSaleInfo || !saleNoCandidate) {
            const err = new Error(`주문번호 ${orderNo}에 대한 판매번호를 찾을 수 없습니다.`);
            err.step = 'step2';
            throw err;
          }
          
          saleNo = saleNoCandidate;
        }
        
        // 여행객 필드 정보 구성
        const travelerFieldRows = [];
        const travelerAmounts = [];
        const travelerOrders = [];

        group.travelers.forEach((traveler, index) => {
          const travelerNo = index + 1;
// 필드 정보 (이름, 연락처, 이메일, 비고)
          travelerFieldRows.push(
            { 여행객번호: travelerNo, 필드번호: 8, 값: traveler.name }, // 한글이름
            { 여행객번호: travelerNo, 필드번호: 12, 값: traveler.phone }, // 휴대폰
            { 여행객번호: travelerNo, 필드번호: 256, 값: traveler.email }, // 이메일
            { 여행객번호: travelerNo, 필드번호: 314, 값: traveler.memo } // 비고사항
          );
          
          // 금액 정보
          travelerAmounts.push({
            여행객번호: travelerNo,
            상품금액: traveler.amount,
            공급단가: traveler.amount,
            택스및유류: traveler.discount
          });
          
          // 순번 정보
          travelerOrders.push({
            여행객번호: travelerNo,
            여행객종류: '여행객', // 기본값
            선발권동의: false,
            명단확인: false,
            순번: index + 1
          });
        });
try {
          // 2단계: 여행객 수 지정 (CW_T_예약관리_여행객변경)
          // 각 여행객에 대해 순차적인 번호로 필수 필드들 생성
          const minimalTravelerRows = [];
          group.travelers.forEach((traveler, index) => {
            // 기존 예약관리 로직과 동일하게 순차적인 번호 사용 (1, 2, 3, ...)
            const travelerNo = index + 1;
            
            // 각 여행객마다 필수 필드들 추가
            minimalTravelerRows.push(
              { 여행객번호: travelerNo, 필드번호: 8, 필드값: '', 연령: '성인' }, // 한글이름 (빈값으로 시작)
              { 여행객번호: travelerNo, 필드번호: 109, 필드값: '남성', 연령: '성인' }, // 성별 (기본값)
              { 여행객번호: travelerNo, 필드번호: 9, 필드값: '', 연령: '성인' }, // 연락처 (빈값으로 시작)
              { 여행객번호: travelerNo, 필드번호: 10, 필드값: '', 연령: '성인' } // 이메일 (빈값으로 시작)
            );
          });
let travelerCountResult = null;
          if (!completedSteps.step2) {
travelerCountResult = await saveReservationTravelersAfterDelete({
              saleNo,
              minimalTravelerRows,
              adultCount: group.travelers.length,
              ageArray: ''
            });
completedSteps.step2 = true;
            if (typeof onProgress === 'function') onProgress({ groupIndex: i, step: 'step2', status: 'success', orderNo, saleNo });
          } else {
}

          // 2단계에서 생성된 실제 여행객번호들 추출
let actualTravelerNumbers = [];
          if (travelerCountResult?.table && Array.isArray(travelerCountResult.table)) {
            travelerCountResult.table.forEach((row, index) => {
              if (row.여행객번호 && !actualTravelerNumbers.includes(row.여행객번호)) {
                actualTravelerNumbers.push(row.여행객번호);
              }
            });
          }
          
          // 항상 최신 여행객번호를 조회 API로 보완하여 실제 ID를 확보
          try {
            const list = await getReservationTravelerListBySaleNo({ saleNo });
            const sorted = (Array.isArray(list) ? list : []).sort((a, b) => {
              const aa = Number.isFinite(Number(a?.순번)) ? Number(a?.순번) : 999999;
              const bb = Number.isFinite(Number(b?.순번)) ? Number(b?.순번) : 999999;
              return aa - bb;
            });
            const refreshed = [];
            sorted.forEach((row) => {
              const no = Number(row?.여행객번호 || row?.여행객키);
              if (Number.isFinite(no) && no > 0 && !refreshed.includes(no)) refreshed.push(no);
            });
            if (refreshed.length > 0) actualTravelerNumbers = refreshed;
          } catch (e) {
            console.warn('여행객 목록 최신화 실패(무시 가능):', e?.message || e);
          }
          
          // 최종적으로도 부족하면 순차번호로 보완 (최후의 수단)
          if (actualTravelerNumbers.length < group.travelers.length) {
            console.warn('⚠️ 여행객번호가 부족하여 순차번호로 보완합니다.');
            while (actualTravelerNumbers.length < group.travelers.length) {
              actualTravelerNumbers.push(actualTravelerNumbers.length + 1);
            }
          }
          
          // 여행객번호가 충분하지 않으면 에러
          if (actualTravelerNumbers.length < group.travelers.length) {
            throw new Error(`생성된 여행객번호 수(${actualTravelerNumbers.length})가 필요한 여행객 수(${group.travelers.length})보다 적습니다.`);
          }

          // 3단계: 여행객 상세 정보 입력 (TRP_T_예약관리_판매상태및여행객수정_택스포함_14)
// 실제 여행객번호를 사용하여 3단계 데이터 재구성
          const actualTravelerFieldRows = [];
          const actualTravelerAmounts = [];
          const actualTravelerOrders = [];

          group.travelers.forEach((traveler, index) => {
            const actualTravelerNo = actualTravelerNumbers[index];
// 필드 정보 (정상 페이로드와 동일한 구조로 구성)
            actualTravelerFieldRows.push(
              { 여행객번호: actualTravelerNo, 필드번호: 8, 값: traveler.name ?? null }, // 한글이름
              { 여행객번호: actualTravelerNo, 필드번호: -1, 값: null },
              { 여행객번호: actualTravelerNo, 필드번호: 109, 값: traveler.gender ?? '남성' }, // 성별
              { 여행객번호: actualTravelerNo, 필드번호: 9, 값: '' }, // 영문성 (없음)
              { 여행객번호: actualTravelerNo, 필드번호: 10, 값: '' }, // 영문이름 (없음)
              { 여행객번호: actualTravelerNo, 필드번호: 11, 값: null }, // 생년월일
              { 여행객번호: actualTravelerNo, 필드번호: 13, 값: null },
              { 여행객번호: actualTravelerNo, 필드번호: 253, 값: null }, // 여권번호
              { 여행객번호: actualTravelerNo, 필드번호: 254, 값: null }, // 여권만료일
              { 여행객번호: actualTravelerNo, 필드번호: 314, 값: traveler.memo ?? null }, // 비고사항
              { 여행객번호: actualTravelerNo, 필드번호: 12, 값: traveler.phone ?? null }, // 휴대폰  
              { 여행객번호: actualTravelerNo, 필드번호: 256, 값: traveler.email ?? null }, // 이메일
              { 여행객번호: actualTravelerNo, 필드번호: 404, 값: null }, // 주민등록번호
              { 여행객번호: actualTravelerNo, 필드번호: 255, 값: null }
            );
            
            // 금액 정보
            actualTravelerAmounts.push({
              여행객번호: actualTravelerNo,
              상품금액: Number(traveler.amount) || 0,
              공급단가: Number(traveler.amount) || 0,
              택스및유류: Number(traveler.discount) || 0
            });
            
            // 순번 정보 (여행객종류를 "여행객"으로 수정)
            actualTravelerOrders.push({
              여행객번호: actualTravelerNo,
              여행객종류: '여행객', // "성인" → "여행객"으로 수정
              선발권동의: false,
              명단확인: false,
              순번: index + 1
            });
          });
if (!completedSteps.step3) {
            const travelerResult = await saveReservationTravelerChanges({
              saleNo,
              travelerFieldRows: actualTravelerFieldRows,
              travelerAmounts: actualTravelerAmounts,
              travelerOrders: actualTravelerOrders
            });
            completedSteps.step3 = true;
            if (typeof onProgress === 'function') onProgress({ groupIndex: i, step: 'step3', status: 'success', orderNo, saleNo });
          } else {
if (typeof onProgress === 'function') onProgress({ groupIndex: i, step: 'step3', status: 'success', orderNo, saleNo });
          }
          
        } catch (travelerError) {
          console.error('❌ 여행객 정보 업데이트 실패:', travelerError);
          throw new Error(`여행객 정보 업데이트 실패: ${travelerError.message}`);
        }
      } else {
        console.warn('⚠️ 여행객 정보가 없어서 2단계, 3단계를 건너뜁니다.');
        console.warn('여행객 배열:', group.travelers);
        if (typeof onProgress === 'function') onProgress({ groupIndex: i, step: 'step3', status: 'success', orderNo });
      }

      results.push({
        success: true,
        orderNo,
        reservationDate: group.reservationDate,
        travelerCount: group.travelers.length,
        totalAmount: group.totalAmount,
        steps: { ...completedSteps },
        checkpoint: { orderNo, saleNo: undefined, completedSteps: { ...completedSteps } }
      });

    } catch (error) {
      console.error(`Error processing group ${i + 1}:`, error);
      const failedStep = error?.step || null;
      const checkpoint = {
        orderNo: (typeof orderNo !== 'undefined' ? orderNo : (checkpoints && checkpoints[i]?.orderNo) || null),
        saleNo: (typeof saleNo !== 'undefined' ? saleNo : (checkpoints && checkpoints[i]?.saleNo) || null),
        completedSteps: { ...completedSteps },
        nextStep: failedStep || (completedSteps.step1 ? (!completedSteps.step1b ? 'step1b' : (!completedSteps.step2 ? 'step2' : 'step3')) : 'step1')
      };
      errors.push({
        groupIndex: i,
        reservationDate: group.reservationDate,
        error: error.message,
        step: failedStep,
        checkpoint
      });
      if (typeof onProgress === 'function') onProgress({ groupIndex: i, step: failedStep || 'unknown', status: 'error', message: error.message, checkpoint });
    }
  }

  return {
    success: errors.length === 0,
    results,
    errors,
    summary: {
      total: reservationGroups.length,
      succeeded: results.length,
      failed: errors.length
    }
  };
};

// 소요일수 계산 헬퍼 함수
const calculateDurationDays = (startDate) => {
  // 기본값 1일, 실제로는 여행시작일과 종료일 차이를 계산해야 함
  return 1;
};

// Create Marit reservation batch for registered products (등록된 상품에 마리트 예약 추가)
// 엑셀의 상품번호와 등록된 투어상품의 상품코드를 매칭하여 예약 생성
export const createMaritReservationForRegisteredProducts = async (reservationGroups, options = {}) => {
  const results = [];
  const errors = [];
  const { onProgress } = options || {};
  const { companyNo, userNo } = getUserInfo() || {};

  if (!companyNo || !userNo) {
    throw new Error('사용자 정보를 찾을 수 없습니다.');
  }

  // 1. 투어상품 리스트 조회
  let tourProducts = [];
  try {
    const productsData = await getTourProductListForCopy();
    tourProducts = Array.isArray(productsData) ? productsData : [];
} catch (error) {
    throw new Error(`투어상품 리스트 조회 실패: ${error.message}`);
  }

  // 2. 각 예약 그룹 처리
  for (let i = 0; i < reservationGroups.length; i++) {
    const group = reservationGroups[i];
    
    try {
// 엑셀에서 파싱한 상품번호 추출 (상품명에서 괄호 안의 번호)
      const productCodeMatch = group.productName?.match(/\((\d+)\)/);
      const excelProductCode = productCodeMatch ? productCodeMatch[1] : null;
      
      if (!excelProductCode) {
        throw new Error('엑셀에서 상품번호를 찾을 수 없습니다.');
      }
// 상품코드로 매칭되는 투어상품 찾기
      const matchedProduct = tourProducts.find(p => String(p.상품코드) === String(excelProductCode));
      
      if (!matchedProduct) {
        throw new Error(`상품코드 ${excelProductCode}에 해당하는 등록된 상품을 찾을 수 없습니다.`);
      }
const productNo = matchedProduct.상품번호;
      const productName = matchedProduct.상품명;
      
      // 날짜 형식 변환
      const formatDateForAPI = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const formattedStartDate = formatDateForAPI(group.eventStartDate);
      
      if (!formattedStartDate) {
        throw new Error('행사시작일이 유효하지 않습니다.');
      }
// 기존 예약 존재 시: 신규 생성 대신 기존 예약 수정(인원 감축 포함)
      let existingOrderNo = null;
      try {
        const orderList = await getOrderStatusList({ startDate: formattedStartDate, endDate: formattedStartDate });
        const list = Array.isArray(orderList) ? orderList : [];
        const codeKeys = ['상품코드','판매상품코드','ProductCode','productCode'];
        const normalizeName = (nm) => String(nm || '').replace(/\s*-\s*\d{4}-\d{2}-\d{2}$/,'').trim();
        const nameKeys = ['판매상품명','상품명','ProductName','productName'];
        const excelName = normalizeName(group.productName);
        let found = null;
        // 우선 상품코드 일치 우선
        found = list.find((row) => codeKeys.some((k) => String(row?.[k] ?? '') === String(excelProductCode)));
        // 보조: 상품명 정규화 일치
        if (!found) {
          found = list.find((row) => {
            const rowName = nameKeys.map((k) => normalizeName(row?.[k])).find((v) => !!v);
            return rowName && rowName === excelName;
          });
        }
        if (found) {
          existingOrderNo = found.주문번호 ?? found.orderNo ?? found.OrderNo ?? null;
        }
      } catch (e) {
        try { console.warn('기존 주문 조회 실패(무시 가능):', e?.message || e); } catch {}
      }

      if (existingOrderNo) {
        // 기존 예약 수정 흐름: 인원 수를 엑셀 인원으로 재설정 후 상세 정보 저장
        let saleNo = null;
        if (typeof onProgress === 'function') {
          try { onProgress({ groupIndex: i, step: 'step1', status: 'success', orderNo: existingOrderNo }); } catch (_) {}
        }
        try {
          const statusRow = await getOrderSaleStatus({ orderNo: existingOrderNo });
          saleNo = statusRow?.판매번호 ?? statusRow?.주문판매번호 ?? statusRow?.saleNo ?? null;
          if (!saleNo) throw new Error('판매번호를 찾을 수 없습니다.');
          if (typeof onProgress === 'function') {
            try { onProgress({ groupIndex: i, step: 'step1b', status: 'success', orderNo: existingOrderNo, saleNo }); } catch (_) {}
          }
        } catch (e) {
          throw new Error(`판매번호 조회 실패: ${e?.message || e}`);
        }

        try {
          // 최소 필드로 여행객 수를 정확히 세팅 (감축 포함)
          const minimalTravelerRows = [];
          group.travelers.forEach((traveler, index) => {
            const travelerNo = index + 1;
            minimalTravelerRows.push(
              { 여행객번호: travelerNo, 필드번호: 8,   필드값: '',   연령: '성인' },
              { 여행객번호: travelerNo, 필드번호: 109, 필드값: '남성', 연령: '성인' },
              { 여행객번호: travelerNo, 필드번호: 9,   필드값: '',   연령: '성인' },
              { 여행객번호: travelerNo, 필드번호: 10,  필드값: '',   연령: '성인' }
            );
          });

          await saveReservationTravelersAfterDelete({
            saleNo,
            minimalTravelerRows,
            adultCount: group.travelers.length,
            ageArray: ''
          });
          if (typeof onProgress === 'function') {
            try { onProgress({ groupIndex: i, step: 'step2', status: 'success', orderNo: existingOrderNo, saleNo }); } catch (_) {}
          }
        } catch (e) {
          throw new Error(`여행객 수 지정 실패: ${e?.message || e}`);
        }

        // 실제 여행객번호 재조회 후 상세 정보 저장
        try {
          let actualTravelerNumbers = [];
          try {
            const travelerList = await getReservationTravelerListBySaleNo({ saleNo });
            const sorted = (Array.isArray(travelerList) ? travelerList : []).sort((a, b) => {
              const aa = Number.isFinite(Number(a?.순번)) ? Number(a?.순번) : 999999;
              const bb = Number.isFinite(Number(b?.순번)) ? Number(b?.순번) : 999999;
              return aa - bb;
            });
            sorted.forEach((row) => {
              const no = Number(row?.여행객번호 || row?.여행객키);
              if (Number.isFinite(no) && no > 0) actualTravelerNumbers.push(no);
            });
          } catch (e) {
            console.warn('여행객 목록 조회 실패(순차번호로 대체):', e?.message || e);
            for (let j = 0; j < group.travelers.length; j++) {
              actualTravelerNumbers.push(j + 1);
            }
          }
          if (actualTravelerNumbers.length < group.travelers.length) {
            while (actualTravelerNumbers.length < group.travelers.length) {
              actualTravelerNumbers.push(actualTravelerNumbers.length + 1);
            }
          }

          const travelerFieldRows = [];
          const travelerAmounts = [];
          const travelerOrders = [];
          group.travelers.forEach((traveler, index) => {
            const actualTravelerNo = actualTravelerNumbers[index];
            travelerFieldRows.push(
              { 여행객번호: actualTravelerNo, 필드번호: 8,   값: traveler.name ?? null },
              { 여행객번호: actualTravelerNo, 필드번호: -1,  값: null },
              { 여행객번호: actualTravelerNo, 필드번호: 109, 값: traveler.gender ?? '남성' },
              { 여행객번호: actualTravelerNo, 필드번호: 9,   값: '' },
              { 여행객번호: actualTravelerNo, 필드번호: 10,  값: '' },
              { 여행객번호: actualTravelerNo, 필드번호: 11,  값: null },
              { 여행객번호: actualTravelerNo, 필드번호: 13,  값: null },
              { 여행객번호: actualTravelerNo, 필드번호: 253, 값: null },
              { 여행객번호: actualTravelerNo, 필드번호: 254, 값: null },
              { 여행객번호: actualTravelerNo, 필드번호: 314, 값: traveler.memo ?? null },
              { 여행객번호: actualTravelerNo, 필드번호: 12,  값: traveler.phone ?? null },
              { 여행객번호: actualTravelerNo, 필드번호: 256, 값: traveler.email ?? null },
              { 여행객번호: actualTravelerNo, 필드번호: 404, 값: null },
              { 여행객번호: actualTravelerNo, 필드번호: 255, 값: null }
            );
            travelerAmounts.push({
              여행객번호: actualTravelerNo,
              상품금액: Number(traveler.amount) || 0,
              공급단가: Number(traveler.amount) || 0,
              택스및유류: Number(traveler.discount) || 0
            });
            travelerOrders.push({
              여행객번호: actualTravelerNo,
              여행객종류: '여행객',
              선발권동의: false,
              명단확인: false,
              순번: index + 1
            });
          });

          await saveReservationTravelerChanges({
            saleNo,
            travelerFieldRows,
            travelerAmounts,
            travelerOrders
          });
          if (typeof onProgress === 'function') {
            try { onProgress({ groupIndex: i, step: 'step3', status: 'success', orderNo: existingOrderNo, saleNo }); } catch (_) {}
          }

          results.push({
            success: true,
            orderNo: existingOrderNo,
            saleNo,
            productNo,
            productName,
            reservationDate: group.reservationDate,
            eventStartDate: formattedStartDate,
            travelerCount: group.travelers.length,
            totalAmount: group.totalAmount
          });

          // 기존 예약 수정 완료 → 다음 그룹 처리로 이동
          continue;
        } catch (e) {
          throw new Error(`여행객 상세 저장 실패: ${e?.message || e}`);
        }
      }
      
      // 3. 예약 가능 날짜 확인 (DBO.RCT_S_가격예약_02)
      const calendarData = await getPriceStockCalendar({
        productTypeName: '투어',
        productNo,
        companyNo,
        startDate: formattedStartDate,
        endDate: formattedStartDate,
        판매여부1: true,
        판매여부2: true,
      });
if (!calendarData?.table || calendarData.table.length === 0) {
        throw new Error(`${formattedStartDate}에 예약 가능한 가격이 없습니다.`);
      }
      
      const datePrice = calendarData.table[0];
      const priceNo = datePrice.가격번호 || datePrice.대표가격번호;
      
      if (!priceNo) {
        throw new Error('가격번호를 찾을 수 없습니다.');
      }
// 4. 가격 정보 조회 (DBO.TS_S_가격번호_가격정보_투어_TRP용)
      const travelerCount = group.travelers.length;
      const priceInfoData = await getPriceInfoForReservation({
        companyNo,
        priceNo,
        nights: 1,
        adultCount: travelerCount,
        ageArray: '',
        roomCount: 1,
        productTypeName: '투어',
      });
if (!priceInfoData?.table || priceInfoData.table.length === 0) {
        throw new Error('가격 정보를 조회할 수 없습니다.');
      }
      
      const rawData = priceInfoData.table[0];
      let priceInfo = null;
      
      if (rawData?.정보) {
        try {
          const parsedInfo = JSON.parse(rawData.정보);
          if (parsedInfo?.객실상품 && parsedInfo.객실상품.length > 0) {
            priceInfo = parsedInfo.객실상품[0];
          }
        } catch (e) {
          priceInfo = rawData;
        }
      } else {
        priceInfo = rawData;
      }
      
      const priceOptionNo = priceInfo?.가격옵션번호 || 0;
      const totalAmount = priceInfo?.결제하실금액 || group.totalAmount || 0;
// 5. 예약 생성 (DBO.TS_T_예약하기_TRP용)
      const firstTraveler = group.travelers[0];
      
      const reservationData = {
        priceNo,
        priceOptionNo,
        startDate: formattedStartDate,
        ordererName: firstTraveler.name,
        ordererPhone: firstTraveler.phone,
        ordererEmail: firstTraveler.email || '',
        adultCount: travelerCount,
        childCount: 0,
        childAges: [],
        nights: 1,
        roomCount: 1,
        selectedOptions: [],
        totalAmount,
        companyNo,
        productTypeName: '투어',
      };
const reservationResult = await submitReservation(reservationData);
if (!reservationResult?.table || reservationResult.table.length === 0) {
        throw new Error('예약 생성에 실패했습니다.');
      }
      
      const firstRow = reservationResult.table[0] || {};
      const orderNo = firstRow.주문번호 ?? firstRow.orderNo ?? firstRow.OrderNo ?? null;
      let saleNo = firstRow.판매번호 ?? firstRow.주문판매번호 ?? firstRow.saleNo ?? null;
      if (typeof onProgress === 'function') {
        try { onProgress({ groupIndex: i, step: 'step1', status: 'success', orderNo }); } catch (_) {}
      }
      
      if (!orderNo) {
        throw new Error('주문번호를 받지 못했습니다.');
      }
// 판매번호 조회
      if (!saleNo) {
        try {
          const statusRow = await getOrderSaleStatus({ orderNo });
          saleNo = statusRow?.판매번호 ?? statusRow?.주문판매번호 ?? null;
} catch (e) {
          console.warn('판매번호 조회 실패:', e);
        }
      }
      if (typeof onProgress === 'function') {
        try { onProgress({ groupIndex: i, step: 'step1b', status: 'success', orderNo, saleNo }); } catch (_) {}
      }
      
      if (!saleNo) {
        throw new Error('판매번호를 찾을 수 없습니다.');
      }
      
      // 6. 여행객 정보 저장 (DBO.TRP_T_예약관리_판매상태및여행객수정_택스포함_14)
if (typeof onProgress === 'function') {
        try { onProgress({ groupIndex: i, step: 'step2', status: 'success', orderNo, saleNo }); } catch (_) {}
      }
      
      // 여행객 목록 조회하여 실제 여행객번호 확인
      let actualTravelerNumbers = [];
      try {
        const travelerList = await getReservationTravelerListBySaleNo({ saleNo });
        const sorted = (Array.isArray(travelerList) ? travelerList : []).sort((a, b) => {
          const aa = Number.isFinite(Number(a?.순번)) ? Number(a?.순번) : 999999;
          const bb = Number.isFinite(Number(b?.순번)) ? Number(b?.순번) : 999999;
          return aa - bb;
        });
        sorted.forEach((row) => {
          const no = Number(row?.여행객번호 || row?.여행객키);
          if (Number.isFinite(no) && no > 0) actualTravelerNumbers.push(no);
        });
} catch (e) {
        console.error('여행객 목록 조회 실패:', e);
        // 순차번호로 대체
        for (let j = 0; j < group.travelers.length; j++) {
          actualTravelerNumbers.push(j + 1);
        }
      }
      
      if (actualTravelerNumbers.length < group.travelers.length) {
        console.warn('여행객번호가 부족하여 순차번호로 보완');
        while (actualTravelerNumbers.length < group.travelers.length) {
          actualTravelerNumbers.push(actualTravelerNumbers.length + 1);
        }
      }
      
      // 여행객 정보 구성
      const travelerFieldRows = [];
      const travelerAmounts = [];
      const travelerOrders = [];
      
      group.travelers.forEach((traveler, index) => {
        const actualTravelerNo = actualTravelerNumbers[index];
        
        travelerFieldRows.push(
          { 여행객번호: actualTravelerNo, 필드번호: 8, 값: traveler.name ?? null },
          { 여행객번호: actualTravelerNo, 필드번호: -1, 값: null },
          { 여행객번호: actualTravelerNo, 필드번호: 109, 값: '남성' },
          { 여행객번호: actualTravelerNo, 필드번호: 9, 값: '' },
          { 여행객번호: actualTravelerNo, 필드번호: 10, 값: '' },
          { 여행객번호: actualTravelerNo, 필드번호: 11, 값: null },
          { 여행객번호: actualTravelerNo, 필드번호: 13, 값: null },
          { 여행객번호: actualTravelerNo, 필드번호: 253, 값: null },
          { 여행객번호: actualTravelerNo, 필드번호: 254, 값: null },
          { 여행객번호: actualTravelerNo, 필드번호: 314, 값: traveler.memo ?? null },
          { 여행객번호: actualTravelerNo, 필드번호: 12, 값: traveler.phone ?? null },
          { 여행객번호: actualTravelerNo, 필드번호: 256, 값: traveler.email ?? null },
          { 여행객번호: actualTravelerNo, 필드번호: 404, 값: null },
          { 여행객번호: actualTravelerNo, 필드번호: 255, 값: null }
        );
        
        travelerAmounts.push({
          여행객번호: actualTravelerNo,
          상품금액: Number(traveler.amount) || 0,
          공급단가: Number(traveler.amount) || 0,
          택스및유류: Number(traveler.discount) || 0
        });
        
        travelerOrders.push({
          여행객번호: actualTravelerNo,
          여행객종류: '여행객',
          선발권동의: false,
          명단확인: false,
          순번: index + 1
        });
      });
const travelerResult = await saveReservationTravelerChanges({
        saleNo,
        travelerFieldRows,
        travelerAmounts,
        travelerOrders
      });
if (typeof onProgress === 'function') {
        try { onProgress({ groupIndex: i, step: 'step3', status: 'success', orderNo, saleNo }); } catch (_) {}
      }
      
      results.push({
        success: true,
        orderNo,
        saleNo,
        productNo,
        productName,
        reservationDate: group.reservationDate,
        eventStartDate: formattedStartDate,
        travelerCount: group.travelers.length,
        totalAmount: group.totalAmount
      });
      
      if (typeof onProgress === 'function') {
        onProgress({ 
          groupIndex: i, 
          status: 'success', 
          orderNo, 
          saleNo,
          productName,
          message: `${productName} 예약 생성 완료` 
        });
      }
      
    } catch (error) {
      console.error(`Error processing group ${i + 1}:`, error);
      errors.push({
        groupIndex: i,
        reservationDate: group.reservationDate,
        productName: group.productName,
        error: error.message
      });
      if (typeof onProgress === 'function') {
        try { onProgress({ groupIndex: i, step: 'error', status: 'error', productName: group?.productName, message: error?.message }); } catch (_) {}
      }
      
      if (typeof onProgress === 'function') {
        onProgress({ 
          groupIndex: i, 
          status: 'error', 
          message: error.message 
        });
      }
    }
  }
  
  return {
    success: errors.length === 0,
    results,
    errors,
    summary: {
      total: reservationGroups.length,
      succeeded: results.length,
      failed: errors.length
    }
  };
};

// 판매상태 코드 목록 조회 (예약관리 - 일괄 상태 변경용)
// SP: DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C
// payload Table1: @종류='판매상태2', @공급업체번호
// Returns: [{ 코드, 코드명 }]
export const getSaleStatusCodes = async () => {
  try {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@종류', 값: '판매상태2', 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%88%98%EB%8F%99%EC%BD%94%EB%93%9C`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const list = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    return list;
  } catch (error) {
    return handleApiError(error);
  }
};

// 예약 일괄 수정 (판매상태 일괄 변경)
// SP: DBO.TRP_T_%EC%98%88%EC%95%BD%EC%9D%BC%EA%B4%84%EC%88%98%EC%A0%95
// payload Table1: @예약리스트 (JSON array), @업체번호, @종류='판매상태', @값 (상태코드), @처리자
export const updateReservationBatch = async ({ saleNoList, statusCode }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    // 예약리스트는 [{"판매번호": xxx}, ...] 형태의 JSON 문자열
    const reservationList = saleNoList.map(saleNo => ({ 판매번호: Number(saleNo) }));
    const payload = {
      Table1: [
        { 컬럼명: '@예약리스트', 값: JSON.stringify(reservationList) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@종류', 값: '판매상태' },
        { 컬럼명: '@값', 값: String(statusCode) },
        { 컬럼명: '@처리자', 값: String(userNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%98%88%EC%95%BD%EC%9D%BC%EA%B4%84%EC%88%98%EC%A0%95`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Update consult product number (견적-상품번호 매칭)
// This updates the 팀컬러 field in the consult info to link it with a product
// SP: DBO.TRP_T_%EC%83%81%EB%8B%B4%EC%A0%95%EB%B3%B4_01
export const updateConsultProductNo = async ({ consultNo, productNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const pn = productNo != null ? String(productNo) : '';
    const cn = consultNo != null ? Number(consultNo) : 0;

    // First, get the current consult info to preserve existing data
    const currentInfo = await getConsultInfo({ consultNo: cn });
    if (!currentInfo) {
      throw new Error('상담정보를 찾을 수 없습니다.');
    }

    // Build minimal 상담정보 JSON with updated 팀컬러
    const info = {
      견적경로: currentInfo.견적경로 || '421001',
      상담번호: cn,
      상담구분: currentInfo.상담구분코드 || currentInfo.상담구분 || '419001',
      상담상태: currentInfo.상담상태코드 || currentInfo.상담상태 || '420001',
      조인업체명: currentInfo.조인업체명 || '',
      담당자: currentInfo.담당자 || '',
      담당자번호: currentInfo.담당자번호 || Number(userNo),
      판매업체: currentInfo.판매업체 || null,
      판매업체번호: currentInfo.판매업체번호 || null,
      수배처: currentInfo.수배처 || null,
      수배처번호: currentInfo.수배처번호 || null,
      팀컬러: pn ? [pn] : [], // Updated product number (allow unlink)
      요청자명: currentInfo.요청자명 || '',
      요청자휴대폰: currentInfo.요청자휴대폰 || '',
      요청자이메일: currentInfo.요청자이메일 || '',
      시작일: currentInfo.시작일 || '',
      종료일: currentInfo.종료일 || '',
      견적명: currentInfo.견적명 || '',
      견적내용: currentInfo.견적내용 || '',
      성인수: Number(currentInfo.성인수 || 0),
      소아수: Number(currentInfo.소아수 || 0),
      유아수: Number(currentInfo.유아수 || 0),
      인솔자수: Number(currentInfo.인솔자수 || 0),
      성인판매가: Number(currentInfo.성인판매가 || 0),
      소아판매가: Number(currentInfo.소아판매가 || 0),
      유아판매가: Number(currentInfo.유아판매가 || 0),
      요청일정표: currentInfo.요청일정표 || '407002',
      첨부파일리스트: Array.isArray(currentInfo.첨부파일리스트) ? currentInfo.첨부파일리스트 : [],
      견적첨부파일리스트: Array.isArray(currentInfo.견적첨부파일리스트) ? currentInfo.견적첨부파일리스트 : [],
      일정표주소: currentInfo.일정표주소 || '',
      적용환율: currentInfo.적용환율 || [{ 통화코드: '058001', 통화기호: '￦', 적용환율: 1, 실시간환율: 1, 평균환율: 1 }],
      통화코드: currentInfo.통화코드 || '058001',
      통화기호: currentInfo.통화기호 || '￦',
      원가구분: currentInfo.원가구분 || '2',
      총원가: currentInfo.총원가 || [{ 구분: '총원가', 통화코드: '058001', 통화기호: 'KRW', 적용환율: 1, 성인: 0, 어린이: 0, 영유아: 0 }],
      세부원가: currentInfo.세부원가 || [],
      공용원가: currentInfo.공용원가 || [],
      수익계산: currentInfo.수익계산 || [],
    };

    const payload = {
      Table1: [
        { 컬럼명: '@상담정보', 값: JSON.stringify(info) },
        { 컬럼명: '@처리자', 값: String(userNo) },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@팀컬러', 값: pn },
        { 컬럼명: '@예상수익', 값: 0 },
        { 컬럼명: '@경로', 값: '' },
        { 컬럼명: '@성인예상수익', 값: 0 },
        { 컬럼명: '@소아예상수익', 값: 0 },
        { 컬럼명: '@유아예상수익', 값: 0 },
        { 컬럼명: '@성인원가', 값: 0 },
        { 컬럼명: '@소아원가', 값: 0 },
        { 컬럼명: '@유아원가', 값: 0 },
      ],
    };

    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%81%EB%8B%B4%EC%A0%95%EB%B3%B4_01`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get agency list (대리점 리스트 조회)
// SP: DBO.CW_S_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98 (DBO.CW_S_판매거래처)
// payload Table1: @업체번호, @사용여부1, @사용여부2
//   {
//     Table1: [
//       { 컬럼명: '@업체번호', 값: '1' },
//       { 컬럼명: '@사용여부1', 값: true },
//       { 컬럼명: '@사용여부2', 값: true },
//     ]
//   }
// 응답 예시: { table: [{ 거래처명, 업체번호2, ... }] }
//   - 거래처명: 대리점 명
//   - 업체번호2: 예약 시 사용할 대리점 코드
// 이 함수는 UI에서 기존에 사용하던 형태와의 호환을 위해
//   { 코드: 업체번호2, 코드명: 거래처명, ...원본필드 }
// 구조로 매핑하여 반환한다.
export const getAgencyList = async ({ companyNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@사용여부1', 값: true },
        { 컬럼명: '@사용여부2', 값: true },
      ],
    };

    const procedureName = encodeURIComponent('DBO.CW_S_판매거래처');
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${procedureName}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();

    const rawList = Array.isArray(data?.table) ? data.table : [];
    // 기존 대리점 드롭다운 코드는 agency.코드 / agency.코드명을 사용하므로
    // 새 프로시저 응답을 이에 맞게 변환한다.
    return rawList
      .map((row) => {
        const code = row?.업체번호2 ?? row?.거래처번호 ?? null;
        const name = row?.거래처명 ?? '';
        if (code === null || code === undefined || String(code).trim() === '') return null;
        return {
          코드: String(code),
          코드명: String(name),
          ...row,
        };
      })
      .filter((row) => row !== null);
  } catch (error) {
    console.error('getAgencyList error:', error);
    return [];
  }
};

// Upload content file to S3 and register it
// Step 1: Upload file to S3 via /Upload/uploadS3
// Step 2: Register file via DBO.TRP_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%ED%8C%8C%EC%9D%BC%EC%B6%94%EA%B0%80
// Returns: { 파일번호, 파일명, 파일경로 }
export const uploadContentFile = async ({ file, productNo, contentNo }) => {
  try {
    if (!file) throw new Error('업로드할 파일이 없습니다.');
    if (!contentNo) throw new Error('컨텐츠번호가 없습니다.');
    if (!productNo) throw new Error('상품번호가 없습니다.');

    const { userNo } = getUserInfo() || {};

    // Generate unique filename with timestamp
    const now = new Date();
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    const originalName = file.name || 'file';
    const dotIndex = originalName.lastIndexOf('.');
    const baseName = dotIndex > 0 ? originalName.slice(0, dotIndex) : originalName;
    const ext = dotIndex > 0 ? originalName.slice(dotIndex) : '';
    const uniqueFileName = `${baseName}_${timestamp}${ext}`;

    // FilePath format: product/{productNo}/content/file/{contentNo}/
    const filePath = `product/${productNo}/content/file/${contentNo}/`;

    // Convert file to base64
    const toBase64 = (blob) => new Promise((resolve, reject) => {
      try {
        if (typeof blob === 'string') { resolve(blob); return; }
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const idx = result.indexOf(',');
          resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      } catch (e) { reject(e); }
    });

    const base64 = await toBase64(file);

    // Step 1: Upload to S3
    const uploadUrl = `${API_BASE_URL}/Upload/uploadS3`;
    const uploadPayload = {
      File: base64,
      FileName: uniqueFileName,
      FilePath: filePath,
    };
    const uploadResponse = await fetch(uploadUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(uploadPayload),
    });
    if (!uploadResponse.ok) throw new Error(`파일 업로드 실패: ${uploadResponse.status}`);
    const uploadData = await uploadResponse.json();
    // uploadData: { table: [{ 파일번호, 파일명, 파일경로 }] }

    // Step 2: Register file in DB
    const registerPayload = {
      Table1: [
        { 컬럼명: '@컨텐츠번호', 값: Number(contentNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@파일명', 값: uniqueFileName, 리스트값: null },
        { 컬럼명: '@파일경로', 값: `${filePath}${uniqueFileName}`, 리스트값: null },
        { 컬럼명: '@처리자', 값: String(userNo), 리스트값: null },
      ],
    };
    const registerUrl = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_T_%EC%83%81%ED%92%88%EC%BD%98%ED%85%90%EC%B8%A0%ED%8C%8C%EC%9D%BC%EC%B6%94%EA%B0%80`;
    const registerResponse = await fetch(registerUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(registerPayload),
    });
    if (!registerResponse.ok) throw new Error(`파일 등록 실패: ${registerResponse.status}`);
    const registerData = await registerResponse.json();

    // Return upload result with file info
    return {
      uploadResult: uploadData,
      registerResult: registerData,
      fileName: uniqueFileName,
      filePath: `${filePath}${uniqueFileName}`,
    };
  } catch (error) {
    console.error('uploadContentFile error:', error);
    return handleApiError(error);
  }
};

// Get content info for editing
// SP: DBO.RCT_S_%EC%BD%98%ED%85%90%EC%B8%A0%EC%A0%95%EB%B3%B4
// Params: { contentNo, productNo, companyNo, userNo }
export const getContentInfo = async ({ contentNo, productNo, companyNo, userNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@컨텐츠번호', 값: Number(contentNo), 리스트값: null },
        { 컬럼명: '@상품번호', 값: String(productNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@유저번호', 값: String(userNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%BD%98%ED%85%90%EC%B8%A0%EC%A0%95%EB%B3%B4`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get content attached files list
// SP: DBO.TRP_S_%EC%BB%A8%ED%85%90%EC%B8%A0%EC%B2%A8%EB%B6%80%ED%8C%8C%EC%9D%BC%EB%A6%AC%EC%8A%A4%ED%8A%B8
// Params: { contentNo }
export const getContentAttachedFiles = async ({ contentNo }) => {
  try {
    const payload = {
      Table1: [
        { 컬럼명: '@컨텐츠번호', 값: Number(contentNo) },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRP_S_%EC%BB%A8%ED%85%90%EC%B8%A0%EC%B2%A8%EB%B6%80%ED%8C%8C%EC%9D%BC%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Get Group Settlement Statistics (단체정산 통계)
// SP: DBO.WSP_S_행사단체통계_02
// payload Table1: @공급업체번호, @시작일(YYYY-MM-DD), @종료일(YYYY-MM-DD)
// Returns: two result sets - table (group settlement rows), table1 (individual sales without group)
export const getGroupSettlementStats = async ({ startDate, endDate } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const start = String(startDate || toYmd(today));
    const oneMonthLater = new Date(today);
    oneMonthLater.setMonth(today.getMonth() + 1);
    const end = String(endDate || toYmd(oneMonthLater));

    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
        { 컬럼명: '@시작일', 값: start, 리스트값: null },
        { 컬럼명: '@종료일', 값: end, 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.WSP_S_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%ED%86%B5%EA%B3%84_02`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    const groupRows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const individualRows = Array.isArray(data?.table1) ? data.table1 : Array.isArray(data?.Table1) ? data.Table1 : [];
    return { groupRows, individualRows };
  } catch (error) {
    return handleApiError(error);
  }
};

// 행사단체 통장입출금 리스트 (단체정산 입금액/전도금 매핑)
// SP: DBO.TRPLITE_S_행사단체입출금리스트
// payload Table1: @시작일, @종료일, @공급업체번호
// 시작일 = 조회일(종료일) 기준 1년 전, 종료일 = 조회일, 공급업체번호 = 토큰 companyNo
// Returns: array in data.table
export const getGroupInOutCashList = async ({ queryDate } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const end = (() => {
      if (queryDate) {
        const d = new Date(queryDate);
        return Number.isNaN(d.getTime()) ? new Date() : d;
      }
      return new Date();
    })();
    const start = new Date(end.getTime());
    start.setFullYear(start.getFullYear() - 1);

    const payload = {
      Table1: [
        { 컬럼명: '@시작일', 값: toYmd(start), 리스트값: null },
        { 컬럼명: '@종료일', 값: toYmd(end), 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: Number(companyNo), 리스트값: null },
      ],
    };
    const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.TRPLITE_S_%ED%96%89%EC%82%AC%EB%8B%A8%EC%B2%B4%EC%9E%85%EC%B6%9C%EA%B8%88%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
  } catch (error) {
    return handleApiError(error);
  }
};

/* ==========================================================================
 * 기획전 (Promotion)
 * --------------------------------------------------------------------------
 * 기획전은 여러 공급업체의 개별 "상품가격(가격번호)"을 하나로 묶어 특가로 파는
 * 기능이다. 매핑 테이블은 상품가격_기획전(가격번호, 기획전번호)이고 할인율/특가
 * 컬럼이 없다 — 기획전은 순수 태그이며, 기획전별로 달라지는 것은
 * 기획전_수수료규정의 대리점/플랫폼 수수료율뿐이다.
 * ========================================================================== */

// SP 응답의 BIT 컬럼은 "true"/"false" 문자열로 돌아온다.
const promoToBool = (v) => {
  if (typeof v === 'boolean') return v;
  const s = String(v ?? '').trim().toLowerCase();
  return s === 'true' || s === '1';
};

// 기획전 SP는 권한 위반 시 RAISERROR로 거부한다 (예: '타 업체 기획전 정보입니다 (편집불가)').
// 그 문구를 사용자에게 그대로 보여주기 위해 상태코드와 무관하게 본문을 파싱한다.
const promoFetch = async (procName, columns) => {
  const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${encodeURIComponent(procName)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ Table1: columns }),
  });
  let data = null;
  try {
    data = await res.clone().json();
  } catch (_) {
    data = null;
  }
  const spMessage = (
    data?.table1?.[0]?.message
    || data?.Table1?.[0]?.message
    || data?.table?.[0]?.message
    || data?.message
    || null
  );
  if (!res.ok) {
    const err = new Error(String(spMessage || `API error: ${res.status}`));
    err.status = res.status;
    err.apiMessage = spMessage || null;
    throw err;
  }
  // 200이어도 업무 오류 메시지가 실려 오는 경우가 있다.
  if (spMessage) throw new Error(String(spMessage));
  if (data == null) data = await res.json();
  return data;
};

const promoRows = (data) => (
  Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : []
);

// 기획전 목록 조회
// SP: DBO.RCT_S_기획전
// 주의: SP가 판매수량/판매액을 하드코딩 0으로 반환한다. 실적으로 표시하면 안 된다.
export const getPromotions = async ({ startDate, endDate, includeClosed = true } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const data = await promoFetch('DBO.RCT_S_기획전', [
      { 컬럼명: '@시작일', 값: String(startDate ?? ''), 리스트값: null },
      { 컬럼명: '@종료일', 값: String(endDate ?? ''), 리스트값: null },
      { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
      { 컬럼명: '@마감상태1', 값: false, 리스트값: null },
      // 마감상태 IN (@마감상태1, @마감상태2) — 두 값을 같게 주면 진행중만 조회된다.
      { 컬럼명: '@마감상태2', 값: !!includeClosed, 리스트값: null },
    ]);
    return promoRows(data).map((r) => ({ ...r, 마감상태: promoToBool(r?.마감상태) }));
  } catch (error) {
    return handleApiError(error);
  }
};

// 기획전 상세 조회 (신규는 기획전번호 0 → SP가 빈 껍데기 + 수수료 0을 반환)
// SP: DBO.RCT_S_기획전정보  →  기획전정보 컬럼에 JSON 문자열 1건
export const getPromotionDetail = async ({ promotionNo = 0 } = {}) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const data = await promoFetch('DBO.RCT_S_기획전정보', [
      { 컬럼명: '@기획전번호', 값: Number(promotionNo) || 0, 리스트값: null },
      { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
    ]);
    const raw = promoRows(data)?.[0]?.기획전정보;
    let info = null;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        info = Array.isArray(parsed) ? (parsed[0] || null) : (parsed || null);
      } catch (e) {
        console.warn('Failed to parse 기획전정보 JSON string:', e);
      }
    } else if (Array.isArray(raw)) {
      info = raw[0] || null;
    } else if (raw && typeof raw === 'object') {
      info = raw;
    }
    if (!info) return null;
    return {
      ...info,
      마감상태: promoToBool(info.마감상태),
      상품종류수수료규정: (Array.isArray(info.상품종류수수료규정) ? info.상품종류수수료규정 : []).map((x) => ({
        ...x,
        적용여부: promoToBool(x?.적용여부),
      })),
    };
  } catch (error) {
    return handleApiError(error);
  }
};

// 기획전 등록/수정 (기획전번호 0 → INSERT)
// SP: DBO.RCT_T_기획전
// 주의 1) @상품종류수수료규정은 OPENJSON WITH()로 파싱되어 아래 4개 키만 살아남는다.
//         상품종류(이름) 같은 표시용 필드를 보내도 조용히 버려진다.
// 주의 2) SP가 `CASE WHEN 수수료 < 100 THEN 수수료/100 ELSE 수수료 END` 로 저장한다.
//         100 이상은 나누기가 걸리지 않으므로 UI에서 0~99.99로 제한해야 한다.
// 주의 3) 타 업체 기획전을 수정하면 SP가 RAISERROR로 거부한다.
export const savePromotion = async ({
  promotionNo = 0,
  managerNo,
  closed = false,
  name,
  startDate,
  endDate,
  description = '',
  url = '',
  terms = '',
  commissionRules = [],
} = {}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const rules = (Array.isArray(commissionRules) ? commissionRules : []).map((r) => ({
      적용여부: !!r?.적용여부,
      상품종류코드: String(r?.상품종류코드 ?? ''),
      대리점수수료: Number(r?.대리점수수료) || 0,
      플랫폼수수료: Number(r?.플랫폼수수료) || 0,
    }));
    const data = await promoFetch('DBO.RCT_T_기획전', [
      { 컬럼명: '@기획전번호', 값: Number(promotionNo) || 0, 리스트값: null },
      { 컬럼명: '@담당자번호', 값: String(managerNo ?? userNo ?? ''), 리스트값: null },
      { 컬럼명: '@마감상태', 값: !!closed, 리스트값: null },
      { 컬럼명: '@기획전명', 값: String(name ?? ''), 리스트값: null },
      { 컬럼명: '@시작일', 값: String(startDate ?? ''), 리스트값: null },
      { 컬럼명: '@종료일', 값: String(endDate ?? ''), 리스트값: null },
      { 컬럼명: '@기획전설명', 값: String(description ?? ''), 리스트값: null },
      { 컬럼명: '@기획전URL', 값: String(url ?? ''), 리스트값: null },
      { 컬럼명: '@이용약관', 값: String(terms ?? ''), 리스트값: null },
      { 컬럼명: '@처리자', 값: String(userNo ?? ''), 리스트값: null },
      { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
      { 컬럼명: '@상품종류수수료규정', 값: JSON.stringify(rules), 리스트값: null },
    ]);
    // SP 말미에 RCT_S_기획전번호_업데이트리스트를 EXEC하므로 갱신된 행이 함께 돌아온다.
    const row = promoRows(data)?.[0] || null;
    return row ? { ...row, 마감상태: promoToBool(row?.마감상태) } : null;
  } catch (error) {
    return handleApiError(error);
  }
};

// 기획전 삭제 (타 업체 기획전이면 SP가 RAISERROR로 거부)
// SP: DBO.RCT_T_기획전삭제
export const deletePromotion = async ({ promotionNo }) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    await promoFetch('DBO.RCT_T_기획전삭제', [
      { 컬럼명: '@기획전번호', 값: Number(promotionNo), 리스트값: null },
      { 컬럼명: '@처리자', 값: String(userNo ?? ''), 리스트값: null },
      { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
    ]);
    return true;
  } catch (error) {
    return handleApiError(error);
  }
};

// 기획전에 담긴 상품 목록 (상품/자원/가격옵션 단위까지만 — 개별 날짜는 나오지 않는다)
// SP: DBO.RCT_S_기획전_상품리스트
export const getPromotionProducts = async ({ promotionNo }) => {
  try {
    const { companyNo } = getUserInfo() || {};
    const data = await promoFetch('DBO.RCT_S_기획전_상품리스트', [
      { 컬럼명: '@기획전번호', 값: Number(promotionNo), 리스트값: null },
      { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
    ]);
    return promoRows(data);
  } catch (error) {
    return handleApiError(error);
  }
};

// 매핑 UI의 체크박스 목록용 — 진행중(마감상태=0) + 로그인 업체가 등록한 기획전만.
// RCT_S_공용기획전 / RCT_S_기획전 은 공급업체번호 IN (1,내업체) 로 본사 공용을 섞어
// 내리고 SELECT 에 공급업체번호가 없다. 소유 판별은 기획전 관리와 같이
// CW_S_유저정보로 확정한 업체명 ↔ 담당업체 대조로 하며, 확정 실패 시 빈 목록(fail-closed).
export const getPromotionOptions = async () => {
  try {
    const { companyNo } = getUserInfo() || {};
    const [data, companyInfo] = await Promise.all([
      promoFetch('DBO.RCT_S_기획전', [
        { 컬럼명: '@시작일', 값: '1900-01-01', 리스트값: null },
        { 컬럼명: '@종료일', 값: '2999-12-31', 리스트값: null },
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
        { 컬럼명: '@마감상태1', 값: false, 리스트값: null },
        // 두 값을 같게(false) 주면 진행중만 조회된다.
        { 컬럼명: '@마감상태2', 값: false, 리스트값: null },
      ]),
      getUserCompanyInfo().catch(() => null),
    ]);
    const myName = String(companyInfo?.업체명 ?? '').trim();
    if (!myName) return [];
    return promoRows(data)
      .filter((r) => String(r?.담당업체 ?? '').trim() === myName)
      .map((r) => ({ ...r, 마감상태: promoToBool(r?.마감상태) }));
  } catch (error) {
    return handleApiError(error);
  }
};

// 기획전명 → 기획전번호 역매핑용 사전.
// RCT_S_가격예약_02가 날짜별로 내려주는 `기획전` 컬럼은 STRING_AGG(기획전명,',')뿐이라
// 번호를 알 수 없다. 전 기간 + 마감 0/1로 조회하면 매핑에 등장할 수 있는 기획전
// 전집합(공급업체 IN (자기업체, 본사 1))이 나오므로, 마감된 기획전도 사전에 포함된다.
// 이름이 중복되면 번호를 확정할 수 없으므로 호출부에서 fail-closed 처리해야 한다.
export const getPromotionDictionary = async () => {
  try {
    const { companyNo } = getUserInfo() || {};
    const data = await promoFetch('DBO.RCT_S_기획전', [
      { 컬럼명: '@시작일', 값: '1900-01-01', 리스트값: null },
      { 컬럼명: '@종료일', 값: '2999-12-31', 리스트값: null },
      { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
      { 컬럼명: '@마감상태1', 값: false, 리스트값: null },
      { 컬럼명: '@마감상태2', 값: true, 리스트값: null },
    ]);
    return promoRows(data).map((r) => ({
      기획전번호: Number(r?.기획전번호),
      기획전명: String(r?.기획전명 ?? '').trim(),
      마감상태: promoToBool(r?.마감상태),
    })).filter((r) => Number.isFinite(r.기획전번호) && r.기획전명 !== '');
  } catch (error) {
    return handleApiError(error);
  }
};

// 선택한 가격번호(날짜)들을 선택한 기획전들에 매핑.
// SP: DBO.RCT_T_상품가격_기획전연동
// ⚠️ 이 SP는 "추가"가 아니라 "교체"다. 선택 가격번호의 기존 매핑을 기획전 조건 없이
//    전부 DELETE한 뒤 (선택 가격번호 × 선택 기획전) CROSS JOIN으로 INSERT한다.
//    따라서 호출부는 "이 날짜들이 최종적으로 속할 기획전 전체 집합"을 넘겨야 한다.
// ⚠️ promotionNos가 비면 DELETE만 수행되어 전체 해제가 된다. 단 SP 기본값이
//    `JSON = ''` 인데 OPENJSON('')은 파싱 에러이므로 반드시 '[]'를 보낸다.
export const mapPricesToPromotions = async ({ productNo, priceNos = [], promotionNos = [] } = {}) => {
  try {
    const { companyNo, userNo } = getUserInfo() || {};
    const priceList = (Array.isArray(priceNos) ? priceNos : [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0)
      .map((n) => ({ 가격번호: n }));
    if (priceList.length === 0) throw new Error('매핑할 가격번호가 없습니다.');
    const promoList = (Array.isArray(promotionNos) ? promotionNos : [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0)
      .map((n) => ({ 기획전번호: n }));
    await promoFetch('DBO.RCT_T_상품가격_기획전연동', [
      { 컬럼명: '@선택가격번호리스트', 값: JSON.stringify(priceList), 리스트값: null },
      // 빈 배열도 '[]' 문자열로 — OPENJSON('')은 터진다.
      { 컬럼명: '@기획전리스트', 값: JSON.stringify(promoList), 리스트값: null },
      { 컬럼명: '@처리자', 값: String(userNo ?? ''), 리스트값: null },
      { 컬럼명: '@상품번호', 값: productNo == null ? null : Number(productNo), 리스트값: null },
      { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
    ]);
    return true;
  } catch (error) {
    return handleApiError(error);
  }
};

// 모든 API 함수를 내보내는 기본 export
const apiService = {
  getPromotions,
  getPromotionDictionary,
  mapPricesToPromotions,
  getPromotionDetail,
  savePromotion,
  deletePromotion,
  getPromotionProducts,
  getPromotionOptions,
  deleteConsultNote,
  deleteTourSelectedEvents,
  getConsultNotes,
  saveConsultNote,
  getQuotePrices,
  saveConsultInfo,
  getConsultList,
  getConsultInfo,
  createUnregisteredTourReservation,
  createProductSelectOption,
  deleteProductSelectOption,
  updateProductSelectOption,
  getProductSelectOptions,
  getReservationSaleInfo,
  getTourMeetingSetting,
  getSupplyContractList,
  createSupplyContract,
  updateSupplyContract,
  deleteSupplyContract,
  getGroupSettlementMain,
  getTrpLiteOrderListByOrderDate,
  getTrpLiteOrderListByDepartureDate,
  getOutstandingReceivables,
  getSupplyContractPricingTableNo,
  getSupplyContractPricingInfo,
  saveHotelPricingTable,
  getUserCompanyInfo,
  getCompanyPrimaryDomain,
  getSaleCustomerRequestItems,
  getDisbursementHistory,
  getReceiptHistory,
  createMaritReservationBatch,
  createMaritReservationForRegisteredProducts,
  getSaleStatusCodes,
  updateReservationBatch,
  updateConsultProductNo,
  getAgencyList,
  uploadContentFile,
  getContentInfo,
  getContentAttachedFiles,
  getGroupSettlementStats,
  getItineraryTypeList,
  saveItineraryTypeInfo,
  deleteItineraryType,
  cloneItineraryType,
  getItineraryTypeSimpleList
};

export default apiService;
